import { pricingSignatureOf, pricingSignatureOfSavedLines } from "@/lib/pricingSignature";
import { resolveDiscount } from "@/services/discountResolver";
import type { DiscountResolution } from "@/services/discountResolver";
import { quantityForQuoteLine } from "@/services/poLineMatch";
import type { PoLineMatch } from "@/services/poLineMatch";
import {
  provenanceFromResolution, rulePairingLabel,
} from "@/services/discountProvenance";
import { mockQuotes } from "@/mocks/quotes";
import { mockMaterials } from "@/mocks/materials";
// NOTE: no mocks/products and no mocks/inventory import.
//
// Slice 1c cut /catalog to the real master, retiring api.getProducts() -- the
// last reader of the mock catalog in this file. The /inventory cutover then
// retired api.getInventory(), the last reader of the generated stock ledger,
// and deleted mocks/inventory.ts with it: that screen now reads
// /anixify/api/products joined to /anixify/api/stock, keyed on product id.
//
// mocks/products.ts survives only because nothing has been asked to remove it
// yet. Nothing in this file may reach for either of them again -- a
// mock-returning read beside a real one is an invitation to wire a screen back
// to invented parts with invented quantities.
import { mockBrandLockRules } from "@/mocks/brandLockRules";
import { defaultPricingContext } from "@/mocks/panel";
import { defaultRiskBufferSettings, mockBufferChangeHistory } from "@/mocks/riskBuffers";
import { mockCompanySettings } from "@/mocks/company";
import { API_BASE, AUTH_ENDPOINTS, ENDPOINTS } from "@/config";
import { notifySessionExpired } from "@/lib/sessionExpiry";
import { setMoneyFormat } from "@/lib/format";
import { setProductName } from "@/lib/brand";
// ⚠️ THE WARNING DECISIONS LIVE IN ONE PURE MODULE, not in this layer and
// not in the component. Only the shipment shape is borrowed here.
import type { PoShipment } from "@/services/poWarnings";
import type {
  Quote, Material, MaterialCategory, PricingContext, Product, ProductCategory,
  ProductBrand, CatalogProduct,
  BrandLockRule, DiscountRule, OemDiscountRule, FreightRate, DutyRate,
  FreightType, FreightMode, FreightBasis, FxRateSource, LockCategory,
  FxRate, RiskBufferSettings, BufferChangeEntry, CompanySettings,
  CustomerPartner, QuoteCustomer, QuoteShipment, AllocationBasis,
  CustomerSnapshot, CustomerSnapshotBlock, QuoteApproval, QuoteOutcome, QuoteRef, QuoteRevision,
  QuoteTax, QuoteTaxLine, OemQuoteLink, OemQuoteWarning,
} from "@/types";

// Single API surface. All data reads go through this file.
//
// Almost everything here is frontend-only and returns local mocks. The one
// exception is priceLinesFromBackend() at the bottom, which is a REAL
// network call to the Odoo backend's validated pricing engine. It runs
// alongside the placeholder math in services/pricing.ts — it does not
// replace it. See "Real backend pricing" below.

// PROTOTYPE IN-MEMORY STATE ONLY. Quotes created via createQuote() live in
// this module-level array for the life of the browser tab and do NOT
// survive a refresh — there is no persistence layer. This will be replaced
// by real Odoo-backed reads/writes later. Do not add a database. Do not add
// localStorage.
// Copies, not the mock objects themselves. saveQuoteToBackend writes
// odooQuoteId/odooQuoteName onto the stored record, and with shared
// references that mutated the imported mock -- which no reset could then undo,
// because the seed itself had changed.
let quotesStore: Quote[] = mockQuotes.map((q) => ({ ...q }));

// The 25 shipped example quote references, frozen at module load.
//
// ⚠️ FROM THE IMPORTED ARRAY, NOT FROM quotesStore ABOVE. The store is seeded
// from the same array but GROWS -- createQuote pushes real quotes into it --
// so a set derived from the store would begin matching quotes a user actually
// created and refuse to save them. See api.isExampleQuoteNo.
const SHIPPED_EXAMPLE_QUOTE_NOS: ReadonlySet<string> = new Set(
  mockQuotes.map((q) => q.quoteNo),
);

/** TEST-ONLY. Restore the in-memory stores to their seeded state.
 *
 * quotesStore and materialsByQuote are module-level, so Jest's module
 * registry keeps them alive for every test in a file: a suite that saves a
 * quote leaves odooQuoteId/odooQuoteName set on a shared record, and the
 * next test silently sends an update where it meant to send a create. Call
 * this in beforeEach.
 *
 * Not exported for production use -- nothing in the app should ever want to
 * discard the user's working state. */
export function __resetStoresForTests(): void {
  quotesStore = mockQuotes.map((q) => ({ ...q }));
  for (const key of Object.keys(materialsByQuote)) delete materialsByQuote[key];
  for (const key of Object.keys(reopenedEndClient)) delete reopenedEndClient[key];
}

// New quotes start with an empty material list. Keyed by quoteNo so
// getMaterials can tell a freshly-created quote apart from the 25 original
// mock quotes, which all still share the one mockMaterials list (unchanged,
// pre-existing behavior). addMaterialsToQuote() below "forks" a legacy
// quote's list into its own entry here the first time materials are added
// to it, leaving mockMaterials and every other legacy quote untouched.
const materialsByQuote: Record<string, Material[]> = {};

// ⚠️ THE STORE KEY FOR A QUOTE THAT CAME OUT OF THE DATABASE.
//
// A quote created in this tab is keyed by its quoteNo, which createQuote mints
// and which is also the /quotes/:id route parameter. A REOPENED quote cannot
// use that: quote NAMES ARE NOT UNIQUE -- live holds Q-2026-0001 seven times
// and the 25 mock quotes reuse the same names -- so keying on `detail.name`
// merges two different quotes into one bucket, and lands a reopened quote on
// whichever mock quote happens to share its name.
//
// `saved:` is what makes the two namespaces disjoint by construction rather
// than by luck: NO QUOTENO CONTAINS A COLON, so no saved key can ever equal
// one.
//
// ⚠️ THE COLON IS THE WHOLE ARGUMENT, and this comment used to overstate it.
// It said "every quoteNo matches /^Q-\d{4}-\d+$/", which stopped being true
// when the working reference gained a session tag (see nextQuoteNo below):
// a freshly created quote is now Q-2026-0026-KXQP. The disjointness never
// depended on that shape -- it depended on the colon, and it still holds.
//
// ⚠️ KEYED ON THE BACKEND'S id, NOT ON THE ROUTE PARAM STRING. /saved-quotes/007
// and /saved-quotes/7 are the same record; keying on the raw param would give
// them two buckets and two different line-number sequences.
//
// ⚠️ AND NOT ON `odooQuoteId` WHEREVER IT HAPPENS TO BE SET. saveQuoteToBackend
// writes that field onto a stored MOCK quote once it has been saved, so a rule
// of "use odooQuoteId when present" would silently re-key a live quote
// mid-session, orphan its materialsByQuote entry and drop it back to
// mockMaterials. The key is decided by which ROUTE is open, never by which
// fields are populated.
export function savedQuoteStoreKey(quoteId: number): string {
  return `saved:${quoteId}`;
}

// The endClient for a quote that is NOT in quotesStore.
//
// addMaterialsToQuote resolves the client to match discount rules against, and
// it used to do that with `quotesStore.find(q => q.quoteNo === quoteId)` alone.
// A reopened quote is never in quotesStore (see setQuotePricingMode's comment),
// so under a `saved:<id>` key that find misses, endClient falls to "", and
// findDiscountPct returns 0 for every line -- a negotiated discount silently
// gone, wearing the same look as a genuine 0%. seedReopenedQuote records the
// client here for exactly that lookup.
const reopenedEndClient: Record<string, string> = {};

export interface NewQuoteInput {
  // Derived from the picked entity's name, so the rule-matching key and the
  // structured customer cannot disagree about who this quote is for.
  endClient: string;
  customer?: QuoteCustomer;
  projectId: string;
  projectName: string;
  warehouse: string;
  // No validityDays (remove-new-quote-validity-picker, 2026-09-23): validity
  // comes only from Settings > Company, stored on the quote at finalise.
}

// ⚠️ THE WORKING REFERENCE CARRIES A PER-QUOTE TAG. Q-2026-0026-KXQP.
//
// THE BUG IT CLOSES, measured rather than theorised. `quotesStore` is
// module-level and reseeds from mocks/quotes.ts on every page load, so the
// counter below always restarts at the same floor: the FIRST new quote in
// EVERY browser session was named Q-2026-0026. Drafts keep the caller's name
// (anixify.quote.create does `if vals.get("is_draft"): continue` before the
// sequence draw, and saveDraft sends `name: quote.quoteNo`), and
// findDraftFor matches drafts BY NAME. So: create a quote, abandon it,
// reload, create another -- and the second one restored the FIRST one's
// lines. Server-side draft scoping (user_id = acting.id) blocked the
// cross-user case; the cross-session one was open.
//
// THE TAG IS FOUR LETTERS, AND THE LETTERS ARE THE POINT.
//
// ⚠️ It cannot collide with an ir.sequence name BY CONSTRUCTION, not by
// probability. data/anixify_sequence.xml sets prefix="Q-%(year)s-" with
// padding=4, so the sequence emits `Q-<year>-` followed by DIGITS ONLY
// (0001..9999, then 10000+). A trailing "-" plus four letters is unreachable
// by that generator. A base32 tag including digits would have made this a
// probability argument instead, which is strictly worse for no gain.
//
// ⚠️ It cannot equal a shipped example quote name either. The 25 names in
// mocks/quotes.ts are plain Q-YYYY-NNNN, so api.isExampleQuoteNo now has a
// structural guarantee where it previously had only an arithmetic one (the
// counter happening to floor above 25).
//
// ⚠️ NO COLON, so savedQuoteStoreKey's `saved:<id>` namespace stays disjoint
// on exactly the argument it always rested on. See its comment above.
//
// ⚠️ A FINALISED QUOTE NEVER CARRIES THIS. Verified on both paths:
// save_from_payload overwrites `name` from ir.sequence on the draft -> final
// transition, and a never-persisted quote sends no name at all
// (saveQuoteToBackend's persistedName is undefined until odooQuoteId exists),
// so create() draws from the sequence. The tag lives on the working
// reference and on the draft row that reference names, and nowhere else.
//
// I, L, O and U are excluded: they are the characters people misread as 1, 1,
// 0 and V when reading a reference off a screen or a printout, and this
// string exists to be quoted back.
const TAG_ALPHABET = "ABCDEFGHJKMNPQRSTVWXYZ";
const TAG_LENGTH = 4;

/** Four letters, from the platform CSPRNG where there is one.
 *
 *  Math.random is the fallback rather than the default: this value only has
 *  to be unique among one user's unfinalised drafts, so it is not a security
 *  boundary -- but crypto is present in every browser this app runs in, and
 *  reaching for it first costs nothing. jsdom in tests has it too. */
function quoteTag(): string {
  const n = TAG_ALPHABET.length;
  const bytes = new Uint8Array(TAG_LENGTH);
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    cryptoObj.getRandomValues(bytes);
  } else {
    for (let i = 0; i < TAG_LENGTH; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  let out = "";
  // 256 % 22 != 0, so the low values are very slightly favoured. That bias is
  // irrelevant for a collision-avoidance tag among a handful of drafts, and
  // rejection sampling here would be ceremony over a fact nobody depends on.
  for (let i = 0; i < TAG_LENGTH; i++) out += TAG_ALPHABET[bytes[i] % n];
  return out;
}

// ⚠️ QUOTE NUMBERING IS CONFIGURABLE (P1-5, 2026-10-29). The working
// reference follows the same prefix and padding the sequence finalises with
// (Settings > Company, stored on Odoo's ir.sequence). Until the settings
// have been read in this tab, the values are today's defaults, so nothing
// changes for a deployment that never edits them. The prefix's date tokens
// render as the sequence renders them and MATCH any value when parsing, so
// an older reference is still counted after the year turns.
let quoteNumbering = { prefix: "Q-%(year)s-", padding: 4 };

export function setQuoteNumbering(prefix: string | null | undefined, padding: number | null | undefined): void {
  if (typeof prefix === "string" && prefix && typeof padding === "number" && padding >= 1) {
    quoteNumbering = { prefix, padding };
  }
}

const PREFIX_TOKENS: Record<string, { render: (d: Date) => string; match: string }> = {
  year: { render: (d) => String(d.getFullYear()), match: "\\d{4}" },
  y: { render: (d) => String(d.getFullYear()).slice(-2), match: "\\d{2}" },
  month: { render: (d) => String(d.getMonth() + 1).padStart(2, "0"), match: "\\d{2}" },
  day: { render: (d) => String(d.getDate()).padStart(2, "0"), match: "\\d{2}" },
};

/** The prefix as the sequence would print it today. */
export function renderQuotePrefix(prefix: string, now: Date = new Date()): string {
  return prefix.replace(/%\((\w+)\)s/g, (all, key: string) => PREFIX_TOKENS[key]?.render(now) ?? all);
}

/** Matches a reference made from this prefix: group 1 is the counter. */
export function quoteRefPattern(prefix: string): RegExp {
  const parts = prefix.split(/(%\(\w+\)s)/);
  const body = parts.map((part) => {
    const token = /^%\((\w+)\)s$/.exec(part);
    if (token && PREFIX_TOKENS[token[1]]) return PREFIX_TOKENS[token[1]].match;
    return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }).join("");
  return new RegExp(`^${body}(\\d+)(?:-[A-Z]+)?$`);
}

function nextQuoteNo(): string {
  let maxN = 0;
  const pattern = quoteRefPattern(quoteNumbering.prefix);
  for (const q of quotesStore) {
    // ⚠️ THE TAG SUFFIX IS OPTIONAL IN THIS PATTERN, and it has to be. The 25
    // shipped references have no tag; every reference minted here does. A
    // pattern that matched only the untagged shape would ignore this
    // session's own quotes, so the floor would never move and every new
    // quote in a tab would read 0026 -- unique, because the tags differ, but
    // visibly wrong.
    const m = pattern.exec(q.quoteNo);
    if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
  }
  const counter = String(maxN + 1).padStart(quoteNumbering.padding, "0");
  return `${renderQuotePrefix(quoteNumbering.prefix)}${counter}-${quoteTag()}`;
}

/** A fresh working reference, in the app's one reference format.
 *
 *  ⚠️ EXPORTED SO START-FROM-PO DOES NOT HAVE TO CREATE A MOCK QUOTE TO GET
 *  A NAME. Every other path gets its reference as a side effect of
 *  api.createQuote, which also pushes a row into the tab-lifetime mock store
 *  and seeds a material list. A quote started from a PO is opened at
 *  /saved-quotes/<real id>, where none of that store is ever read -- so
 *  creating one would be dead state that a later reader would have to work
 *  out was inert.
 *
 *  The uniqueness comes from the random tag, not from the counter, which is
 *  why this is safe to call without the store knowing about the new draft. */
export function newWorkingReference(): string {
  return nextQuoteNo();
}

// ---- Add Materials -----------------------------------------------------
//
// This section builds a raw (pre-recompute) Material row from a picked
// Product + qty. It intentionally stops short of any landed-cost/margin
// math — recomputeAll() from services/pricing.ts is still the only place
// that happens, called by the caller (QuoteDetail) with its own live
// PricingContext, exactly like every existing MaterialTable edit already
// does. This section is data assembly (rule/inventory lookups, field
// copying), not pricing calculation.

// The placeholder pricing enum (services/pricing.ts, fenced) knows three
// categories. Category NAMES are master data (P1-9), so any other name maps
// to "accessory" -- the neutral value that raises no placeholder brand
// warning, as savedQuoteToMaterials already does.
const CATEGORY_MAP: Record<string, MaterialCategory> = {
  Copper: "copper",
  Fiber: "fiber",
  Accessory: "accessory",
};
function toMaterialCategory(category: ProductCategory | null | undefined): MaterialCategory {
  return (category && CATEGORY_MAP[category]) || "accessory";
}

// A reasonable fixed starting margin for a freshly added line — there's no
// per-line negotiated margin data to draw from yet, same idea as Qty
// defaulting to 1. Editable afterward like any other MaterialTable cell.
const DEFAULT_NEW_LINE_MARGIN_PCT = 12;

// findDiscountPct AND isDiscountRuleActive WERE HERE AND ARE GONE (D3a-iii).
//
// They matched mocks/discountRules.ts on NAME STRINGS -- endClient + OEM +
// category -- against invented rules, eight of whose eighteen rows pointed at
// customers that do not exist on live. And they RETURNED 0 WHEN NOTHING
// MATCHED, which is the bug that mattered: a customer/brand pairing nobody has
// negotiated is not a pairing with no discount. The line then priced cleanly at
// OEM list under the green `live` badge with nothing to say so.
//
// The replacement is services/discountResolver.ts, which matches on
// (partner_id, brand_id) against the real anixify.discount.rule table and
// REFUSES rather than defaulting. isDiscountRuleActive also gated on a
// validTo date; the real model deliberately has no such gate -- a rate applies
// until it is archived, not until a date passes.

export interface MaterialPick {
  // ⚠️ THE RESOLVED CATALOG ROW, CARRIED BY THE CALLER -- not a part number
  // for this function to look up again.
  //
  // It used to be a bare partNo, and this function re-resolved it against
  // mockProducts. Once the picker moved to the real master that became a
  // silent data-loss bug: a real part misses the mock array, the loop hit
  // `continue`, the pick VANISHED, and the picker still closed reporting
  // success. Passing the row that was actually picked removes the second
  // lookup, and with it the possibility of the two disagreeing.
  product: CatalogProduct;
  qty: number;
  // Set when this pick was only selectable because it broke a Preferred
  // (not Strict — those can't be picked at all) brand-lock rule.
  lockWarning?: "preferred";
}

// Deltas, not a full snapshot — MaterialTable's live cell edits (qty/
// discount/margin) never get written back into materialsByQuote (same as
// every other MaterialTable edit today: it's a self-contained buffer with
// no persistence path, matching the rest of this prototype). If this
// returned a full post-add materials array, the caller would have to reseed
// MaterialTable from it and silently discard any of the user's in-progress,
// uncommitted edits to other rows. Returning newLines + a qty delta per
// bumped line instead lets the caller (QuoteDetail) merge onto its own live
// rows, preserving those edits, while this function still durably persists
// what it actually needs to (next lineNo, new lines, partNo existence) so
// the add survives navigating away and back within the tab session.
export interface AddMaterialsResult {
  newLines: Material[];
  bumpedLines: { lineNo: number; addedQty: number }[];
  touchedLineNos: number[]; // newLines + bumpedLines' lineNos, for flashing
}

// ---- Removing a line: the pure half -------------------------------------
//
// ONE renumbering implementation, used by BOTH sides of a removal.
//
// A removal has to land in two places that must not drift: the durable
// per-quote store in this file, and QuoteDetail's live `rows`, which carry
// the user's uncommitted cell edits and are therefore NOT the store's copy.
// If each renumbered for itself, one stray off-by-one would put the store
// and the screen on different line numbers -- and lineNo is what livePricing,
// the staleness fingerprint and the undo stack all key on.
//
// LINE NUMBERS ARE POSITIONAL, 1..n, with no gaps. That is what the backend
// hands back on reopen (savedQuoteToMaterials numbers by index), so keeping
// gaps locally would mean the numbers a salesperson reads before saving are
// not the ones they read after.

export interface RemoveMaterialResult {
  /** The list with the line gone and the rest renumbered 1..n. */
  rows: Material[];
  /** The row that was removed, whole. Undefined when no line matched. */
  removed?: Material;
  /** Where it sat, so an Undo can put it back there rather than at the end. */
  index: number;
}

/** Drop the line with this lineNo and renumber what is left 1..n.
 *
 * Returns the input untouched (and `removed` undefined) when nothing
 * matches, so a double-click on Remove cannot renumber twice. */
export function removeLineFromRows(rows: Material[], lineNo: number): RemoveMaterialResult {
  const index = rows.findIndex((r) => r.lineNo === lineNo);
  if (index === -1) return { rows, index: -1 };
  const removed = rows[index];
  const kept = [...rows.slice(0, index), ...rows.slice(index + 1)];
  return { rows: renumberLines(kept), removed, index };
}

/** Put a row back at `index` and renumber 1..n. The inverse of the above. */
export function restoreLineToRows(
  rows: Material[],
  removed: Material,
  index: number,
): Material[] {
  // Clamped rather than trusted: an index past the end would silently drop
  // the row on the floor, which is the one thing an Undo may never do.
  const at = Math.max(0, Math.min(rows.length, index));
  return renumberLines([...rows.slice(0, at), removed, ...rows.slice(at)]);
}

/** 1..n by position. Rebuilds every row, which is also what forks a list
 *  read straight off the shared mockMaterials array. */
function renumberLines(rows: Material[]): Material[] {
  return rows.map((r, i) => (r.lineNo === i + 1 ? r : { ...r, lineNo: i + 1 }));
}

// ---- Real backend pricing (DOMESTIC path only) -------------------------
//
// The only genuine network call in this file. It posts lines to the Odoo
// addon's /anixify/api/price_lines endpoint, which calls the validated
// pricing engine (repo /engine) and returns numbers confirmed to the paisa
// against a real client deal.
//
// SCOPE — deliberately narrow. This covers the DOMESTIC path only: the
// columns the engine has real data behind. Freight, FX, customs duty and
// landed cost are NOT computed by the backend yet and are NOT touched here
// — services/pricing.ts keeps owning those as placeholders, exactly as
// before. Do not route them through this until /engine validates an
// imported deal.
//
// NEVER FALLS BACK. On a network failure, a non-200, a malformed body, or
// an { ok: false } answer, this returns an error result and no numbers.
// It must never quietly substitute locally-computed values: the whole
// point of calling the backend is that its numbers are the trustworthy
// ones, and a silent fallback would produce plausible-looking wrong
// prices. Same principle as the backend's own hard-fail-on-missing-engine
// rule.

// Field names are snake_case because they are the backend's wire format,
// not this app's naming convention. Kept verbatim so the mapping between
// the two sides stays obvious.
export interface BackendPriceLineInput {
  oem: string;
  part_number: string;
  qty: number;
  unit_price: number;
  ibf_pct: number;
}

// The IMPORT path's line input — a separate shape, not a superset of the
// domestic one. It carries no ibf_pct at all: inbound freight on an imported
// deal is expressed as freight_pct against the supplier-currency cost, so
// sending both would be two ways of saying the same thing, and the backend
// accepts only these keys on this path.
//
// Kept as its own interface rather than a union member on purpose. A union
// would force every existing domestic call site to narrow before it could
// read `ibf_pct`, which is churn in exchange for nothing — the caller always
// knows statically which path it is on. Same optional-field / distinct-shape
// style the rest of this file already uses instead of discriminated unions
// (see PriceLinesResult's comment for why unions don't narrow here at all).
export interface BackendImportPriceLineInput {
  oem: string;
  part_number: string;
  qty: number;
  // Supplier-currency unit cost, NOT rupees — conversion_rate is what turns
  // it into INR, and doing that here instead would move a pricing step into
  // the frontend.
  unit_price: number;
  // line-sources (2026-09-30): OPTIONAL -- a line with no typed freight and
  // no shipment OMITS it and the server refuses ("No freight on this line").
  // It used to send 0.
  freight_pct?: number;
  // A line on a shipment and not yet allocated: stands in for freight_pct on
  // the stateless call only (the save names its shipment through the block).
  shipment_key?: string;
  // missing-duty-not-zero: a line with an HSN and NO stated duty OMITS
  // duty_pct and sends the HSN instead -- the server looks the duty up, or
  // refuses ("No duty rate on file for HSN X"). It used to send 0, which the
  // server read as a stated duty-free line.
  hsn_code?: string;
  // OPTIONAL because an unresolved rate is now OMITTED when the request also
  // names a supplier currency -- that absence is what asks the backend to
  // resolve it from the FX schedule. Still sent (as 0) when no currency is in
  // play, because nothing server-side could resolve it then and the engine
  // bridge rejects a missing number. See materialToBackendLine.
  conversion_rate?: number;
  duty_pct?: number;
}

// The FULLER response model — it already carries domestic outputs nothing in
// this app reads (inbound_freight, cost_with_inbound_freight, total), because
// modelling the whole response is what keeps the wire contract legible.
//
// The import path returns its own chain instead, so every field that only one
// path produces is optional: a domestic line has no landing_cost, an import
// line has no cost_with_sv. Optionals rather than a discriminated union for
// the reason spelled out on PriceLinesResult below — "strict": false means a
// union never narrows here.
export interface BackendPricedLine extends BackendPriceLineInput {
  extd_price: number;
  inbound_freight: number;
  cost_with_inbound_freight: number;
  total: number;
  // Domestic only.
  cost_with_sv?: number;
  variance_pct_snapshot: number;
  gp_pct_snapshot: number;
  unit_sp: number;
  extended_sp: number;
  // Echoed back by the backend so a response says which path produced it,
  // rather than the caller having to remember what it asked for.
  pricing_mode?: "domestic" | "import";
  // ISO code, or absent when not recorded. Not a pricing input.
  supplier_currency?: string | null;
  // The import chain. Echoed inputs first, then the computed steps:
  // value_in_inr -> landing_cost -> landing_plus_variance -> cf.
  freight_pct?: number;
  conversion_rate?: number;
  duty_pct?: number;
  freight_value?: number;
  freight_plus_unit?: number;
  value_in_inr?: number;
  landing_cost?: number;
  landing_plus_variance?: number;
  total_landing_cost?: number;
  cf?: number;
}

// The engine outputs the quote screen reads off a priced line — MaterialTable's
// "live" columns, plus every intermediate step of whichever chain produced
// them, so a per-line price explanation can be shown without a second call.
//
// Both a freshly-priced line (BackendPricedLine) and a line read back off a
// saved quote satisfy this, which is the point: a reopened quote drives the
// same live columns as a just-priced one, because in both cases these are
// engine numbers.
//
// EVERYTHING BEYOND THE FOUR REQUIRED FIELDS IS OPTIONAL, and that is the
// honest shape, not laziness:
//   - The two chains are mutually exclusive by pricing_mode. A domestic line
//     has no landing_cost, an import line has no cost_with_sv. Optionals
//     rather than a discriminated union to match this file's existing pattern
//     under "strict": false — see PriceLinesResult for why a union would never
//     narrow here.
//   - The two entry paths don't carry identical detail. A fresh price returns
//     the whole response model; the saved-quote read endpoint's _serialize_line
//     omits the domestic intermediates inbound_freight /
//     cost_with_inbound_freight / total entirely, so a reopened domestic quote
//     leaves those undefined. Nothing invents them. A consumer must render an
//     absent value as absent, never as 0 — on the domestic path 0 is also a
//     legitimate answer for inbound_freight, so the two must not be conflated.
//
// The two snapshots are the authoritative record of what a line was priced
// with. A quote's pricing config can be edited afterwards, and the compute
// deliberately does not restate priced lines, so the config's percentages
// today and these can legitimately differ. These win.
export interface LivePricedLine {
  variance_pct_snapshot: number;
  gp_pct_snapshot: number;
  unit_sp: number;
  extended_sp: number;
  // Shared by both chains: qty * unit_price, the first step either way.
  extd_price?: number;
  // The domestic chain's intermediates:
  // extd_price -> inbound_freight -> cost_with_inbound_freight -> total ->
  // cost_with_sv -> unit_sp. Present on a fresh price; absent on a reopened
  // quote for all but cost_with_sv, per the endpoint note above.
  inbound_freight?: number;
  cost_with_inbound_freight?: number;
  total?: number;
  cost_with_sv?: number;
  // The import chain's intermediates:
  // freight_value -> freight_plus_unit -> value_in_inr -> landing_cost ->
  // landing_plus_variance -> total_landing_cost -> cf -> unit_sp. Carried on
  // both entry paths — the read endpoint serializes the import chain in full.
  freight_value?: number;
  freight_plus_unit?: number;
  value_in_inr?: number;
  landing_cost?: number;
  landing_plus_variance?: number;
  total_landing_cost?: number;
  cf?: number;
}

// A single optional-field shape rather than a discriminated union: this
// project compiles with "strict": false, and without strictNullChecks
// TypeScript widens the `true`/`false` literals to `boolean`, so a union
// would never narrow at the call site. Callers branch on `ok` and read
// whichever field applies.
export interface PriceLinesResult {
  ok: boolean;
  lines?: BackendPricedLine[];
  error?: string;
}

// Stops a hung request from leaving the UI in a permanent loading state.
// One policy for every backend call — pricing and saving alike.
const BACKEND_REQUEST_TIMEOUT_MS = 15000;

// ---- The one place backend requests are built ---------------------------
//
// Every call to the Odoo API goes through backendPost(). That is the point:
// the API key header, the timeout, and the 401/network/failure handling all
// live here once, so the two existing callers can't drift and a third can't
// forget the header.
//
// It returns the parsed body on success. Callers validate their own
// response shape — this helper only knows what every call shares.

export interface BackendCallResult {
  ok: boolean;
  body?: any;
  error?: string;
  // Distinct on purpose. unauthenticated -> show the login screen;
  // forbidden -> the user is signed in and simply may not do this, so
  // sending them to a login form would loop forever.
  unauthenticated?: boolean;
  forbidden?: boolean;
  // True only when the request was aborted on the timeout. Callers that
  // WRITE need this: a timed-out write may still have committed, which a
  // timed-out read never has to worry about.
  timedOut?: boolean;
  // THE QUOTE'S STATE REFUSED THIS, and it is not going to stop refusing.
  //
  // A separate flag rather than leaving the caller to read `error`, for the
  // same reason `forbidden` is separate from a 403's message: a caller that
  // must CHANGE BEHAVIOUR needs a machine-readable answer, and matching on
  // display prose is how a reworded message silently disables a guard.
  //
  // ⚠️ ONE MATCH SITE. `quoteFinalised` is set below by the only
  // `=== "quote_finalised"` comparison in the app. Every consumer branches on
  // this boolean instead, so the wire string appears once, which is also what
  // makes it a single thing for deploy/bundle-signatures.tsv to key on.
  conflict?: boolean;
  quoteFinalised?: boolean;
  // SOMEONE ELSE WROTE THIS QUOTE SINCE THIS COPY WAS READ, so the backend
  // refused rather than overwriting them.
  //
  // ⚠️ ITS OWN FLAG, NOT A REUSE OF `quoteFinalised`, AND NOT A STRING
  // COMPARISON AT THE CALL SITE. The flag-not-string rule is REPEATED here
  // rather than reused, and the reason it must be a second flag is that the
  // two mean different things to a caller: finalised is a one-way door and
  // the correct response is to stop for good, while stale means this copy is
  // behind and could be recovered. Folding them together would make the
  // wrong recovery look correct.
  //
  // ⚠️ IT MUST NOT BE LEFT TO FALL THROUGH AS A PLAIN `ok: false`.
  // QuoteDetail's autosave latches only `quoteFinalised` and returns
  // SILENTLY on every other failure -- see freightBasisGuard.ts's header,
  // which documents that swallow and the wall it produced -- so a refusal
  // without its own flag is a refusal nobody ever sees.
  quoteStale?: boolean;
}

async function backendRequest(
  endpoint: string,
  method: "GET" | "POST",
  payload?: unknown,
): Promise<BackendCallResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), BACKEND_REQUEST_TIMEOUT_MS);

  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      // The session cookie, and nothing else. It is HttpOnly, so this is
      // the only way the browser will attach it — and there is no key
      // header to build, because the app no longer holds a secret.
      //
      // "include" rather than "same-origin" so a dev server on :3000 can
      // still talk to the deployed backend. The deployed app is
      // same-origin either way.
      credentials: "include",
      // Flat contract — no JSON-RPC envelope. The endpoints are Odoo
      // type="http" routes precisely so a plain fetch works. GET carries no
      // body; its parameters ride in the query string.
      body: method === "POST" ? JSON.stringify(payload) : undefined,
      signal: controller.signal,
    });

    // 401 and 403 are DIFFERENT problems and must not be merged. 401 means
    // the session is gone or was never established — the app should show
    // the login screen. 403 means we know who you are and your role does
    // not allow this — logging in again would change nothing, and bouncing
    // the user to a login form on a permissions error is an infinite loop.
    if (res.status === 401) {
      // session-expired-redirect: the app goes to the sign-in screen (after
      // an unsaved-changes warning). The caller still gets its failure.
      notifySessionExpired();
      return {
        ok: false,
        unauthenticated: true,
        error: "Your session has expired. Please sign in again.",
      };
    }

    if (res.status === 403) {
      // The backend names the role required; use it when present rather
      // than a generic refusal the user can do nothing with.
      let required = "";
      try {
        required = String((await res.json())?.required_role ?? "");
      } catch {
        /* a 403 with no JSON body is still a 403 */
      }
      return {
        ok: false,
        forbidden: true,
        error: required
          ? `Your role does not allow this. It needs the ${required} role.`
          : "Your role does not allow this action.",
      };
    }

    // 409 means the quote's STATE refuses this, not the caller's identity —
    // today, a draft-ward save aimed at a quote someone else already
    // finalised. Parsed rather than reported as a bare status because the
    // body names the reason ("quote_finalised") and the generic branch below
    // would throw it away, the same reason 403 reads required_role above.
    //
    // ⚠️ A 409 IS PERMANENT IN A WAY NO OTHER FAILURE HERE IS. A network
    // error, a timeout and a 500 are all worth retrying; finalising is a
    // one-way door, so every later attempt from this page gets the same
    // answer. That is why it carries a flag callers can branch on rather than
    // only a message: the correct response is to STOP, which is a behaviour,
    // and no amount of message-formatting expresses it.
    //
    // ⚠️ THE ONLY `"quote_finalised"` COMPARISON IN THE APP LIVES HERE.
    // Consumers read `quoteFinalised`. Keeping the wire string to one site is
    // what stops a second, drifting copy of "which reason means stop", and it
    // is the signature deploy/bundle-signatures.tsv keys on — an older-lineage
    // build that dropped this branch would deploy looking entirely normal.
    if (res.status === 409) {
      let reason = "";
      try {
        reason = String((await res.json())?.error ?? "");
      } catch {
        /* a 409 with no JSON body is still a 409 */
      }
      return {
        ok: false,
        conflict: true,
        quoteFinalised: reason === "quote_finalised",
        // ⚠️ THE ONLY `"quote_stale"` COMPARISON IN THE APP, on exactly the
        // same terms as the line above it. Two reasons share one status, so
        // this is the single place that turns either wire string into the
        // behaviour a caller branches on.
        quoteStale: reason === "quote_stale",
        error: reason || `Backend returned HTTP ${res.status} ${res.statusText}.`,
      };
    }

    if (!res.ok) {
      return { ok: false, error: `Backend returned HTTP ${res.status} ${res.statusText}.` };
    }

    const body = await res.json();

    // The backend answers 200 with { ok: false, error } for its own
    // failures — a bad line, a missing percentage. Surface the reason.
    if (!body?.ok) {
      // ⚠️ THE BODY RIDES ALONG ON A REFUSAL TOO, AND CALLERS MAY IGNORE IT.
      // A 200 with {ok:false} is the backend REFUSING, and some refusals are
      // structured: extract_po_document answers `no_text_layer`,
      // `no_header`, `not_a_pdf` and `extractor_unavailable`, each of which
      // the screen renders as its OWN first-class state with its own wording
      // and remedy -- not as a red box holding a sentence. Collapsing every
      // refusal to a string threw that structure away here, one layer above
      // the only place that knows what to do with it.
      //
      // Additive: `error` is unchanged, so every existing caller behaves
      // exactly as before.
      return {
        ok: false,
        body,
        error: String(body?.error ?? body?.message ?? "Backend reported an unknown failure."),
      };
    }

    return { ok: true, body };
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return {
        ok: false,
        timedOut: true,
        error: `Backend did not respond within ${BACKEND_REQUEST_TIMEOUT_MS / 1000}s.`,
      };
    }
    // Most often CORS or the host being unreachable. fetch gives no detail
    // for either, so say so rather than guessing.
    return {
      ok: false,
      error: `Could not reach the backend at ${API_BASE}. Check it is running and reachable (a CORS block looks identical here). ${
        err instanceof Error ? err.message : String(err)
      }`,
    };
  } finally {
    clearTimeout(timeout);
  }
}

// A FILE, not JSON -- the one sibling of backendRequest, and deliberately on
// the same terms: the session cookie and nothing else, the same timeout, and
// the same 401 / 403 split and wording. A 200 that is JSON rather than a file
// is the backend refusing (e.g. a bad date) and is reported as such.
async function backendDownload(
  endpoint: string,
): Promise<{ ok: boolean; blob?: Blob; filename?: string; unauthenticated?: boolean;
            forbidden?: boolean; error?: string }> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), BACKEND_REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      method: "GET", credentials: "include", signal: controller.signal,
    });
    if (res.status === 401) {
      notifySessionExpired();
      return { ok: false, unauthenticated: true,
               error: "Your session has expired. Please sign in again." };
    }
    if (res.status === 403) {
      let required = "";
      try { required = String((await res.json())?.required_role ?? ""); } catch { /* still a 403 */ }
      return { ok: false, forbidden: true,
               error: required ? `Your role does not allow this. It needs the ${required} role.`
                               : "Your role does not allow this action." };
    }
    if (!res.ok) return { ok: false, error: `Backend returned HTTP ${res.status} ${res.statusText}.` };
    const type = res.headers.get("Content-Type") ?? "";
    if (type.includes("json")) {
      let reason = "";
      try { reason = String((await res.json())?.error ?? ""); } catch { /* unreadable */ }
      return { ok: false, error: reason || "The backend did not send a file." };
    }
    const disposition = res.headers.get("Content-Disposition") ?? "";
    const match = /filename="([^"]+)"/.exec(disposition);
    return { ok: true, blob: await res.blob(), filename: match ? match[1] : "download.xlsx" };
  } catch (err) {
    return { ok: false, error: (err as Error)?.name === "AbortError"
      ? "The download took too long and was stopped."
      : `Could not reach the backend at ${API_BASE}.` };
  } finally {
    clearTimeout(timeout);
  }
}

const backendPost = (endpoint: string, payload: unknown) =>
  backendRequest(endpoint, "POST", payload);

const backendGet = (endpoint: string) => backendRequest(endpoint, "GET");

// ---- PO documents (slice A) ----------------------------------------------
//
// STORAGE ONLY. Nothing here reads a document's contents; comparing a PO
// against the quote is slice B and is a different act with a different risk.
//
// ⚠️ THE BYTES GO AS BASE64 IN A JSON BODY, NOT multipart/form-data, AND
// THAT COSTS A THIRD OF THE TRANSFER. What it buys is that this stays inside
// backendRequest() -- the one seam carrying the session cookie, the 15s
// abort and the 401/403 split. A multipart upload would be the first request
// in this app to bypass it, and the first able to forget any of them.

/** One stored PO, as the list renders it. Never the bytes. */
export interface QuoteDocument {
  id: number;
  filename: string;
  mimetype: string;
  size_bytes: number;
  uploaded_at: string;
  // ⚠️ WHO UPLOADED IT, RECORDED FROM THE SESSION (TASK 3). null means
  // UNKNOWN -- a file stored before attribution existed -- and is shown as
  // "unknown", never guessed.
  uploaded_by: string | null;
  uploaded_by_uid: number | null;
}

/** What the browser will send before the server decides. Mirrors the
 *  server's own cap and allowlist so a hopeless upload fails instantly with
 *  a reason -- but the SERVER's copy is the one that counts, because a
 *  caller is not obliged to run this code. */
export const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_DOCUMENT_TYPES = ".pdf,.png,.jpg,.jpeg,.webp";

export function documentTooLargeMessage(bytes: number): string {
  return (
    `That file is ${(bytes / (1024 * 1024)).toFixed(1)} MB. The limit for a ` +
    `PO document is ${MAX_DOCUMENT_BYTES / (1024 * 1024)} MB.`
  );
}

/** A File as base64, without the `data:...;base64,` prefix.
 *
 *  ⚠️ readAsDataURL, NOT readAsText. The CSV import reads text because a CSV
 *  IS text; a PDF is bytes and readAsText would mangle them silently into
 *  replacement characters -- a corrupt file that still uploads. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The file could not be read."));
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

export async function listQuoteDocuments(
  quoteId: number,
): Promise<{ ok: boolean; documents?: QuoteDocument[]; error?: string;
            unauthenticated?: boolean; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.quoteDocuments, { quote_id: quoteId });
  if (!call.ok) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  return { ok: true, documents: (call.body?.documents ?? []) as QuoteDocument[] };
}

/** One cell the extractor read, or one it could not. Deliberately the SAME
 *  shape as services/poPaste.ts's Cell, so the review table renders both
 *  producers with no branching. */
export type ExtractCell =
  | { kind: "value"; text: string; n?: number }
  | { kind: "unreadable"; reason: string };

export interface ExtractedRow {
  index: number;
  partNumber: ExtractCell;
  quantity: ExtractCell;
  unitPrice: ExtractCell;
  description: ExtractCell;
  manufacturer: ExtractCell;
  lineTotal: ExtractCell;
  uom: ExtractCell;
  raw: string[];
  flagged: boolean;
  flagReason: string;
  supplierContext: Record<string, string>;
  pages: number[];
  // --- the format profiles add these. OPTIONAL, because the generic reader
  // does not produce them and a row from it must stay a valid ExtractedRow.
  /** The line number the document printed. Text: a PO may print "1.1". */
  lineNumber?: string | null;
  /** ⚠️ ITS OWN FIELD AND NEVER THE PART NUMBER. The supplier's code varies
   *  by seller; nothing in this system matches on it. */
  anixterPartNumber?: ExtractCell;
  /** Each schedule row as printed. More than one means the document states NO
   *  single quantity for the line -- see `shipmentsNote`. */
  shipments?: PoShipment[];
}

/** The header facts a format profile reads off the document, as printed.
 *
 *  ⚠️ EVERY FIELD IS OPTIONAL AND AN ABSENT ONE IS UNKNOWN, NEVER EMPTY. The
 *  generic reader produces no header at all, and the Oracle profile returns
 *  null for a heading printed with no value beneath it -- which is a real
 *  reading of the document and must not be shown as a blank somebody typed. */
export interface ExtractedHeader {
  format?: string;
  po_number?: string | null;
  po_date?: string | null;
  po_date_raw?: string | null;
  po_revision?: string | null;
  po_revision_date?: string | null;
  change_order?: string | null;
  po_total?: number | null;
  po_total_raw?: string | null;
  currency?: string | null;
  vendor?: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  payment_terms?: string | null;
  freight_terms?: string | null;
  fob?: string | null;
  shipping_method?: string | null;
  /** po-deliver-to-match: the ONE Deliver-To location code printed, or null. */
  deliver_to_code?: string | null;
  /** ⚠️ ALL OF THEM, EACH WITH ITS OWN LABEL. Never one picked silently. */
  emails?: { label: string; value: string }[];
  phones?: { label: string; value: string }[];
  /** What the reader could not settle. Recorded, never resolved. */
  flags?: string[];
}

export interface ExtractResult {
  ok: boolean;
  /** "" on success. Otherwise no_text_layer | no_header | not_a_pdf |
   *  extractor_unavailable -- each a FIRST-CLASS STATE with its own wording,
   *  not an error string to dump in a red box. */
  reason?: string;
  /** Set on a refusal, and also on a PARTIAL text layer, where ok is true. */
  message?: string;
  textLayer?: {
    kind: "text" | "partial" | "none";
    pages: number;
    blank_pages: number[];
    message: string;
  };
  rows?: ExtractedRow[];
  /** The header the format profile read, or absent on the generic path. */
  header?: ExtractedHeader;
  documentCheck?: {
    checked: boolean;
    reason: string;
    stated: number | null;
    summed: number | null;
    balanced: boolean | null;
  };
  /** po-deliver-to-match: an EXACT ref match under this quote's customer,
   *  or null. A suggestion only -- nothing is set without the click. */
  ship_to_suggestion?: ShipToSuggestion | null;
  error?: string;
  unauthenticated?: boolean;
  forbidden?: boolean;
}

/** Read PO lines out of an already-uploaded PDF. THE DOCUMENT NEVER LEAVES
 *  THE SERVER -- extraction is local pdfminer inside Odoo, and this call sends
 *  only two integers.
 *
 *  ⚠️ IT NEVER FALLS BACK. A failure returns `ok: false` with the reason the
 *  backend gave, and the caller shows that reason. It must never quietly
 *  substitute an empty row list: "no lines" is a claim about the customer's
 *  order, and "I could not read it" is not. */
export async function extractPoDocument(
  quoteId: number, documentId: number,
): Promise<ExtractResult> {
  const call = await backendPost(ENDPOINTS.extractPoDocument, {
    quote_id: quoteId, document_id: documentId,
  });
  // ⚠️ A REFUSAL IS NOT AN ERROR, AND THE DIFFERENCE IS THE WHOLE UI. A
  // scanned PDF, a missing header and a non-PDF are things the screen should
  // EXPLAIN with a remedy; a dead session or a 500 is an error. Both arrive
  // here as call.ok === false, so the structured `reason` decides which.
  const body = call.body ?? {};
  if (!call.ok && !body.reason) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  return {
    ok: Boolean(body.ok),
    reason: body.reason ?? "",
    message: body.message ?? "",
    textLayer: body.textLayer,
    rows: (body.rows ?? []) as ExtractedRow[],
    // ⚠️ PASSED THROUGH, NOT RESHAPED. An absent header is absent; an empty
    // object here would read on screen as "the document printed nothing",
    // which is a different claim from "this reader has no header for it".
    header: body.header as ExtractedHeader | undefined,
    documentCheck: body.documentCheck,
    // po-deliver-to-match: the server's exact-match suggestion, or null.
    ship_to_suggestion: (body.ship_to_suggestion ?? null) as ShipToSuggestion | null,
    error: body.ok ? undefined : (body.error ?? body.message),
  };
}

export async function uploadQuoteDocument(
  quoteId: number, file: File,
): Promise<{ ok: boolean; document?: QuoteDocument; error?: string;
             unauthenticated?: boolean; forbidden?: boolean }> {
  // Checked here so a 12 MB file fails in the browser rather than after a
  // 16 MB upload. The server checks again and its answer is the real one.
  if (file.size > MAX_DOCUMENT_BYTES) {
    return { ok: false, error: documentTooLargeMessage(file.size) };
  }
  let data_b64: string;
  try {
    data_b64 = await fileToBase64(file);
  } catch (err) {
    return { ok: false, error: (err as Error).message };
  }
  const call = await backendPost(ENDPOINTS.uploadQuoteDocument, {
    quote_id: quoteId, filename: file.name, data_b64,
  });
  if (!call.ok) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  // ⚠️ NEVER REPORT A STORED FILE WITHOUT ONE COMING BACK. The returned row
  // is the only proof it exists, exactly as quote_id is for a saved quote.
  const document = call.body?.document as QuoteDocument | undefined;
  if (!document || typeof document.id !== "number") {
    return { ok: false, error: "Upload reported success but no document came back." };
  }
  return { ok: true, document };
}

export async function deleteQuoteDocument(
  quoteId: number, documentId: number,
): Promise<{ ok: boolean; error?: string;
             unauthenticated?: boolean; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.deleteQuoteDocument, {
    quote_id: quoteId, document_id: documentId,
  });
  if (!call.ok) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  return { ok: true };
}

// ---- customer PO lines and the comparison (slice B) ----------------------
//
// ⚠️ NOTHING HERE READS A DOCUMENT. PO lines are TYPED BY A PERSON. The
// comparison is the product; extraction is a replaceable input and there is
// none in this build. The verdicts are computed SERVER-SIDE by one pure
// module (addons/anixify_cpq/models/po_comparison.py) so there is exactly one
// implementation of the decision, not one per client.

/** The six verdicts, and no others. Mirrors po_comparison.VERDICTS. */
export type PoVerdict =
  | "matched"
  | "quantity_mismatch"
  | "price_mismatch"
  | "part_not_on_quote"
  | "on_quote_not_on_po"
  | "ambiguous";

export interface PoLineInput {
  part_number: string;
  quantity: number;
  unit_price: number;
  description?: string;
  manufacturer?: string;
  // --- carried through from an extracted PO. All optional: a typed line has
  // none of them, and the backend's line allowlist ignores what is absent.
  /** As printed. Text, because a PO may print "1.1" or "10A". */
  line_number?: string;
  uom?: string;
  /** ⚠️ THE PRICE EXACTLY AS PRINTED, kept beside the numeric `unit_price`
   *  because Oracle prints four decimals and a Float loses the typography.
   *  The screen shows this; arithmetic uses the number. */
  unit_price_text?: string;
  /** As printed on the PO. ⚠️ NEVER computed from quantity x unit price. */
  extended_price?: number;
  extended_price_text?: string;
  /** ⚠️ NEVER the part number and never matched on. */
  anixter_part_number?: string;
  /** Why the document printed no single quantity for this line. */
  shipments_note?: string;
}

export interface PoLine extends PoLineInput {
  id: number;
  /** quantity x unit_price, DERIVED server-side and never stored. */
  extended: number;
  line_origin: "manual" | "extracted" | null;
}

export interface QuotePo {
  id: number;
  po_reference: string;
  po_date: string | null;
  currency: string | null;
  notes: string | null;
  document_id: number | null;
  created_at: string;
  // ⚠️ WHO RECORDED / LAST EDITED IT, from the SESSION (TASK 3) -- it used to
  // be create_uid, which named the superuser. null means UNKNOWN (a PO
  // recorded before attribution existed) and is shown as "unknown".
  created_by: string | null;
  created_by_uid?: number | null;
  last_edited_by?: string | null;
  last_edited_by_uid?: number | null;
  lines: PoLine[];
  // --- what the PO itself says, as printed. Optional throughout: a PO
  // recorded before the profiles existed, or typed by hand, has none of it.
  //
  // ⚠️ NONE OF THESE IS A LINK TO ANYTHING. customer_name is text the
  // document printed -- it is not the quote's customer and is never written
  // back to one.
  po_format?: string | null;
  po_revision?: string | null;
  po_revision_date?: string | null;
  vendor_name?: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  payment_terms?: string | null;
  freight_terms?: string | null;
  fob?: string | null;
  shipping_method?: string | null;
  po_total?: number | null;
  po_total_text?: string | null;
  contact_emails?: string | null;
  contact_phones?: string | null;
  extraction_flags?: string | null;
  // po-deliver-to-match: the Deliver-To code the PO printed. Reference only.
  deliver_to_code?: string | null;
}

/** The header fields a save may carry, beside the lines. Exactly the
 *  backend's `_PO_HEADER_FIELDS`, and deliberately nothing else. */
export interface PoHeaderInput {
  po_format?: string | null;
  po_revision?: string | null;
  po_revision_date?: string | null;
  vendor_name?: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  payment_terms?: string | null;
  freight_terms?: string | null;
  fob?: string | null;
  shipping_method?: string | null;
  po_total?: number | null;
  po_total_text?: string | null;
  contact_emails?: string | null;
  contact_phones?: string | null;
  extraction_flags?: string | null;
  // po-deliver-to-match: the Deliver-To code the PO printed. Reference only.
  deliver_to_code?: string | null;
}

export interface PoComparisonRow {
  verdict: PoVerdict;
  part_number: string;
  manufacturer: string | null;
  po_line_id: number | null;
  quote_line_id: number | null;
  po_quantity: number | null;
  quote_quantity: number | null;
  po_unit_price: number | null;
  quote_unit_price: number | null;
  candidate_quote_line_ids: number[];
}

export interface PoComparison {
  po_id: number;
  po_reference: string;
  po_currency: string | null;
  quote_currency: string | null;
  /** ⚠️ TRUE MEANS NOTHING WAS COMPARED. Two known, different currencies --
   *  the backend refuses rather than converting, because converting is
   *  pricing. `rows` is empty and `refusal_reason` says why. */
  refused: boolean;
  refusal_reason: string | null;
  counts: Record<PoVerdict, number>;
  rows: PoComparisonRow[];
}

export async function listQuotePos(quoteId: number): Promise<{
  ok: boolean; pos?: QuotePo[]; error?: string;
  // ⚠️ THE QUOTE'S OWN CUSTOMER AND CURRENCY, FROM THE BACKEND, and this is
  // what the review screen's amber warnings are weighed against. They come
  // down this route rather than being read off the open quote in the browser
  // for one reason: the backend has exactly one definition of each -- the
  // quote's partner, and `_quote_sell_currency_name` -- and those are the
  // definitions the comparison itself uses. A display string deciding whether
  // a warning fires would be a second, silently divergent answer.
  //
  // Absent (not "") means nobody recorded one, which is NOT a disagreement
  // with anything and raises no warning.
  quoteCustomerName?: string | null;
  quoteCurrency?: string | null;
  unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.quotePos, { quote_id: quoteId });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  return {
    ok: true,
    pos: (call.body?.pos ?? []) as QuotePo[],
    // ⚠️ `?? null` RATHER THAN A DEFAULT. A backend predating these keys
    // sends neither, and the honest reading of that is "unknown" -- which
    // shows no warning, rather than a warning against a guessed value.
    quoteCustomerName: (call.body?.quote_customer_name ?? null) as string | null,
    quoteCurrency: (call.body?.quote_currency ?? null) as string | null,
  };
}

export async function saveQuotePo(
  quoteId: number,
  po: {
    po_id?: number; po_reference: string; po_date?: string;
    currency?: string; notes?: string; document_id?: number;
    lines: PoLineInput[];
  } & PoHeaderInput,
): Promise<{ ok: boolean; po?: QuotePo; error?: string;
             // quote-won-lost: a PO on a finalised quote marks it Won; this is
             // the outcome as the backend now reports it (undefined if none).
             outcome?: QuoteOutcome;
             unauthenticated?: boolean; forbidden?: boolean }> {
  // ⚠️ line_origin IS NOT SENT AND MUST NOT BE. The server sets "manual" and
  // refuses "extracted" outright -- there is no extractor, and a payload able
  // to claim one could put a line on screen saying a document was read.
  const call = await backendPost(ENDPOINTS.saveQuotePo, { quote_id: quoteId, ...po });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  const saved = call.body?.po as QuotePo | undefined;
  // Never report a stored PO without one coming back: the returned row is the
  // only proof it exists, exactly as quote_id is for a saved quote.
  if (!saved || typeof saved.id !== "number") {
    return { ok: false, error: "Save reported success but no PO came back." };
  }
  return { ok: true, po: saved, outcome: toQuoteOutcome(call.body?.outcome) };
}

export async function compareQuotePo(quoteId: number, poId: number): Promise<{
  ok: boolean; comparison?: PoComparison; error?: string;
  unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.compareQuotePo, {
    quote_id: quoteId, po_id: poId,
  });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  return { ok: true, comparison: call.body?.comparison as PoComparison };
}

export async function deleteQuotePo(quoteId: number, poId: number): Promise<{
  ok: boolean; error?: string; unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.deleteQuotePo, {
    quote_id: quoteId, po_id: poId,
  });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  return { ok: true };
}

/** Delete an EMPTY DRAFT. The only call in this app that removes a quote.
 *
 *  ⚠️ IT NEVER REPORTS A DELETE THAT DID NOT HAPPEN. Every refusal -- the
 *  quote is finalised, or still holds a line, a PO, a document or a shipment
 *  -- arrives as HTTP 200 with {ok:false, error}, which backendRequest
 *  already turns into call.ok === false. So `ok: true` here means the row is
 *  gone, on the same terms as saveQuoteToBackend's rule that a quote is only
 *  "saved" when the backend returned an id.
 *
 *  ⚠️ IT DOES NOT REMOVE THE QUOTE'S DOCUMENTS FIRST. A quote still holding
 *  one is refused by the backend, deliberately: deleting the paperwork has to
 *  go through deleteQuoteDocument so the audit trail records who did it. A
 *  caller that wants both does them in that order and STOPS if the first
 *  fails -- a partial delete must never be reported as a success. */
export async function deleteQuote(quoteId: number): Promise<{
  ok: boolean; removed?: { quote_id: number; name?: string };
  error?: string; unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.deleteQuote, { quote_id: quoteId });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  return {
    ok: true,
    removed: call.body?.removed as { quote_id: number; name?: string },
  };
}

/** Discard a DRAFT and its lines (discard-draft). The server enforces every
 *  rule and its refusal comes back verbatim. */
export async function discardDraft(quoteId: number): Promise<{
  ok: boolean; removed?: { quote_id: number; name?: string; lines?: number };
  error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.discardDraft, { quote_id: quoteId });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  return { ok: true, removed: call.body?.removed as { quote_id: number; name?: string; lines?: number } };
}

/** The download URL for one stored document.
 *
 *  ⚠️ A PLAIN LINK, NOT A fetch(). The cookie is HttpOnly and SameSite=Lax,
 *  and Lax sends it on a top-level GET -- so an <a> reaches the role-gated
 *  route with the session attached, and the browser handles the download
 *  from the Content-Disposition the server sets. Pulling the bytes through
 *  fetch() into a blob would put a whole PO in memory to achieve the same
 *  thing, and would need an object URL nothing revokes. */
export function quoteDocumentUrl(quoteId: number, documentId: number): string {
  return (
    `${API_BASE}${ENDPOINTS.quoteDocument}` +
    `?quote_id=${encodeURIComponent(String(quoteId))}` +
    `&document_id=${encodeURIComponent(String(documentId))}`
  );
}

// ---- Drafts --------------------------------------------------------------
//
// The SPA's build state used to live in module-level JavaScript and died on
// reload. A draft routes it through the SAME save endpoint a real quote
// uses, rather than a second store: a draft IS an anixify.quote carrying
// is_draft, which keeps it out of the quote list and out of the numbering
// until the user finalises it.
//
// The draft's `name` is this app's own working reference (the quoteNo in the
// URL), which is how it is found again after a reload -- the module store
// that used to hold the link is exactly what does not survive.

export interface DraftSummary {
  quote_id: number;
  name: string;
}

/** Save the working set as a draft. Returns the backend id, or an error.
 *
 * Deliberately reuses saveQuoteToBackend's payload shape via the same
 * endpoint, so a draft is priced by the engine like anything else and there
 * is no second, unvalidated write path. */
export async function saveDraft(
  quote: Quote,
  lines: Material[],
  variancePct: number,
  gpPct: number,
  draftId?: number,
  pricingContext?: PricingContext,
  // The quote's REAL shipments, as the panel holds them. Passed on the SAME
  // terms as the final save and built by the SAME builder -- see the note on
  // the `shipments` key below for why a draft that decided this differently
  // would fight the save it is meant to back up.
  shipments?: QuoteShipment[],
  // The token this copy last read. Sent on BOTH save paths or neither, for
  // the same reason the shipments block is: the two write the same record,
  // and a draft tick that omitted the token while the press sent it would
  // leave the more frequent writer able to overwrite what the guarded one
  // could not.
  saveToken?: number,
): Promise<{
  ok: boolean;
  quoteId?: number;
  shipments?: BackendShipment[];
  // The token as this tick left it, to echo on the next one. Adopted under
  // the same supersession guard as the shipment ids -- see QuoteDetail.
  saveToken?: number;
  // THE SAVED LINES, purely so adoptSavedShipments can correlate a returned
  // shipment id back to the panel key that produced it -- lines[i].shipment_id
  // against the row this app sent at the same index.
  //
  // RETURN SHAPE ONLY. `lines` has always been in this response (one route and
  // one serializer serve the draft and the final save alike); saveDraft simply
  // did not surface it, while saveQuoteToBackend already did. Nothing about
  // the request changes.
  //
  // It matters most HERE rather than on the final save: the autosave fires
  // every 1.5s, so a shipment added in the panel gets its id -- and, before
  // this, its rename -- long before anyone presses Save.
  lines?: BackendSavedLine[];
  // THE QUOTE WAS FINALISED BY SOMEONE ELSE, so this draft tick was refused
  // and so is every one after it. Surfaced because the caller must STOP
  // autosaving rather than treat it as one more failed attempt -- see the
  // 409 branch in backendRequest, which owns the wire string.
  quoteFinalised?: boolean;
  // SOMEONE ELSE WROTE THIS QUOTE since this copy was read. Distinct from
  // quoteFinalised above: that one is a one-way door, this one means this
  // copy is behind. Its own flag, never a string match.
  quoteStale?: boolean;
  error?: string;
}> {
  if (lines.length === 0) return { ok: false, error: "Nothing to save yet." };

  const mode = quote.pricingMode ?? "domestic";
  const shipmentsBlock = shipmentsFor(mode, quote.supplierCurrency, shipments);
  const call = await backendPost(ENDPOINTS.saveQuote, {
    ...(draftId !== undefined ? { quote_id: draftId } : {}),
    // The working reference, so this draft can be found again by name.
    name: quote.quoteNo,
    is_draft: true,
    // IDS when this quote has a picked customer. partner_name is sent ONLY
    // as the fallback for a quote that has not been given one yet -- the
    // backend reads it last, and this app stops relying on it the moment a
    // customer is chosen.
    ...(quote.customer?.entity?.id
      ? customerToBackendPayload(quote.customer)
      : { partner_name: quote.endClient ?? "" }),
    variance_pct: variancePct,
    gp_pct: gpPct,
    pricing_mode: mode,
    // Omitted when unchosen, so a save never blanks a currency someone set:
    // the backend treats an absent key as "leave the stored one alone".
    ...(quote.supplierCurrency ? { supplier_currency: quote.supplierCurrency } : {}),
    // project-id: sent when known; an absent key leaves the stored one alone.
    ...(quote.projectId ? { project_ref: quote.projectId } : {}),
    ...(quote.projectName ? { project_name: quote.projectName } : {}),
    // warehouse-master: only a choice made on this screen; absent = untouched.
    ...(quote.dispatchWarehouseChoice !== undefined ? { warehouse_id: quote.dispatchWarehouseChoice } : {}),
    // Omitted entirely when the caller has none, because the backend treats
    // an absent key as "leave what is stored alone". Sending null would clear
    // settings the user chose.
    ...(pricingContext ? { pricing_context: pricingContext } : {}),
    // THE REAL SHIPMENT RECORDS, on exactly the same terms as the final save.
    //
    // ⚠️ BOTH PATHS OR NEITHER. This autosaves every 1.5s while the final save
    // is a button press, so a draft tick that omitted the block while Save
    // sent it -- or vice versa -- would leave the two disagreeing about the
    // quote's shipment set, with the more frequent writer winning. The shared
    // builder is what keeps the absent/empty decision in one place.
    ...(shipmentsBlock ? { shipments: shipmentsBlock } : {}),
    // Omitted when unknown rather than sent as 0 -- 0 is a real token held
    // by every quote written before the column existed. See the same key on
    // the final save.
    ...(saveToken !== undefined ? { save_token: saveToken } : {}),
    lines: lines.map((m) => materialToBackendSaveLine(m, mode)),
  });

  // Carried through rather than flattened into `error`: these are the
  // failures the caller reacts to differently from every other one.
  if (!call.ok) {
    return {
      ok: false,
      quoteFinalised: call.quoteFinalised,
      quoteStale: call.quoteStale,
      error: call.error,
    };
  }
  if (typeof call.body?.quote_id !== "number") {
    return { ok: false, error: "Draft saved but no id came back." };
  }
  // The shipments as written, ids included, so the NEXT autosave tick updates
  // them instead of recreating them. This path fires every 1.5s, so without
  // it the churn is continuous rather than once per button press.
  return {
    ok: true,
    quoteId: call.body.quote_id,
    shipments: call.body.shipments as BackendShipment[] | undefined,
    lines: call.body.lines as BackendSavedLine[] | undefined,
    saveToken:
      typeof call.body.save_token === "number" ? call.body.save_token : undefined,
  };
}

/** Create an EMPTY draft and return its backend id. Start-from-PO only.
 *
 *  ⚠️ WHY THIS IS NOT saveDraft. saveDraft refuses a lineless payload on the
 *  client (`"Nothing to save yet."`), and that guard EARNS ITS KEEP: it is
 *  what stops the 1.5s autosave writing a row for every quote screen somebody
 *  merely opens. Relaxing it would make every empty New Quote create a draft,
 *  which is a change to existing quote creation and not what was asked for.
 *
 *  So this is a second, explicit call for the one flow that genuinely needs a
 *  row before it has anything to put in it: a quote started from a customer
 *  PO, where the PDF must be attached to something the moment it is chosen
 *  rather than held unsaved in the browser.
 *
 *  ⚠️ IT IS THE SAME ENDPOINT AND THE SAME ENVELOPE. `save_quote` with
 *  `is_draft: true` and `lines: []` -- the narrowest relaxation the backend
 *  allows (17.0.1.34.0 permits empty lines ONLY on a create, and only for a
 *  draft; an UPDATE with empty lines is still refused). There is no new route
 *  and no second write path.
 *
 *  ⚠️ NO CUSTOMER IS SENT, AND THAT IS THE POINT. The customer is not known
 *  until the PO has been read. `partner_name` is omitted entirely rather than
 *  sent empty, so the backend's find-or-create is never reached and no
 *  partner with a blank name can be born here. A draft may have no customer
 *  (17.0.1.34.0); only FINALISING requires one (17.0.1.35.0).
 *
 *  ⚠️ AND NO PRICE OF ANY KIND. The two percentages are the standing config
 *  the engine is driven with, exactly as every other save sends them. Nothing
 *  from the PO reaches this call -- the PO has not even been read yet. */
export async function createEmptyDraft(
  workingReference: string,
  variancePct: number,
  gpPct: number,
): Promise<{
  ok: boolean; quoteId?: number; error?: string;
  unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveQuote, {
    name: workingReference,
    is_draft: true,
    lines: [],
    variance_pct: variancePct,
    gp_pct: gpPct,
  });
  if (!call.ok) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  // ⚠️ NEVER REPORT A DRAFT THAT DID NOT LAND. The id is the only proof the
  // row exists -- the same rule saveQuoteToBackend follows -- and here it
  // matters twice over, because the very next thing the flow does is attach a
  // PDF to that id.
  const quoteId = call.body?.quote_id;
  if (typeof quoteId !== "number") {
    return { ok: false, error: "The draft was not created: no quote id came back." };
  }
  return { ok: true, quoteId };
}

/** This user's drafts, as full summary rows for the quotes list.
 *
 *  ⚠️ SAME ENDPOINT, SAME SERIALIZER, NO BACKEND CHANGE. `?drafts=1` has
 *  always answered with _serialize_quote_summary, exactly as the unfiltered
 *  list does -- partner, line count, engine grand total, state and date were
 *  already on the wire and were simply thrown away by listDrafts below, which
 *  only ever needed the id and the name. Nothing new is asked of the server.
 *
 *  ⚠️ AND THE SCOPING IS THE SERVER'S, NOT OURS. The route resolves
 *  `[("is_draft","=",True), ("user_id","=",acting.id)]` from the SESSION, so a
 *  caller cannot ask for anybody else's drafts -- there is no user parameter
 *  to pass. That is precisely why drafts are merged HERE rather than by
 *  widening the list domain: one salesperson's unfinished quote stays theirs.
 *  Do NOT add a user argument to this function; it would imply a capability
 *  the endpoint deliberately does not have.
 *
 *  Kept separate from listDrafts() rather than replacing it: that one answers
 *  "does a draft exist for this working reference" for findDraftFor, a
 *  different question with a different shape, and widening it would make its
 *  caller carry fields it never reads. */
export async function listDraftQuotes(): Promise<{
  ok: boolean; quotes?: SavedQuoteSummary[]; error?: string;
  unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendGet(`${ENDPOINTS.quotes}?drafts=1&limit=200`);
  if (!call.ok) {
    return {
      ok: false, error: call.error,
      unauthenticated: call.unauthenticated, forbidden: call.forbidden,
    };
  }
  const quotes = (call.body?.quotes ?? []) as SavedQuoteSummary[];
  // ⚠️ MARKED FROM THE ROW WHERE THE BACKEND SAID SO, and defaulted to true
  // only for a response that omitted the key. Every row from THIS call is a
  // draft by the domain that produced it, so a missing flag means a stale
  // backend, not a finalised quote -- and calling it finalised would put a
  // draft on screen wearing a real quote's status.
  return {
    ok: true,
    quotes: quotes.map((q) => ({ ...q, is_draft: q.is_draft ?? true })),
  };
}

// ---- THE QUOTES LIST SCREEN (quotes-list-usability, 2026-10-06) -------------------
//
// ONE read: /anixify/api/quote_list pages, sorts and filters on the server and
// decides SCOPE there (Admin: all; anyone else: finalised quotes + their own
// drafts). The filters sent are requests to NARROW; no parameter widens
// anything, and a role that may not use one (owner, follow-up) is refused 403.

export interface QuoteListRow extends SavedQuoteSummary {
  /** draft / awaiting / open / expired / won / lost / superseded, from the server. */
  status_key?: string;
  valid_until?: string | null;
  days_left?: number | null;
  /** M4 (D195): on a Won quote, the quantity still to order from OEMs; null otherwise. */
  left_to_order?: number | null;
}

export interface QuoteListPage {
  quotes: QuoteListRow[];
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  sort: string;
  dir: string;
  awaitingCount: number;
  customers: { id: number; name: string }[];
  owners: { uid: number; name: string }[];
  scope: string;
}

export async function listQuotesPage(params: Record<string, string>): Promise<{
  ok: boolean; page?: QuoteListPage; error?: string; unauthenticated?: boolean; forbidden?: boolean;
}> {
  const query = new URLSearchParams(params).toString();
  const call = await backendGet(`${ENDPOINTS.quoteList}${query ? `?${query}` : ""}`);
  if (!call.ok) {
    return { ok: false, error: call.error, unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  const b = call.body ?? {};
  if (!Array.isArray(b.quotes) || typeof b.total !== "number") {
    return { ok: false, error: "The quote list came back in a shape this app does not recognise." };
  }
  return {
    ok: true,
    page: {
      quotes: (b.quotes as QuoteListRow[]).map((q) => ({ ...q, is_draft: q.is_draft ?? false })),
      total: b.total, page: b.page ?? 1, pages: b.pages ?? 1, pageSize: b.page_size ?? 50,
      sort: b.sort ?? "date", dir: b.dir ?? "desc", awaitingCount: b.awaiting_count ?? 0,
      customers: Array.isArray(b.customers) ? b.customers : [],
      owners: Array.isArray(b.owners) ? b.owners : [],
      scope: b.scope ?? "",
    },
  };
}

/** This user's drafts. Scoped to the session server-side. */
export async function listDrafts(): Promise<DraftSummary[]> {
  const call = await backendGet(`${ENDPOINTS.quotes}?drafts=1&limit=200`);
  if (!call.ok || !Array.isArray(call.body?.quotes)) return [];
  return call.body.quotes.map((q: any) => ({ quote_id: q.quote_id, name: q.name }));
}

/** The draft for this working reference, if one was left behind. */
export async function findDraftFor(quoteNo: string): Promise<number | undefined> {
  const drafts = await listDrafts();
  // Newest wins: ids ascend, so a stale duplicate never shadows the current
  // one. The backend updates in place, so there should only ever be one.
  const match = drafts.filter((d) => d.name === quoteNo).sort((a, b) => b.quote_id - a.quote_id)[0];
  return match?.quote_id;
}

/** What a working reference resolves to among THIS user's drafts.
 *
 *  An optional-field interface, not a union: this project compiles without
 *  strictNullChecks, where a union on `status` is not worth what it costs to
 *  read. `quoteId` is set only for "found", `count` only for "ambiguous". */
export interface WorkingReferenceLookup {
  status: "found" | "none" | "ambiguous" | "error";
  quoteId?: number;
  count?: number;
  // "none" was answered from a FULL page of drafts, so an older draft could
  // sit beyond it. The screen says so rather than claiming a clean miss.
  truncated?: boolean;
  error?: string;
}

const REFERENCE_LOOKUP_LIMIT = 200;

/** Resolve a working reference that is not in this tab -- a reload, a restored
 *  tab, a pasted link -- to the draft the autosave wrote under it.
 *
 *  ⚠️ NOT findDraftFor, deliberately, and it differs from it in exactly the
 *  two places where a wrong answer here would be SENT somewhere:
 *
 *    * A failed read is "error", never "none". findDraftFor folds both into
 *      undefined, which is right for the restore it serves (no draft -> start
 *      empty) and wrong here: "could not look" must not be reported as
 *      "nothing is saved".
 *    * More than one match is "ambiguous", never the newest. Restore prefers
 *      the newest to keep working; this answer decides which record a person
 *      is taken to, and a guess there opens the wrong quote with confidence.
 *
 *  ⚠️ THE SCOPING IS THE SERVER'S. `?drafts=1` is resolved from the SESSION
 *  to `is_draft = True AND user_id = <signed-in user>` (test_quote_drafts's
 *  TestDraftListIsPerUser), and there is no parameter that widens it. So
 *  another user's draft can never be a candidate, even one carrying the same
 *  reference. Do not add a user argument here.
 *
 *  An exact name match only. The four-letter tag is what makes a working
 *  reference unique per quote; a prefix match would drop it. */
export async function resolveWorkingReference(reference: string): Promise<WorkingReferenceLookup> {
  const call = await backendGet(`${ENDPOINTS.quotes}?drafts=1&limit=${REFERENCE_LOOKUP_LIMIT}`);
  if (!call.ok) {
    return { status: "error", error: call.error ?? "The draft list could not be read." };
  }
  if (!Array.isArray(call.body?.quotes)) {
    return { status: "error", error: "The draft list came back in an unexpected shape." };
  }
  const rows = call.body.quotes as Array<{ quote_id?: unknown; name?: unknown; is_draft?: unknown }>;
  const matches = rows.filter((q) =>
    q?.name === reference
    && typeof q.quote_id === "number" && Number.isInteger(q.quote_id) && q.quote_id > 0
    // Every row from this domain is a draft. A row saying otherwise is a stale
    // or confused backend, and a finalised quote is not what was left behind.
    && q.is_draft !== false,
  );
  if (matches.length === 1) return { status: "found", quoteId: matches[0].quote_id as number };
  if (matches.length > 1) return { status: "ambiguous", count: matches.length };
  return { status: "none", truncated: rows.length >= REFERENCE_LOOKUP_LIMIT };
}

// ---- Authentication ------------------------------------------------------
//
// Session-based, per user. These three do NOT go through backendRequest():
// that helper enforces the business endpoints' {ok, ...} envelope, and the
// auth routes answer a bare identity object instead. Wrapping them in it
// would mean pretending they share a contract they do not.
//
// The session cookie is HttpOnly, so this code never sees it and cannot
// store it. credentials:"include" is the entire mechanism.

export interface SessionUser {
  uid: number;
  login: string;
  name: string;
  // null when the user holds no Anixify group at all. Such a user is
  // refused by every endpoint, reads included — being signed in is not
  // itself permission.
  role: "admin" | "sales" | "viewer" | null;
}

export interface LoginResult {
  ok: boolean;
  user?: SessionUser;
  error?: string;
}

export const auth = {
  /** Exchange credentials for a session. 401 means bad credentials. */
  async login(login: string, password: string): Promise<LoginResult> {
    try {
      const res = await fetch(`${API_BASE}${AUTH_ENDPOINTS.login}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ login, password }),
      });

      if (res.status === 401) {
        // The backend answers identically for an unknown login and a wrong
        // password, deliberately. Saying which would confirm whether an
        // account exists, so the UI must not invent a distinction either.
        return { ok: false, error: "Incorrect login or password." };
      }
      if (!res.ok) {
        return { ok: false, error: `Sign-in failed (HTTP ${res.status}).` };
      }
      return { ok: true, user: (await res.json()) as SessionUser };
    } catch (err) {
      return {
        ok: false,
        error: `Could not reach the server at ${API_BASE}. ${
          err instanceof Error ? err.message : String(err)
        }`,
      };
    }
  },

  /** The current session's user, or null when there is no valid session.
   *
   * Anonymous requests are answered with a JSON 401 (they used to be a 303
   * to Odoo's login page; changed 2026-10-06, review item P3-5). Anything that
   * is not a 200 is "not signed in". `redirect: "manual"` stays as a guard: a
   * redirect from an older backend still must not be followed into an HTML
   * page that would then fail to parse as JSON.
   */
  async whoami(): Promise<SessionUser | null> {
    try {
      const res = await fetch(`${API_BASE}${AUTH_ENDPOINTS.whoami}`, {
        credentials: "include",
        redirect: "manual",
      });
      if (!res.ok) return null;
      const body = await res.json();
      return body && typeof body.uid === "number" ? (body as SessionUser) : null;
    } catch {
      // Unreachable backend is indistinguishable from no session here, and
      // both mean the same thing to the caller: show the sign-in screen.
      return null;
    }
  },

  /** Destroy the session. Best-effort: the UI returns to sign-in either way. */
  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE}${AUTH_ENDPOINTS.logout}`, {
        method: "POST",
        credentials: "include",
      });
    } catch {
      /* the local session state is cleared by the caller regardless */
    }
  },
};

// ---- Saving a quote to the backend -------------------------------------
//
// The second real network call. POSTs to /anixify/api/save_quote, which
// creates a real anixify.quote and its lines in Odoo. The backend prices
// them itself, through the same validated engine, and answers with the
// values read back off the saved records.
//
// SEND ONLY WHAT THE BACKEND ACCEPTS. Lines carry six input fields and
// nothing else. The placeholder fields this app computes locally —
// freight, duty, landedCost, sellPrice, lineTotal, marginPct — are
// deliberately NOT sent: they are pricing OUTPUTS from a path the backend
// does not trust, and the endpoint ignores unknown keys by allowlist
// precisely so a caller can't hand-write a price that then looks
// validated. Do not "helpfully" widen this payload.
//
// SAME DISCIPLINE AS PRICING. Same 15s abort, same never-silent-fallback
// rule: on any failure this returns an error and no quote id. It must
// never report a save that did not happen, and never invent an id — the
// id's presence is what tells the rest of the app a real record exists.

export interface BackendSaveLineInput extends BackendPriceLineInput {
  description: string;
  // Descriptive, none of them engine inputs -- see materialToBackendSaveLine.
  manufacturer?: string;
  uom?: string;
  // The catalog row this line was picked from. Descriptive: it lands in the
  // backend's _LINE_DESCRIPTIVE_FIELDS, not the pricing allowlist.
  product_id?: number;
  origin?: string;
  weight?: number;
  volume?: number;
  remark?: string;
  lead_time?: string;
  price_status?: "priced" | "restricted";
  price_status_reason?: string;
  // THE LIST/DISCOUNT SPLIT. Descriptive, like every field above it: they
  // land in the backend's _LINE_DESCRIPTIVE_FIELDS, not the pricing
  // allowlist, because unit_price is and stays the engine's only cost input.
  // The backend records them and deliberately does not audit the arithmetic.
  //
  // ⚠️ NOT ON BackendPriceLineInput. price_lines must never see these -- see
  // materialToBackendLine, which is unchanged and emits exactly the engine's
  // inputs. discount_recorded is absent from BOTH: the server derives it from
  // whether these two keys arrived, and refuses a caller that sends it.
  list_price?: number;
  discount_pct?: number;
  // WHERE THE PERCENTAGE CAME FROM. Descriptive, like the split above, and
  // absent from BackendPriceLineInput for the same reason: price_lines emits
  // exactly the engine's inputs and must never see these.
  //
  // ⚠️ discount_rule_id IS A NUMBER ON THE WIRE and a string on Material.
  // The app keys every id as a string; anixify.discount.rule's is an integer
  // column and a Many2one write takes an int. materialToBackendSaveLine is
  // the one place that converts.
  discount_source?: "rule" | "manual" | "none" | "oem_quote";
  discount_rule_id?: number;
  // Only with discount_source "oem_quote" (oem-quote-register).
  oem_quote_line_id?: number;
}

// Import-path equivalent. Extends the import input for the same reason the
// domestic one extends the domestic input: description is the only field the
// save endpoint takes beyond the pricing inputs, so the two payloads can't
// drift on how a Material becomes engine input.
//
// ⚠️ freight_pct is OPTIONAL here and required on the price-line type it is
// built from, and that difference is the whole point. Stateless pricing always
// states a freight percentage, because nothing else can supply one. A SAVE
// must omit it on a line assigned to a shipment: that line's freight is the
// shipment's bill divided across everything on it, the engine owns the
// division, and the backend REFUSES a caller-set percentage there rather than
// overwriting it silently. `Omit` is what lets the field be absent at all.
// ⚠️ conversion_rate is OPTIONAL here for a DIFFERENT reason from freight_pct
// above, and the two must not be collapsed into one explanation. freight_pct
// is omitted because the allocator owns it on an assigned line. conversion_rate
// is omitted because ABSENCE IS A REQUEST: the backend's _should_resolve_fx
// gates on the KEY being present, so sending one -- even a 0 -- reads as "the
// caller has stated the rate" and suppresses the FX schedule lookup entirely.
// A line that never had a rate was therefore saved with 0, which multiplies the
// whole import chain by zero.
//
// A stated 0 is still sendable, deliberately. Omission means "we do not know",
// not "it is falsy" -- see materialToBackendSaveLine.
export interface BackendImportSaveLineInput
  extends Omit<BackendImportPriceLineInput, "freight_pct" | "conversion_rate"> {
  freight_pct?: number;
  conversion_rate?: number;
  // The QuoteShipment.key this line travels on, never a database id. Present
  // only on an assigned line, and never together with freight_pct.
  shipment_key?: string;
  description: string;
  manufacturer?: string;
  uom?: string;
  origin?: string;
  weight?: number;
  volume?: number;
  remark?: string;
  lead_time?: string;
  price_status?: "priced" | "restricted";
  price_status_reason?: string;
}

// The wire shape of one shipment in a save payload. Mirrors the backend's
// allowlist exactly: the booking and the bill, never the split.
export interface BackendShipmentInput {
  key: string;
  // Present -> update that record in place. Absent -> create a new one. Only
  // ever an id the backend itself returned.
  id?: number;
  name?: string;
  origin?: string;
  mode?: "sea" | "air" | "road";
  freight_total?: number;
  currency?: string;
  allocation_basis?: string;
  gross_weight?: number;
  volume?: number;
  container_count?: number;
  trip_count?: number;
  rate?: number;
  rate_basis?: string;
  min_charge?: number;
  notes?: string;
}

// A line read back off a saved record. Same two-path story as
// BackendPricedLine: cost_with_sv is domestic-only, the import chain below is
// import-only, so both sides are optional and the caller reads whichever the
// line's mode produced.
export interface BackendSavedLine extends BackendSaveLineInput {
  id: number;
  // Serialized as null when unset, so these are nullable here rather than
  // merely optional.
  manufacturer?: string | null;
  uom?: string | null;
  remark?: string | null;
  lead_time?: string | null;
  price_status?: "priced" | "restricted" | null;
  price_status_reason?: string | null;
  // The catalog link. Optional on the SAVE input above, but the serializer
  // returns it on every line and sends null on a free-text one, so it is
  // nullable on the way back -- same correction as the fields above it.
  product_id?: number | null;
  // Descriptive, returned by the serializer on every line. origin is nullable
  // (`line.origin or None`); weight and volume are RAW floats, so an
  // unrecorded figure arrives as 0.0 rather than null.
  origin?: string | null;
  weight?: number;
  volume?: number;
  // WHICH SHIPMENT CARRIED THIS LINE -- a real database id, or null for the
  // ordinary unassigned case. It matches an `id` in the quote-level
  // `shipments` list; the client key this app sent is NOT stored, so the id
  // is the only handle a reopen gets. resolveLineShipmentKey turns it back
  // into a key.
  //
  // On an assigned import line the `freight_pct` below is the ALLOCATOR'S
  // answer, not anything this app sent -- the backend refuses a caller-set
  // one on such a line.
  shipment_id?: number | null;
  // THE LIST/DISCOUNT SPLIT AS RECORDED, or nulls when nobody recorded one.
  //
  // ⚠️ THE FLAG IS THE FACT, NOT THE NUMBERS. The backend gates the pair on
  // discount_recorded precisely so a never-recorded line answers null rather
  // than 0 -- an Odoo Float reads a NULL back as 0.0, and serializing that
  // bare would tell every line saved before addon 17.0.1.17.0 that it was
  // sold at 0% off. A RECORDED zero comes back as a real 0.0.
  //
  // Absent entirely on a response from a backend older than that addon
  // version, which reads the same as false: not recorded.
  discount_recorded?: boolean;
  list_price?: number | null;
  discount_pct?: number | null;
  // WHERE THAT PERCENTAGE CAME FROM, or null when nobody recorded a source.
  //
  // ⚠️ null IS "UNKNOWN", NOT "none". Every line written before addon
  // 17.0.1.22.0 has a NULL column -- the 5 live drafts among them -- and the
  // serializer sends it through as null rather than collapsing it to "none",
  // which is a positive claim that the resolver ran and produced nothing.
  // Absent entirely on a response from an older backend, which reads the same.
  discount_source?: "rule" | "manual" | "none" | "oem_quote" | null;
  // The OEM quote line the net cost came from (oem-quote-register), with its
  // warnings while the quote is a draft. null when the line has none.
  oem_quote?: OemQuoteLink | null;
  // WHERE THE CONVERSION RATE CAME FROM (fx-rate-source-marker): null on a
  // line saved before the marker existed, and on every domestic line.
  rate_source?: "schedule" | "stated" | null;
  // The rule joined server-side, so an ARCHIVED rule still names itself on a
  // reopened line. `active` is carried so the label can say so if it ever
  // needs to; the pairing is what the line displays today.
  discount_rule?: {
    id: number;
    partner_name?: string | null;
    brand_name?: string | null;
    active?: boolean;
  } | null;
  // Domestic only.
  cost_with_sv?: number;
  variance_pct_snapshot: number;
  gp_pct_snapshot: number;
  unit_sp: number;
  extended_sp: number;
  // Import only — echoed inputs, then the computed chain.
  freight_pct?: number;
  conversion_rate?: number;
  duty_pct?: number;
  freight_value?: number;
  freight_plus_unit?: number;
  value_in_inr?: number;
  landing_cost?: number;
  landing_plus_variance?: number;
  total_landing_cost?: number;
  cf?: number;
}

export interface SaveQuoteResult {
  ok: boolean;
  quoteId?: number;
  name?: string;
  grandTotal?: number;
  lines?: BackendSavedLine[];
  // THE SHIPMENTS AS SAVED, each carrying the id the backend issued.
  //
  // Not decoration: a shipment the panel created has no id, and the backend
  // matches an update on the id alone. Re-sending an id-less entry makes the
  // next save CREATE a second record and delete the first -- the id churns on
  // every autosave tick. Adopting these ids is what makes the second save an
  // update of the first. See adoptSavedShipments.
  shipments?: BackendShipment[];
  // THE QUOTE'S TOKEN AS THIS SAVE LEFT IT, to echo on the next one. Adopted
  // on exactly the same terms as `shipments` above and for a related reason:
  // both are server-issued handles that only mean anything if the NEXT
  // request carries them back. See the adoption site in QuoteDetail.
  saveToken?: number;
  // Someone else wrote this quote since this copy was read; the backend
  // refused rather than overwriting them. Its own flag, never a string
  // match — see BackendCallResult.quoteStale.
  quoteStale?: boolean;
  error?: string;
  // WHO FINALISED THIS QUOTE AND WHEN, as the backend recorded them from the
  // session. null on a draft and on a quote finalised before this was
  // recorded -- unknown, shown as unknown.
  finalisedBy?: string | null;
  finalisedByUid?: number | null;
  finalisedAt?: string | null;
  // Who last edited it AFTER it was finalised, and when (TASK 6). null when
  // never, or not since this was recorded.
  finalEditedBy?: string | null;
  finalEditedAt?: string | null;
  // The customer as issued, when this save finalised the quote.
  customerSnapshot?: CustomerSnapshot;
  // quote-won-lost: the outcome as the save left it (Won when a quote holding
  // a PO was just finalised).
  outcome?: QuoteOutcome;
  // margin-approval: the finalise was refused because the quote's GP is
  // below the approval limit. Both figures are the backend's.
  approvalRequired?: { quoteGpPct: number; thresholdPct: number };
  // margin-approval: the quote is awaiting approval, so it is locked.
  awaitingApproval?: boolean;
}

// ---- Reading saved quotes back -----------------------------------------
//
// Two read-only calls against the backend's read endpoints. They create
// nothing and price nothing: every number they return was computed by the
// engine when the quote was saved and has been in the database since.
// Opening a saved quote must never silently re-save or re-price it.

/** One row of the currency picker. `code` is the ISO code the backend
 *  accepts and returns; `symbol` is carried for a later rendering slice and
 *  is not used to format money today. */
export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

export interface SavedQuoteSummary {
  quote_id: number;
  name: string;
  partner_name: string | null;
  // project-id: the customer's project reference and name, when stored.
  project_ref?: string | null;
  project_name?: string | null;
  date: string | null;
  state: string;
  line_count: number;
  grand_total: number;
  // ⚠️ WHETHER THIS ROW IS SOMEBODY'S UNFINISHED WORKING SET.
  //
  // The backend has always sent it -- _serialize_quote_summary carries it for
  // BOTH branches of the list route -- and this app simply discarded it while
  // there was only one kind of row on the screen. The list now merges drafts
  // in, so this flag is what marks them, and it is read from the ROW rather
  // than inferred from which call produced it: one fact, in the data, that a
  // later refactor cannot get out of step with.
  //
  // Optional because a response from a backend predating this key would omit
  // it, and absent reads as "not a draft" -- which is exactly what the
  // unfiltered list has always contained.
  is_draft?: boolean;
  // margin-approval: "pending" marks a quote awaiting an Admin's approval.
  approval_state?: "none" | "pending" | "approved" | "rejected";
  // quote-won-lost: open / won / lost / expired on a finalised quote; null on
  // a draft. Absent from a backend predating it.
  outcome_status?: "open" | "won" | "lost" | "expired" | "superseded" | null;
  // Order Management O2: the ONE derived status and its badges. Null where the
  // caller may not see this quote's OEM confirmations; absent from an older
  // backend -- both mean "fall back to outcome_status".
  oem?: QuoteOemListStatus | null;
  // Whose draft this is. Sent ONLY by the Admin team-drafts read; absent on
  // every other row, where the owner is either the caller or not a question.
  owner_uid?: number | null;
  owner_name?: string | null;
  // Which path the quote was priced on. Quote-level, per the read-back
  // envelope. Optional: a record saved before the import path existed carries
  // no mode, and inventing "domestic" for it here would be this layer
  // deciding a pricing fact it did not read.
  pricing_mode?: "domestic" | "import";
  // ISO code, or absent when not recorded. Not a pricing input.
  supplier_currency?: string | null;
}

export interface SavedQuoteLine extends BackendSavedLine {
  extd_price: number;
}

// A shipment as the backend serializes it — the inputs a caller stated, plus
// the id it issued and whatever the model resolved (a `freight_total` computed
// from a rate card comes back here as a plain total).
//
// NO ALLOCATION OUTPUT. The split lives on the lines, as each one's
// `freight_pct`; repeating a per-line answer at shipment level would be a
// second copy of a number that only means anything per line.
export interface BackendShipment {
  id: number;
  name?: string | null;
  origin?: string | null;
  mode?: "sea" | "air" | "road" | null;
  gross_weight?: number;
  volume?: number;
  container_count?: number;
  trip_count?: number;
  freight_total?: number;
  rate_basis?: string | null;
  rate?: number;
  min_charge?: number;
  // ISO code, like the quote's own supplier_currency — not an id, which would
  // mean something different in every database.
  currency?: string | null;
  allocation_basis?: string | null;
  notes?: string | null;
}

/** customer_snapshot on the wire: snake_case, exactly as the backend sends. */
export interface WireCustomerSnapshot {
  bill_to: CustomerSnapshotBlock | null;
  ship_to: CustomerSnapshotBlock | null;
  at: string;
}

/** Wire -> app shape. Absent, null or a snapshot with neither block reads as
 *  NO snapshot, so the document falls back to current details -- never to a
 *  blank "Bill to". */
export function toCustomerSnapshot(
  raw: WireCustomerSnapshot | null | undefined,
): CustomerSnapshot | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const billTo = raw.bill_to && typeof raw.bill_to === "object" ? raw.bill_to : null;
  const shipTo = raw.ship_to && typeof raw.ship_to === "object" ? raw.ship_to : null;
  if (!billTo && !shipTo) return undefined;
  return { billTo, shipTo, at: String(raw.at ?? "") };
}

export interface SavedQuoteDetail {
  quote_id: number;
  name: string;
  // Working state saved so it survives a reload, as opposed to a quote the
  // user deliberately pressed Save on.
  is_draft?: boolean;
  // Who finalised it and when -- null on a draft, and on a quote finalised
  // before the backend recorded it. Read off the recorded actor server-side.
  finalised_by?: string | null;
  finalised_by_uid?: number | null;
  finalised_at?: string | null;
  // The customer as issued (customer-snapshot-at-finalise). null on a draft
  // and on a quote finalised before 17.0.1.40.0.
  customer_snapshot?: WireCustomerSnapshot | null;
  // Who last edited it after it was finalised (TASK 6); null when never.
  final_edited_by?: string | null;
  final_edited_by_uid?: number | null;
  final_edited_at?: string | null;
  // Whose quote this is. null on anything saved before ownership existed.
  // Used ONLY to decide what to render -- the backend refuses the update
  // regardless, and is the actual boundary.
  owner_uid?: number | null;
  // THE CONCURRENCY TOKEN AS STORED. Carried on the reopen read and not only
  // on a save response, because reopening a saved quote is the PRIMARY
  // multi-tab case -- two people open /saved-quotes/:id and edit. Without it
  // here, a reopened quote's first save would carry no token, land in the
  // backend's absent-is-allowed branch, and the guard would never engage on
  // the very case it exists for.
  //
  // Optional because a quote read from a backend that predates the column
  // legitimately has none. 0 is a real stored value, not an absence.
  save_token?: number;
  partner_name: string | null;
  date: string | null;
  state: string;
  // What the quote's pricing config says TODAY. The per-line snapshots are
  // what each line was actually priced with; prefer those.
  variance_pct: number | null;
  gp_pct: number | null;
  grand_total: number;
  // Quote-level pricing path, same optionality as on the summary above.
  pricing_mode?: "domestic" | "import";
  // ISO code, or absent when not recorded. Not a pricing input.
  supplier_currency?: string | null;
  // The SidePanel's FX and negotiation-buffer settings, as they were saved.
  // Opaque on the wire and validated by savedQuoteToPricingContext before
  // anything reads it -- the backend stores it without inspecting it, so this
  // app cannot assume it is well-formed. null when nothing was ever stored.
  pricing_context?: unknown;
  // THE REAL SHIPMENT RECORDS on this quote. Absent on every quote saved
  // before BF5a and empty on every domestic one, so a client that ignores it
  // reads exactly what it always read.
  //
  // Not to be confused with the `shipments` array still sitting inside the
  // stored `pricing_context` of any quote saved before BF5d-3: that was the
  // display blob, read by nothing on the backend and by nothing here any
  // more. These are database records that actually price the lines.
  shipments?: BackendShipment[];
  lines: SavedQuoteLine[];
}

export interface ListSavedQuotesResult {
  ok: boolean;
  quotes?: SavedQuoteSummary[];
  error?: string;
}

export interface GetSavedQuoteResult {
  ok: boolean;
  quote?: SavedQuoteDetail;
  error?: string;
  // Distinguishes "this id no longer exists" from a real failure. A caller
  // working from a stale list deserves a different message than one whose
  // backend is down or whose key was rejected.
  notFound?: boolean;
}

// Turns a saved quote's lines into the Material rows the table renders.
//
// A saved quote carries the line inputs, the engine's outputs, and the
// descriptive fields this app actually sends. UoM, origin, weight and volume
// ARE persisted and are restored from the response. What is still absent is
// genuinely absent — spec was never sent, and no list price or discount was
// either — and those are left empty rather than invented; a reopened quote
// should show what was actually saved.
//
// Two consequences worth knowing:
//   - listPrice equals unit_price with a 0% discount, because unit_price IS
//     what this app sent as netPrice. Reconstructing a list/discount split
//     that was never stored would be fabrication.
//   - category is "accessory" as a neutral placeholder. It is display-only
//     here, and it is the one value that produces no spurious brand-lock
//     warning: services/pricing.ts's placeholder check only flags
//     copper and fiber.
/** A stored conversion rate, as the quote screen should hold it.
 *
 *  0 (or absent) becomes **undefined** -- "no rate" -- so it is never re-sent
 *  as a stated rate that overrides the FX schedule. Any real rate is passed
 *  through untouched.
 *
 *  ⚠️ IT IS ONLY EVER APPLIED ON THE WAY IN. The send path keeps its own rule
 *  (`!== undefined`), so a rate a person types -- including one the schedule
 *  disagrees with, which is the forward-contract escape hatch -- still
 *  reaches the backend as a statement. */
export function zeroRateMeansNoRate(
  stored: number | null | undefined,
): number | undefined {
  return typeof stored === "number" && stored !== 0 ? stored : undefined;
}

/** WHETHER A SAVED LINE'S DUTY IS A STATEMENT someone made, or the 0 the
 *  frontend used to send for a duty nobody had entered (missing-duty-not-zero,
 *  2026-09-30). The database has one Float and no marker, so the two cannot be
 *  told apart from the value alone; the rule is the safe one:
 *
 *    - non-zero                    -> stated (a person, or the schedule's row)
 *    - 0, on a DRAFT, with an HSN  -> NOT stated: ask the schedule again, and
 *                                     refuse if it has no row. Retype 0 to
 *                                     state "exempt" on purpose.
 *    - 0, no HSN                   -> stated as before: there is nothing to
 *                                     look up, and the line has always priced
 *    - a FINALISED quote           -> read back exactly; it is a record.
 */
export function dutyIsStated(
  line: { duty_pct?: number | null; hsn_code?: string | null; duty_source?: string | null },
  isDraft: boolean | undefined,
): boolean {
  if (typeof line.duty_pct !== "number") return false;
  // line-sources (2026-09-30): a recorded source is the answer. "stated" is a
  // typed value (0 stays 0); "schedule" is shown with its marker.
  if (line.duty_source === "stated" || line.duty_source === "schedule") return true;
  if (line.duty_pct !== 0) return true;
  // LEGACY (no source): a finalised quote is a record, read exactly; on a
  // draft a stored 0 is the old default, with or without an HSN.
  return isDraft === false;
}

/** line-sources: whether a saved line's freight is KNOWN (typed, or a
 *  shipment's share) rather than the old default 0. Same shape as
 *  dutyIsStated. A legacy line (no source) counts when finalised, non-zero,
 *  or assigned to a shipment; a legacy 0 on an unassigned draft is unset. */
export function freightIsKnown(
  line: { freight_pct?: number | null; freight_source?: string | null; shipment_id?: number | null },
  isDraft: boolean | undefined,
): boolean {
  if (typeof line.freight_pct !== "number") return false;
  if (line.freight_source === "stated" || line.freight_source === "shipment") return true;
  if (line.freight_pct !== 0) return true;
  if (line.shipment_id !== undefined && line.shipment_id !== null) return true;
  return isDraft === false;
}

export function savedQuoteToMaterials(
  detail: SavedQuoteDetail,
  // The quote's shipments, already turned into panel objects by
  // detailToQuoteShipments. Passed in rather than derived here so the caller
  // seeds the panel and the lines from ONE list -- deriving it twice would
  // let a line reference a key the panel never minted.
  //
  // Optional: a domestic quote, or one saved before shipments existed, has
  // none, and every line then reads as unassigned.
  shipments: QuoteShipment[] = [],
): Material[] {
  return detail.lines.map((line, index) => ({
    lineNo: index + 1,
    partNo: line.part_number ?? "",
    description: line.description ?? "",
    manufacturer: line.manufacturer ?? undefined,
    remark: line.remark ?? undefined,
    leadTime: line.lead_time ?? undefined,
    priceStatus: line.price_status ?? undefined,
    priceStatusReason: line.price_status_reason ?? undefined,
    // THE CATALOG LINK, restored. It was dropped here while the backend was
    // already serializing it, so every reopened line lost the link and a
    // re-save wrote it back as free text.
    //
    // ⚠️ `?? undefined`, NEVER `line.product_id` bare and never `?? 0`. The
    // wire sends null on a free-text line (`line.product_id.id or None`), and
    // addMaterialsToQuote's dedupe discriminates on `m.productId !== undefined`
    // -- so a null would take the ID branch and compare `null === product.id`,
    // which is false for every product, and the row would never match at all.
    // A genuinely link-less line must keep the partNo fallback instead.
    // strictNullChecks is off in this project, so TypeScript would NOT catch
    // the null: it has to be coalesced here, deliberately.
    productId: line.product_id ?? undefined,
    spec: "",
    oem: line.oem ?? "",
    qty: line.qty,
    // Was hardcoded "" because the backend had nowhere to keep it, so every
    // reopened quote printed a blank UoM column. It is stored now.
    uom: line.uom ?? "",
    // ⚠️ THE SPLIT, IN THREE STATES -- AND NEVER-RECORDED IS NOT 0%.
    //
    // This used to read `listPrice: line.unit_price, discountPct: 0`, and
    // that was not laziness: it was LOAD-BEARING. recomputeAll derives
    // netPrice from listPrice and discountPct, so fabricating a 0% split off
    // unit_price was what made it reproduce unit_price on reopen. The cost
    // was that a quote issued at 40% off reopened claiming 0% -- and the
    // Disc % cell is editable, so re-typing the 40 discounted it twice.
    //
    // The backend records the split since addon 17.0.1.17.0, so the three
    // states are now readable and are mapped straight through:
    //
    //   recorded, discounted  listPrice 11880, discountPct 40
    //   recorded, no discount listPrice  7128, discountPct  0  <- a real claim
    //   never recorded        both undefined                   <- not 0%
    //
    // recomputeAll gained a not-derive branch for the third case, so
    // netPrice below still stands. Read that branch before changing this.
    listPrice: line.discount_recorded ? line.list_price ?? undefined : undefined,
    discountPct: line.discount_recorded ? line.discount_pct ?? undefined : undefined,
    discountRecorded: !!line.discount_recorded,
    // WHERE THAT PERCENTAGE CAME FROM, READ BACK -- NEVER RE-DERIVED.
    //
    // ⚠️ `?? undefined`, AND NOTHING ELSE. A null or absent source stays
    // undefined, which every consumer renders as nothing. Mapping it to
    // "none" here would make the 5 live drafts -- and every line saved
    // before addon 17.0.1.22.0 -- assert that the resolver ran and found no
    // discount, which nobody ever decided about them. This is the same
    // absent-is-not-zero rule the split two lines up follows, applied to a
    // Selection instead of a Float.
    //
    // ⚠️ AND IT IS NOT RE-RESOLVED. Running the resolver over a reopened
    // line would answer with TODAY's rules: a rule archived since the save
    // would flip a rule-sourced line to "none", and an edited percentage
    // would leave the source pointing at a rate the line was never priced
    // at. The stored answer is the answer.
    discountSource: line.discount_source ?? undefined,
    oemQuote: line.oem_quote ?? undefined,
    discountRuleId:
      line.discount_rule ? String(line.discount_rule.id) : undefined,
    discountRulePairing: line.discount_rule
      ? [line.discount_rule.partner_name, line.discount_rule.brand_name]
          .map((n) => n || "?")
          .join(" / ")
      : undefined,
    // UNCHANGED, AND IT IS THE LOAD-BEARING VALUE. unit_price is what the
    // engine priced from and what pricingSignatureOf fingerprints, so it must
    // arrive on the row exactly as stored whatever the split says.
    netPrice: line.unit_price,
    // The import path's per-line inputs, restored so a reopened import quote
    // can be re-priced or edited from what it was actually saved with rather
    // than from zeros. Undefined on a domestic line, because the backend does
    // not serialize these there at all — which leaves a domestic Material
    // byte-for-byte what it was before.
    // On an ASSIGNED line this is the allocator's answer, not anything this
    // app sent -- the backend refuses a caller-set percentage there. It comes
    // back as an ordinary number and is displayed as one; making it look
    // different from a typed value is BF5c-2b-ii's job, not this slice's.
    // line-sources (2026-09-30): a stored 0 with no recorded source on an
    // unassigned draft line is the old default, not a typed 0 -- read back
    // UNSET so the save refuses it until somebody states a freight or assigns
    // a shipment. A typed 0 ("stated") stays 0.
    freightPct: freightIsKnown(line as never, detail.is_draft) ? line.freight_pct : undefined,
    // ⚠️ A STORED RATE OF 0 MEANS "NO RATE", AND READING IT AS A TYPED 0 IS
    // THE CURRENCY TRAP ON DRAFTS 550 AND 551.
    //
    // The send path deliberately distinguishes an ABSENT rate from a TYPED
    // one: `conversionRate !== undefined` sends the number, because a rate
    // somebody stated is a statement the backend honours OVER its own FX
    // schedule. That rule is right and is not what is being changed.
    //
    // What was wrong is upstream of it. A line saved with no rate stores 0,
    // so reopening handed that 0 back as though a person had typed it -- and
    // the next save re-sent it as a STATED rate, overriding the schedule with
    // zero. The quote stayed at Rs 0 and the finalise stayed refused, and
    // naming a currency could not fix it because the cell locks (see
    // MaterialTable.buildCols). Two drafts on live are stuck in exactly that
    // state.
    //
    // ⚠️ AND A GENUINE "TYPED 0" IS NOT A THING BEING LOST HERE. Zero is not
    // a commercial statement about an exchange rate -- the engine divides by
    // it and the line can only come out at 0. Owner decision, 2026-09-22: a
    // stored 0 means no rate.
    conversionRate: zeroRateMeansNoRate(line.conversion_rate),
    // The marker as stored. null (legacy) stays undefined, so the line keeps
    // today's behaviour -- no backfill on the way in either.
    rateSource: line.rate_source === "schedule" || line.rate_source === "stated"
      ? line.rate_source
      : undefined,
    // ⚠️ A STORED 0 ON A DRAFT LINE WITH AN HSN IS NOT A STATEMENT
    // (missing-duty-not-zero, 2026-09-30). Until now the frontend sent an
    // unset duty as 0, which the server stored as though somebody had chosen
    // "duty-free"; the stored value cannot tell those lines from a typed 0
    // (no marker column -- that would be a schema change). Read back as
    // UNSET, the line asks the schedule again and refuses if there is no row.
    // A person who really means 0% retypes it (or the schedule row says 0).
    // A non-zero duty is always a statement. A FINALISED quote is a record of
    // what was priced and is read back exactly.
    dutyPct: dutyIsStated(line as never, detail.is_draft) ? line.duty_pct : undefined,
    dutySource: (line as { duty_source?: string | null }).duty_source === "schedule" ? "schedule"
      : (line as { duty_source?: string | null }).duty_source === "stated" ? "stated" : undefined,
    hsnCode: (line as { hsn_code?: string | null }).hsn_code ?? undefined,
    // WHICH SHIPMENT THIS LINE TRAVELS ON, back from the id the backend
    // stored. undefined when unassigned, or when the id names a shipment this
    // response did not include -- resolveLineShipmentKey reads that as
    // unassigned rather than inventing a key nothing can show.
    shipmentKey: resolveLineShipmentKey(line, shipments),
    // Was hardcoded "" for the same reason UoM was, and with a worse
    // consequence at the time: services/pricing.ts joined shipments to lines
    // on origin, so a blank one silently allocated zero freight and zero duty
    // to every reopened line. That join is gone (BF5d-3); the field is read
    // now only by SidePanel's Sourcing Split, and is still the line's own
    // fact rather than a blank.
    origin: line.origin ?? "",
    // The serializer returns these as RAW floats, not `or None`, so an
    // unrecorded figure arrives as 0.0 rather than null -- `?? 0` covers the
    // older rows that predate the fields entirely.
    weight: line.weight ?? 0,
    volume: line.volume ?? 0,
    landedCost: 0,
    marginPct: 0,
    sellPrice: 0,
    lineTotal: 0,
    category: "accessory" as MaterialCategory,
  }));
}

// A Quote header for the detail screen's top strip.
//
// value is the engine grand total, so a reopened quote shows what it was
// actually saved at. margin is 0 and status "Draft": neither is stored on
// an anixify.quote, and guessing would put an invented number next to real
// ones. Odoo's own state is shown on the Saved Quotes list instead.
// THE CUSTOMER DIRECTORY.
//
// Two reads behind the picker. They replace a free-text customer box, which
// was the name-collision bug itself: typing "Pinewell" matched whichever
// Pinewell the database found first, and a typo silently created a new
// company. A customer is now something you PICK, and a save sends an id.
//
// Both go through backendGet, so they inherit the timeout, the 401 handling
// and the never-silent-fallback discipline every other call has.

/** The currencies this deployment has adopted, for the quote header picker.
 *
 * Fetched rather than hardcoded so the list tracks the backend master:
 * activating a currency in Odoo makes it selectable with no frontend build.
 * Returns [] on failure rather than a guessed list -- an empty picker is a
 * visible problem, an invented one silently offers a currency the backend
 * would refuse.
 */
export async function listCurrencies(): Promise<CurrencyOption[]> {
  const call = await backendGet(ENDPOINTS.currencies);
  if (!call.ok || !Array.isArray(call.body?.currencies)) return [];
  return call.body.currencies.map((c: any) => ({
    code: c.code,
    name: c.name ?? c.code,
    symbol: c.symbol ?? "",
  }));
}

/** Search customer entities by name. An empty query returns the first page. */
export async function searchCustomers(
  query: string,
  limit = 20,
): Promise<{ ok: boolean; customers?: CustomerPartner[]; error?: string }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (query.trim()) params.set("q", query.trim());

  const call = await backendGet(`${ENDPOINTS.customers}?${params.toString()}`);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.customers)) {
    return {
      ok: false,
      error: "The customer list came back in a shape this app does not recognise.",
    };
  }
  return { ok: true, customers: call.body.customers };
}

/** One entity's addresses and contacts, grouped by role. */
export async function getCustomerAddresses(
  entityId: number,
): Promise<{
  ok: boolean;
  entity?: CustomerPartner;
  addresses?: {
    shipping: CustomerPartner[];
    invoice: CustomerPartner[];
    contact: CustomerPartner[];
    other: CustomerPartner[];
  };
  error?: string;
}> {
  const call = await backendGet(
    `${ENDPOINTS.customerAddresses}?entity_id=${encodeURIComponent(entityId)}`,
  );
  if (!call.ok) return { ok: false, error: call.error };
  if (!call.body?.addresses) {
    return { ok: false, error: call.body?.error ?? "No addresses came back." };
  }
  return { ok: true, entity: call.body.entity, addresses: call.body.addresses };
}

/** Create a customer entity and its addresses. Returns the created graph.
 *
 *  Creating a customer is an explicit request with an explicit result, not a
 *  side effect of saving a quote. That matters for more than tidiness: the
 *  quote autosave fires on a debounce, so an inline-create riding along with
 *  it could be sent twice before the first response landed and would make two
 *  companies with no id to tell them apart. */
export async function createCustomer(
  spec: Record<string, unknown>,
): Promise<{
  ok: boolean;
  entity?: CustomerPartner;
  shipping?: CustomerPartner | null;
  invoice?: CustomerPartner | null;
  contact?: CustomerPartner | null;
  // state-from-gstin: a State that disagrees with its GSTIN (amber; the
  // customer WAS created). Empty when none.
  gstinNotes?: string[];
  error?: string;
}> {
  const call = await backendPost(ENDPOINTS.createCustomer, { customer: spec });
  if (!call.ok) return { ok: false, error: call.error };
  if (!call.body?.ok || !call.body?.entity) {
    return { ok: false, error: call.body?.error ?? "The customer was not created." };
  }
  return {
    ok: true,
    entity: call.body.entity,
    shipping: call.body.shipping ?? null,
    invoice: call.body.invoice ?? null,
    contact: call.body.contact ?? null,
    gstinNotes: Array.isArray(call.body.gstin_notes) ? call.body.gstin_notes : [],
  };
}

// ---- Copy a quote to a new draft (copy-quote) -------------------------------

/** Copy a quote's INPUTS into a new draft the caller owns. The backend
 *  re-prices it through the ordinary save path; nothing is copied from engine
 *  outputs, and no quote number is consumed. */
export async function copyQuote(
  quoteId: number,
  name: string,
): Promise<{ ok: boolean; quoteId?: number; name?: string; forbidden?: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.copyQuote, { quote_id: quoteId, name });
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  if (typeof call.body?.quote_id !== "number") {
    return { ok: false, error: "The copy was not made: no quote id came back." };
  }
  return { ok: true, quoteId: call.body.quote_id, name: call.body.name };
}

// ---- Margin approval (margin-approval) ------------------------------------

export interface BackendApproval {
  state?: string | null;
  requested_by?: string | null;
  requested_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  rejection_reason?: string | null;
  quote_gp_pct?: number | null;
  threshold_pct?: number | null;
  below_limit?: boolean;
}

/** The backend's approval block, as the screen reads it. Figures are copied,
 *  never computed. undefined when the backend sent none. */
export function toQuoteApproval(raw: BackendApproval | null | undefined): QuoteApproval | undefined {
  if (!raw) return undefined;
  const state = raw.state === "pending" || raw.state === "approved" || raw.state === "rejected"
    ? raw.state : "none";
  return {
    state,
    requestedBy: raw.requested_by ?? null,
    requestedAt: raw.requested_at ?? null,
    approvedBy: raw.approved_by ?? null,
    approvedAt: raw.approved_at ?? null,
    rejectionReason: raw.rejection_reason ?? null,
    quoteGpPct: typeof raw.quote_gp_pct === "number" ? raw.quote_gp_pct : null,
    thresholdPct: typeof raw.threshold_pct === "number" ? raw.threshold_pct : null,
    belowLimit: !!raw.below_limit,
  };
}

export interface ApprovalActionResult {
  ok: boolean;
  approval?: QuoteApproval;
  saveToken?: number;
  forbidden?: boolean;
  quoteStale?: boolean;
  error?: string;
}

async function approvalAction(endpoint: string, body: Record<string, unknown>): Promise<ApprovalActionResult> {
  const call = await backendPost(endpoint, body);
  if (!call.ok) {
    return { ok: false, forbidden: call.forbidden, quoteStale: call.quoteStale, error: call.error };
  }
  return {
    ok: true,
    approval: toQuoteApproval(call.body?.approval),
    saveToken: typeof call.body?.save_token === "number" ? call.body.save_token : undefined,
  };
}

/** Sales: hold this below-limit draft for an Admin. The token is the one the
 *  screen last saved, so what is locked is what the requester saw. */
export function requestApproval(quoteId: number, saveToken: number | undefined): Promise<ApprovalActionResult> {
  return approvalAction(ENDPOINTS.requestApproval, { quote_id: quoteId, save_token: saveToken });
}

/** The requester (or an Admin): unlock a pending draft. */
export function withdrawApproval(quoteId: number): Promise<ApprovalActionResult> {
  return approvalAction(ENDPOINTS.withdrawApproval, { quote_id: quoteId });
}

/** Admin: finalise a pending quote through the normal finalise path. */
export function approveQuote(quoteId: number): Promise<ApprovalActionResult> {
  return approvalAction(ENDPOINTS.approveQuote, { quote_id: quoteId });
}

/** Admin: return a pending quote to draft. A reason is required. */
export function rejectQuote(quoteId: number, reason: string): Promise<ApprovalActionResult> {
  return approvalAction(ENDPOINTS.rejectQuote, { quote_id: quoteId, reason });
}

// ---- Quote outcome: Open / Won / Lost (quote-won-lost) ---------------------

export interface BackendOutcome {
  status?: string | null;
  superseded_by?: { id?: unknown; name?: unknown } | null;
  lost_reason?: string | null;
  lost_reason_label?: string | null;
  lost_note?: string | null;
  changed_by?: string | null;
  changed_at?: string | null;
}

/** The fixed list of reasons a quote is Lost. The backend refuses any other
 *  key; "other" needs a note. Labels as the backend names them. */
export const LOST_REASONS: { key: string; label: string }[] = [
  { key: "price", label: "Price" },
  { key: "lead_time", label: "Lead time" },
  { key: "competitor", label: "Lost to competitor" },
  { key: "cancelled", label: "Project cancelled" },
  { key: "no_response", label: "No response" },
  { key: "other", label: "Other" },
];

/** The backend's outcome block, as the screen reads it. undefined when the
 *  backend sent none. */
export function toQuoteOutcome(raw: BackendOutcome | null | undefined): QuoteOutcome | undefined {
  if (!raw) return undefined;
  const s = raw.status;
  const status = s === "open" || s === "won" || s === "lost" || s === "expired" || s === "superseded"
    ? s : null;
  return {
    status,
    supersededBy: toQuoteRef(raw.superseded_by),
    lostReason: raw.lost_reason ?? null,
    lostReasonLabel: raw.lost_reason_label ?? null,
    lostNote: raw.lost_note ?? null,
    changedBy: raw.changed_by ?? null,
    changedAt: raw.changed_at ?? null,
  };
}

export interface OutcomeActionResult {
  ok: boolean;
  outcome?: QuoteOutcome;
  forbidden?: boolean;
  error?: string;
}

async function outcomeAction(endpoint: string, body: Record<string, unknown>): Promise<OutcomeActionResult> {
  const call = await backendPost(endpoint, body);
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  return { ok: true, outcome: toQuoteOutcome(call.body?.outcome) };
}

/** M3.1 (owner B3): one line's customer-promised date. `date` null = not
 *  stated -- never defaulted, never today. */
export interface CustomerDateEntry { line_id: number; date: string | null }

/** Sales (own) / Admin (any): record Won by hand, e.g. a verbal order. The
 *  dialog's customer dates travel with it (optional); none = not stated. */
export function markQuoteWon(quoteId: number, customerDates: CustomerDateEntry[] = []): Promise<OutcomeActionResult> {
  return outcomeAction(ENDPOINTS.markQuoteWon, {
    quote_id: quoteId, ...(customerDates.length ? { customer_dates: customerDates } : {}),
  });
}

/** Sales (own) / Admin (any): record Lost. `reason` is a LOST_REASONS key;
 *  "other" needs a note. The backend is what enforces both. */
export function markQuoteLost(quoteId: number, reason: string, note: string): Promise<OutcomeActionResult> {
  return outcomeAction(ENDPOINTS.markQuoteLost, { quote_id: quoteId, reason, note });
}

/** Admin: a Won or Lost quote back to Open. */
export function resetQuoteStatus(quoteId: number): Promise<OutcomeActionResult> {
  return outcomeAction(ENDPOINTS.resetQuoteStatus, { quote_id: quoteId });
}

// ---- Quote revisions (quote-revisions) --------------------------------------

/** {id, name} off the wire, or null when either is missing. */
export function toQuoteRef(raw: { id?: unknown; name?: unknown } | null | undefined): QuoteRef | null {
  return raw && typeof raw.id === "number" && typeof raw.name === "string"
    ? { id: raw.id, name: raw.name } : null;
}

export interface BackendRevision {
  revision_of?: { id?: unknown; name?: unknown } | null;
  revision_no?: number | null;
  superseded_by?: { id?: unknown; name?: unknown } | null;
  open_revision_draft?: { id?: unknown; name?: unknown } | null;
}

export function toQuoteRevision(raw: BackendRevision | null | undefined): QuoteRevision | undefined {
  if (!raw) return undefined;
  return {
    revisionOf: toQuoteRef(raw.revision_of),
    revisionNo: typeof raw.revision_no === "number" ? raw.revision_no : 0,
    supersededBy: toQuoteRef(raw.superseded_by),
    openRevisionDraft: toQuoteRef(raw.open_revision_draft),
  };
}

/** Sales (own) / Admin: a revision DRAFT of a finalised Open/Expired quote.
 *  Finalising it names it <number>-R<n> and supersedes the original. The
 *  backend enforces every rule (one open draft, not Won/Lost/Superseded). */
export async function reviseQuote(quoteId: number, name: string): Promise<{
  ok: boolean; quoteId?: number; name?: string; forbidden?: boolean; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.reviseQuote, { quote_id: quoteId, name });
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  if (typeof call.body?.quote_id !== "number") {
    return { ok: false, error: "No revision was made: no quote id came back." };
  }
  return { ok: true, quoteId: call.body.quote_id, name: call.body.name };
}

// ---- GST split (gst-split) -----------------------------------------------------

export interface BackendTax {
  mode?: string;
  rates?: number[];
  lines?: Array<{ kind?: string; rate?: number; base?: number; amount?: number; label?: string }>;
  taxable_base?: number;
  total_tax?: number;
  grand_total?: number;
  note?: string | null;
  frozen?: boolean;
  legacy?: boolean;
  // warehouse-master: OUR GSTIN / State from the dispatch warehouse (frozen
  // with the block at finalise). Absent on legacy and pre-warehouse blocks.
  our_gstin?: string | null;
  our_state_name?: string | null;
  dispatch?: { id?: number; code?: string; label?: string; gstin?: string | null; state_name?: string | null } | null;
}

/** The server's tax block, copied. Nothing is computed here; a block missing
 *  its figures reads as undefined rather than as zeros. */
export function toQuoteTax(raw: BackendTax | null | undefined): QuoteTax | undefined {
  if (!raw || typeof raw.total_tax !== "number" || typeof raw.taxable_base !== "number"
      || typeof raw.grand_total !== "number" || !Array.isArray(raw.lines)) return undefined;
  const mode = raw.mode === "cgst_sgst" || raw.mode === "igst" ? raw.mode : "flat";
  return {
    mode,
    rates: Array.isArray(raw.rates) ? raw.rates : [],
    lines: raw.lines
      .filter((l) => typeof l.amount === "number" && typeof l.label === "string")
      .map((l) => ({
        kind: (l.kind === "cgst" || l.kind === "sgst" || l.kind === "igst" ? l.kind : "gst") as QuoteTaxLine["kind"],
        rate: l.rate ?? 0, base: l.base ?? 0, amount: l.amount as number, label: l.label as string,
      })),
    taxableBase: raw.taxable_base,
    totalTax: raw.total_tax,
    grandTotal: raw.grand_total,
    note: raw.note ?? null,
    frozen: !!raw.frozen,
    legacy: !!raw.legacy,
    ourGstin: raw.our_gstin ?? null,
    ourStateName: raw.our_state_name ?? null,
    dispatchLabel: raw.dispatch?.label ?? null,
  };
}

/** A saved quote's GST block, read from the server (Viewer and up). */
export async function getQuoteTax(quoteId: number): Promise<{ ok: boolean; tax?: QuoteTax; error?: string }> {
  const call = await backendPost(ENDPOINTS.quoteTax, { quote_id: quoteId });
  if (!call.ok) return { ok: false, error: call.error };
  const tax = toQuoteTax(call.body?.tax);
  return tax ? { ok: true, tax } : { ok: false, error: "The tax came back in an unexpected shape." };
}

// ---- Follow-up list (follow-up-list) ----------------------------------------

export interface FollowUpQuote extends SavedQuoteSummary {
  valid_until: string;
  /** Days until Valid Until; negative once it has passed. Server-computed. */
  days_left: number;
}

/** Finalised Open quotes due within 7 days or already past Valid Until,
 *  soonest first. Read only. The backend scopes it: Sales their own, Admin
 *  everyone's; a Viewer is refused. */
export async function getFollowUp(): Promise<{
  ok: boolean; quotes?: FollowUpQuote[]; forbidden?: boolean; error?: string;
}> {
  const call = await backendGet(ENDPOINTS.followUp);
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  if (!Array.isArray(call.body?.quotes)) {
    return { ok: false, error: "The follow-up list came back in an unexpected shape." };
  }
  return { ok: true, quotes: call.body.quotes as FollowUpQuote[] };
}

// ---- Settings > Users (read only, Admin only) ------------------------------

export interface AppUser {
  id: number;
  name: string;
  login: string;
  /** The highest Anixify role, or null when the user holds none. */
  role: "admin" | "sales" | "viewer" | null;
  active: boolean;
  /** The newest stored sign-in record, UTC "YYYY-MM-DD HH:MM:SS", or null. */
  last_sign_in: string | null;
  /** An Odoo system administrator: always an Anixify Admin, not demotable here. */
  system_admin?: boolean;
}

export type UserRole = "admin" | "sales" | "viewer";

type UserWrite = { ok: boolean; user?: AppUser; error?: string; forbidden?: boolean };

async function userWrite(endpoint: string, body: Record<string, unknown>): Promise<UserWrite> {
  const call = await backendPost(endpoint, body);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const user = call.body?.user as AppUser | undefined;
  if (!user || typeof user.id !== "number") {
    return { ok: false, error: "The change was not confirmed: no user came back." };
  }
  return { ok: true, user };
}

/** Add a user. ADMIN ONLY. The password goes to the server once and is never
 *  kept, logged or shown again by this app. */
export function createUser(input: { name: string; login: string; role: UserRole; password: string }): Promise<UserWrite> {
  return userWrite(ENDPOINTS.createUser, input);
}

/** Edit name, login or role. ADMIN ONLY. The server refuses a self role change
 *  and demoting the last active Admin. */
export function updateUser(id: number, changes: { name?: string; login?: string; role?: UserRole }): Promise<UserWrite> {
  return userWrite(ENDPOINTS.updateUser, { id, ...changes });
}

/** Set ANOTHER user's password. ADMIN ONLY. Their sessions end. The password
 *  is sent once and never kept, logged or shown by this app. */
export function resetUserPassword(id: number, password: string): Promise<UserWrite> {
  return userWrite(ENDPOINTS.resetUserPassword, { id, password });
}

/** Change YOUR OWN password. Every role. Your other sessions end; this one
 *  stays signed in. */
export async function changeMyPassword(current: string, next: string): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.changeMyPassword, { current, new: next });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true };
}

/** Deactivate or reactivate. ADMIN ONLY. Never deletes. */
export function setUserActive(id: number, active: boolean): Promise<UserWrite> {
  return userWrite(ENDPOINTS.setUserActive, { id, active });
}

/** Every internal user. Admin only -- the route refuses anyone else. */
export async function listUsers(): Promise<{ ok: boolean; users?: AppUser[]; forbidden?: boolean; error?: string }> {
  const call = await backendGet(ENDPOINTS.users);
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  if (!Array.isArray(call.body?.users)) {
    return { ok: false, error: "The user list came back in a shape this app does not recognise." };
  }
  return { ok: true, users: call.body.users };
}

// ---- The sales report (read only) -----------------------------------------
//
// ⚠️ EVERY FIGURE IS THE BACKEND'S. Counts, value and GP % arrive already
// aggregated from stored engine outputs (see anixify.sales.report); this
// layer only carries them and the page only formats them. Nothing here sums,
// weights or re-derives a number -- the "Pricing calculations" hard rule.

export interface SalesBucket {
  count: number;
  /** Sum of stored extended_sp over priced lines of priced quotes. */
  value: number;
  /** Quotes counted but kept out of value (and GP) because they cannot price. */
  unpriced: number;
  /** Finalised and its outcomes only; null when there is no priced value to weigh. */
  gp_pct?: number | null;
}

export interface SalesFigures {
  drafts: SalesBucket;
  finalised: SalesBucket;
  /** quote-won-lost: status = Won (it used to mean "has a customer PO"). */
  won: SalesBucket;
  // quote-won-lost: the rest of finalised. Optional only because a backend
  // predating the slice omits them; the page then shows none of them.
  lost?: SalesBucket;
  open?: SalesBucket;
  /** Open past Valid Until, on the day the report ran. */
  expired?: SalesBucket;
  /** quote-revisions: replaced by a finalised revision; in no other bucket. */
  superseded?: SalesBucket;
  /** Won / (Won + Lost) x 100, by count; null when nothing is decided. */
  win_rate_pct?: number | null;
}

/** quote-won-lost: one Lost reason, every reason listed (zeros included). */
export interface SalesLostReason {
  reason: string;
  label: string;
  count: number;
  value: number;
  unpriced: number;
}

export interface SalesBreakdownRow extends SalesFigures {
  key: string;
  label: string;
}

export interface SalesReport {
  from: string | null;
  to: string | null;
  /** "all" for an Admin, "own" for Sales. */
  scope: "all" | "own";
  totals: SalesFigures;
  lost_reasons?: SalesLostReason[];
  by_customer: SalesBreakdownRow[];
  by_salesperson: SalesBreakdownRow[];
  by_month: SalesBreakdownRow[];
}

/** THE SAME REPORT AS A WORKBOOK (reports-export, owner 2026-09-23). Admin
 *  only: the route answers Sales and Viewer with a 403. The file is built on
 *  the server from the very dict getSalesReport receives -- nothing here or
 *  there is recalculated. */
export async function downloadSalesReportXlsx(
  from: string,
  to: string,
): Promise<{ ok: boolean; blob?: Blob; filename?: string; forbidden?: boolean; error?: string }> {
  const params = new URLSearchParams({ export: "xlsx" });
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  return backendDownload(`${ENDPOINTS.salesReport}?${params.toString()}`);
}

/** The report for an inclusive quote-date range. Fails loud: an error comes
 *  back as ok:false with the reason, never as a zeroed report. */
export async function getSalesReport(
  from: string,
  to: string,
): Promise<{ ok: boolean; report?: SalesReport; forbidden?: boolean; error?: string }> {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const call = await backendGet(`${ENDPOINTS.salesReport}?${params.toString()}`);
  if (!call.ok) {
    return { ok: false, forbidden: !!(call as { forbidden?: boolean }).forbidden, error: call.error };
  }
  const body = call.body;
  if (!body?.totals || !Array.isArray(body?.by_customer)) {
    return { ok: false, error: "The report came back in a shape this app does not recognise." };
  }
  return { ok: true, report: body as SalesReport };
}

// ---- Editing an existing customer (customer-details-on-quote) -------------
//
// ⚠️ THE BILLING ADDRESS LIVES ON THE CUSTOMER COMPANY RECORD (owner,
// 2026-09-23). No quote is written; every quote naming this customer --
// finalised ones included -- prints the details as they are NOW. A per-quote
// snapshot is deferred.
//
// ⚠️ TYPED BY A PERSON, ONLY. Nothing from a PO is ever copied into a
// customer; the route accepts no PO input and refuses any key outside its
// allowlist.

export interface IndianState {
  id: number;
  name: string;
  code: string;
}

/** Indian states for the customer form. [] on failure, never a guessed list. */
export async function listIndianStates(): Promise<IndianState[]> {
  const call = await backendGet(ENDPOINTS.states);
  if (!call.ok || !Array.isArray(call.body?.states)) return [];
  return call.body.states;
}

/** The CREATE-path duplicate check: the active customer COMPANIES holding
 *  this GSTIN, exactly (case, spaces and punctuation ignored), at any size.
 *  A holder that is a branch answers its top-level company, since that is
 *  what "Use existing" selects. Replaces the old first-200 scan. */
export async function findCustomersByGstin(
  vat: string,
): Promise<{ ok: boolean; customers?: CustomerPartner[]; error?: string }> {
  const call = await backendGet(
    `${ENDPOINTS.customerGstinMatches}?vat=${encodeURIComponent(vat.trim())}`,
  );
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.customers)) {
    return { ok: false, error: "The GSTIN check came back in a shape this app does not recognise." };
  }
  return { ok: true, customers: call.body.customers };
}

/** What a person may change on a customer. NO `name`: it is locked here. */
export interface CustomerDetailsEdit {
  id: number;
  street: string;
  street2: string;
  city: string;
  zip: string;
  state_id: number | null;
  vat: string;
  email: string;
  phone: string;
}

/** Save a customer's billing details.
 *
 *  A GSTIN already held by another ACTIVE customer comes back as
 *  `duplicates` with NOTHING written; the same call with `confirmDuplicate`
 *  saves. That is a warning, not a refusal (owner, 2026-09-23). */
export async function updateCustomer(
  edit: CustomerDetailsEdit,
  confirmDuplicate = false,
): Promise<{
  ok: boolean;
  entity?: CustomerPartner;
  duplicates?: { id: number; name: string }[];
  // state-from-gstin: the State disagrees with the GSTIN (saved anyway).
  gstinNote?: string | null;
  error?: string;
}> {
  const payload: Record<string, unknown> = { ...edit };
  if (confirmDuplicate) payload.confirm_duplicate_gstin = true;
  const call = await backendPost(ENDPOINTS.updateCustomer, payload);
  // ⚠️ BEFORE the ok check: the duplicate answer IS an ok:false, and the
  // helper hands its body back for exactly this kind of structured refusal.
  if (call.body?.error === "duplicate_gstin" && Array.isArray(call.body?.duplicate_gstin)) {
    return { ok: false, duplicates: call.body.duplicate_gstin };
  }
  if (!call.ok) return { ok: false, error: call.error };
  if (!call.body?.ok || !call.body?.entity) {
    return { ok: false, error: call.body?.error ?? "The customer was not saved." };
  }
  return { ok: true, entity: call.body.entity, gstinNote: call.body.gstin_note ?? null };
}

// ---- PO deliver-to match (po-deliver-to-match) ------------------------------

/** A ship-to suggested from an extracted PO's Deliver-To location code:
 *  an EXACT ref match under THIS quote's customer. Never applied by itself. */
export interface ShipToSuggestion {
  id: number;
  name: string;
  code: string;
  already_set: boolean;
}

/** The "Use this ship-to" click. Own-or-Admin; the backend checks the
 *  ship-to belongs to the quote's customer and writes the ship-to only. */
export async function setQuoteShipTo(quoteId: number, partnerId: number): Promise<{
  ok: boolean; forbidden?: boolean; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.setQuoteShipTo, { quote_id: quoteId, partner_id: partnerId });
  if (!call.ok) return { ok: false, forbidden: call.forbidden, error: call.error };
  return { ok: true };
}

// ---- The real product master --------------------------------------------
//
// THE PRODUCT CATALOG, read from Odoo rather than from mocks/products.ts.
//
// Both screens that show products now read this: the Add Materials picker
// (slice 1b) and /catalog (slice 1c). api.getProducts() is gone with the
// second of those -- there is no mock-returning product read left in this
// file, and adding one back would put two catalogs behind one app.

/** anixify.product.to_payload() on the wire. All 14 keys, as sent.
 *
 *  ⚠️ brand and category are `string | null` HERE and narrowed to the closed
 *  unions in toCatalogProduct. The backend's taxonomy is master data -- a row
 *  someone adds in Odoo -- so the wire genuinely can carry a name this build
 *  has never heard of, and typing it as ProductBrand would be asserting
 *  otherwise. See ProductBrand's comment in types/index.ts.
 *
 *  ⚠️ THERE IS NO PRICE KEY, and its absence is the point. See CatalogProduct. */
export interface BackendProduct {
  id: number;
  part_number: string;
  description: string | null;
  manufacturer: string | null;
  supplier_ref: string | null;
  brand: string | null;
  category: string | null;
  /** The brand RECORD's id, added at D3a-iii. `brand` above stays the name.
   *  Two keys because they answer two questions: the name classifies the
   *  product, the id joins a discount rule. */
  brand_id: number | null;
  hsn_code: string | null;
  default_origin: string | null;
  weight: number;
  volume: number;
  uom: string | null;
  active: boolean;
  /** HOW this row got here: "erp" or "manual".
   *
   *  A backend Selection with required=True and a default, so it is one of
   *  exactly two strings and NEVER null -- unlike every other field above.
   *  Typed `string` rather than a union anyway: it is master data on the wire
   *  and the ProductBrand lesson was that a closed type over an open source
   *  fails silently. Nothing branches on it; it is displayed and no more.
   *
   *  ⚠️ READ-ONLY. It is not in the backend's _PRODUCT_INPUT_FIELDS, so
   *  sending it back changes nothing -- a caller that could set "erp" could
   *  dress a hand-typed one-off as ERP master data. Its companion
   *  created_by_uid is NOT serialized at all: a res.users id answers an
   *  internal audit question, not a browser's. Do not add it here. */
  source: string;
}

/** A wire brand / category name, or null when the product has none.
 *
 *  ⚠️ NO LONGER NARROWED AGAINST A COMPILED LIST (configurable identity P1-9).
 *  The backend sends these through a Many2one to anixify.brand /
 *  anixify.category, so any non-empty name IS an existing master-data row; a
 *  brand added in Odoo now works without a deploy. An absent name is still
 *  null, and the picker still refuses such a row as unclassified. */
function narrowBrand(raw: string | null): ProductBrand | null {
  const name = (raw ?? "").trim();
  return name ? name : null;
}

function narrowCategory(raw: string | null): ProductCategory | null {
  const name = (raw ?? "").trim();
  return name ? name : null;
}

/** The wire row as the picker reads it.
 *
 *  ⚠️ NOTHING IS INVENTED HERE. An absent description, uom or origin comes
 *  through as undefined or "", never as a plausible default -- the same rule
 *  savedQuoteToMaterials follows for spec/uom/origin, and for the same
 *  reason: a guessed value is indistinguishable from a recorded one once it
 *  is on screen. There is no listPrice to map at all. */
export function toCatalogProduct(p: BackendProduct): CatalogProduct {
  return {
    id: p.id,
    partNo: p.part_number,
    description: p.description ?? "",
    manufacturer: p.manufacturer ?? undefined,
    supplierRef: p.supplier_ref ?? undefined,
    brand: narrowBrand(p.brand),
    category: narrowCategory(p.category),
    // ⚠️ NOT NARROWED, AND THAT IS NOT AN OVERSIGHT. `brand` is narrowed
    // against a closed union because an unrecognised NAME cannot be reasoned
    // about by this build. An id is an opaque database key -- there is no
    // "unrecognised" id to guard against, and narrowing it against anything
    // would mean holding a second list of ids here for the join to go wrong
    // against. It rides through as sent, or null.
    brandId: p.brand_id ?? null,
    hsnCode: p.hsn_code ?? undefined,
    defaultOrigin: p.default_origin ?? undefined,
    weight: p.weight,
    volume: p.volume,
    uom: p.uom ?? undefined,
    // Straight through. Not `?? something` -- the backend cannot send null
    // here, and a default would invent a provenance nobody recorded, which is
    // the one thing this field exists to make impossible.
    source: p.source,
  };
}

/** Search the product master by part number or description.
 *
 *  ⚠️ IT NEVER FALLS BACK TO mockProducts. On any failure it returns
 *  {ok: false} and no rows. A silent fallback would put two catalogs on
 *  screen behind one control -- the picker would look like it was showing
 *  the real master while showing invented parts with invented prices, and
 *  the pick would then be saved against a product id that does not exist.
 *  This is the same discipline every other call in this file follows and the
 *  same class of bug the two-keys-live cutover existed to kill.
 *
 *  An empty query returns the first page, exactly as searchCustomers does. */
export async function searchProducts(
  query: string,
  limit = 50,
): Promise<{ ok: boolean; products?: CatalogProduct[]; error?: string }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (query.trim()) params.set("q", query.trim());

  const call = await backendGet(`${ENDPOINTS.products}?${params.toString()}`);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.products)) {
    return {
      ok: false,
      error: "The product list came back in a shape this app does not recognise.",
    };
  }
  return { ok: true, products: call.body.products.map(toCatalogProduct) };
}

/** THE TOP BAR'S SEARCH (global-search, owner 2026-09-30): quotes (number,
 *  customer, project), customers and products, read-only.
 *
 *  ⚠️ IT ADDS NO READ OF ITS OWN. It runs the existing reads -- the quote
 *  list (finalised, then the caller's own drafts), customers and products --
 *  so what a person may find is EXACTLY what each list already shows them:
 *  same role scoping, same drafts rule, by construction and not by a second
 *  copy of the rule.
 *
 *  ⚠️ A GROUP THAT FAILED IS REPORTED, NOT DROPPED. `failed` names each read
 *  that did not answer and why; a search that quietly returned only two of
 *  the three kinds of thing would be a wrong "nothing found". */
export interface GlobalSearchResult {
  quotes: SavedQuoteSummary[];
  customers: CustomerPartner[];
  products: CatalogProduct[];
  failed: Array<{ what: "Quotes" | "Customers" | "Products"; error: string }>;
}

export async function searchEverything(query: string, perGroup = 6): Promise<GlobalSearchResult> {
  const q = query.trim();
  const draftsCall = backendGet(
    `${ENDPOINTS.quotes}?${new URLSearchParams({ drafts: "1", limit: String(perGroup), q }).toString()}`);
  const [finals, drafts, customers, products] = await Promise.all([
    api.listSavedQuotes(perGroup, q), draftsCall, searchCustomers(q, perGroup), searchProducts(q, perGroup),
  ]);
  const failed: GlobalSearchResult["failed"] = [];
  const quotes: SavedQuoteSummary[] = [];
  if (!finals.ok) failed.push({ what: "Quotes", error: finals.error ?? "The quote search failed." });
  else quotes.push(...(finals.quotes ?? []));
  if (drafts.ok && Array.isArray(drafts.body?.quotes)) {
    quotes.push(...(drafts.body.quotes as SavedQuoteSummary[]));
  } else if (!drafts.ok && finals.ok) {
    failed.push({ what: "Quotes", error: drafts.error ?? "Your drafts could not be searched." });
  }
  if (!customers.ok) failed.push({ what: "Customers", error: customers.error ?? "The customer search failed." });
  if (!products.ok) failed.push({ what: "Products", error: products.error ?? "The product search failed." });
  // Newest first, drafts and finalised together, no duplicates.
  const byId = new Map(quotes.map((x) => [x.quote_id, x]));
  return {
    quotes: [...byId.values()].sort((a, b) => b.quote_id - a.quote_id).slice(0, perGroup),
    customers: customers.customers ?? [],
    products: products.products ?? [],
    failed,
  };
}

// ---- OEM discount rules --------------------------------------------------
//
// THE STANDING RATE CARD. anixify.discount.rule holds the rates a client
// negotiated directly with an OEM, keyed on (partner_id, brand_id).
//
// ⚠️ STANDING REGISTERED RATES ONLY. A competitive bid is a negotiation --
// different every time, the OEM quoting a net for that deal alone -- and it is
// entered as a hand-typed net cost on the LINE. It must never be written here.
// Owner's instruction, transcript 2026-09-07.
//
// ⚠️ READ IS VIEWER, WRITE IS ADMIN, and the asymmetry is commercial rather
// than technical: a rule flows into every quote priced afterwards, so Sales
// creating one would let the person negotiating a deal also set the floor for
// it. The backend enforces it; the SPA only avoids offering what would 403.
//
// ⚠️ NO MOCK FALLBACK, on either call. A fallback here would put an invented
// rate on screen under the same heading as a real one -- the two-catalogs-live
// bug, applied to money. mocks/discountRules.ts is now QUARANTINED: nothing
// renders it and nothing prices from it; its only consumer is one test using
// it as a negative fixture.

interface BackendDiscountRule {
  id: number;
  partner_id: number;
  partner_name: string | null;
  brand_id: number;
  brand_name: string | null;
  discount_pct: number;
  effective_from: string | null;
  active: boolean;
  notes: string;
  // ⚠️ THE RECORDED ACTOR. Odoo's own create_uid / write_uid are deliberately
  // NOT on this wire -- the route writes as superuser, so they name OdooBot
  // and would be a false attribution. null = unknown.
  created_by: string | null;
  created_by_uid: number | null;
  last_edited_by: string | null;
  last_edited_by_uid: number | null;
}

export function toOemDiscountRule(r: BackendDiscountRule): OemDiscountRule {
  return {
    // A string, like every other id this app carries on a row. The NUMERIC id
    // is kept separately below because a write must address the record by it.
    id: String(r.id),
    partnerId: r.partner_id,
    partnerName: r.partner_name,
    brandId: r.brand_id,
    brandName: r.brand_name,
    discountPct: r.discount_pct,
    // ⚠️ AUDIT ONLY. null means nobody recorded when the rate was agreed,
    // which is a perfectly valid rule -- it does NOT mean the rule is invalid
    // or pending. Never render it as a validity window.
    effectiveFrom: r.effective_from,
    active: r.active,
    notes: r.notes ?? "",
    // ⚠️ `?? null` RATHER THAN `?? ""`, AND THE DIFFERENCE IS THE WHOLE POINT.
    // An empty string renders as a blank cell, which reads as "nothing to say
    // here". null is rendered as "Unknown" by the table -- an explicit claim
    // that nobody knows, which is the true state for every rule written
    // before the backend carried these columns.
    createdBy: r.created_by ?? null,
    createdByUid: r.created_by_uid ?? null,
    lastEditedBy: r.last_edited_by ?? null,
    lastEditedByUid: r.last_edited_by_uid ?? null,
  };
}

export interface DiscountRulesResult {
  ok: boolean;
  rules?: OemDiscountRule[];
  error?: string;
}

export async function listDiscountRules(): Promise<DiscountRulesResult> {
  const call = await backendGet(ENDPOINTS.discountRules);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.discount_rules)) {
    return { ok: false, error: "Backend response had no 'discount_rules' array." };
  }
  return {
    ok: true,
    rules: (call.body.discount_rules as BackendDiscountRule[]).map(toOemDiscountRule),
  };
}

/** What the editor sends. Only these keys exist on the wire.
 *
 *  ⚠️ AN ABSENT KEY MEANS "LEAVE IT ALONE" ON AN UPDATE, which is the same
 *  convention every other write in this file follows. It is not a way to
 *  clear a required field, and the backend does not offer one. */
export interface DiscountRuleInput {
  /** Absent creates; present updates that row. Never guessed -- it is only
   *  ever an id the backend itself returned. */
  ruleId?: number;
  partnerId?: number;
  brandId?: number;
  discountPct?: number;
  /** "" clears the stored date. Audit only either way. */
  effectiveFrom?: string;
  /** false ARCHIVES the rule. This is how a rate is superseded -- there is no
   *  delete, because deleting one destroys the record of what a line was
   *  priced under. */
  active?: boolean;
  notes?: string;
}

export interface SaveDiscountRuleResult {
  ok: boolean;
  rule?: OemDiscountRule;
  error?: string;
  /** The caller shows the login screen. */
  unauthenticated?: boolean;
  /** Signing in again would change nothing -- this needs an Admin. */
  forbidden?: boolean;
}

export async function saveDiscountRule(
  input: DiscountRuleInput,
): Promise<SaveDiscountRuleResult> {
  // ⚠️ BUILT KEY BY KEY, NEVER SPREAD. Spreading the caller's object would
  // send `undefined` keys, and JSON.stringify drops those -- so it would
  // happen to work, until someone passed an explicit null and cleared a
  // required field. Naming each key is what keeps "absent means leave alone"
  // a decision rather than an accident.
  const payload: Record<string, unknown> = {};
  if (input.ruleId !== undefined) payload.rule_id = input.ruleId;
  if (input.partnerId !== undefined) payload.partner_id = input.partnerId;
  if (input.brandId !== undefined) payload.brand_id = input.brandId;
  if (input.discountPct !== undefined) payload.discount_pct = input.discountPct;
  if (input.effectiveFrom !== undefined) payload.effective_from = input.effectiveFrom;
  if (input.active !== undefined) payload.active = input.active;
  if (input.notes !== undefined) payload.notes = input.notes;

  const call = await backendPost(ENDPOINTS.saveDiscountRule, payload);
  if (!call.ok) {
    return {
      ok: false,
      error: call.error,
      unauthenticated: call.unauthenticated,
      forbidden: call.forbidden,
    };
  }
  if (!call.body?.rule) {
    // ⚠️ NEVER REPORT A SAVE THAT DID NOT HAPPEN. The returned record is the
    // only proof one exists, the same rule saveQuoteToBackend follows for
    // quote_id.
    return { ok: false, error: "Saved but no rule came back." };
  }
  return { ok: true, rule: toOemDiscountRule(call.body.rule as BackendDiscountRule) };
}

/** The brands a rule may name, WITH THE IDS A WRITE NEEDS.
 *
 *  ⚠️ A SECOND SHAPE OF THE SAME LIST, AND NOT REDUNDANT. getTaxonomy above
 *  returns NAMES, because that is the join key when CLASSIFYING A PRODUCT --
 *  anixify.product resolves a payload's brand by exact name and the closed
 *  ProductBrand union compares with `===`. This returns {id, name}, because
 *  anixify.discount.rule stores a LINK and a write must address it by id.
 *
 *  ⚠️ WHY NOT SEND THE BRAND NAME ON THE WRITE, given anixify.brand.name is
 *  UNIQUE and would resolve unambiguously. Because the same payload carries a
 *  PARTNER, and partner names are emphatically NOT unique -- which is the
 *  whole reason the rule model keys on partner_id. Addressing one record by
 *  name and the other by id is the inconsistency that gets a partner name sent
 *  next. ONE RULE: writes address records by id.
 *
 *  ⚠️ AND IT IS NOT INTERSECTED WITH THIS BUILD'S ProductBrand UNION, for the
 *  same reason getTaxonomy is not: a brand this build has never heard of is
 *  still a brand somebody can hold a rate with, and a rule naming it is
 *  perfectly valid. The union governs how a PRODUCT is classified, not which
 *  companies Anixify buys from. */
export interface BrandOption {
  id: number;
  name: string;
}

export async function getBrandOptions(): Promise<{
  ok: boolean;
  brands?: BrandOption[];
  error?: string;
}> {
  const call = await backendGet(ENDPOINTS.taxonomy);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.brand_options)) {
    return {
      ok: false,
      error: "The taxonomy came back without the brand ids this screen needs.",
    };
  }
  return {
    ok: true,
    brands: (call.body.brand_options as BrandOption[]).map((b) => ({
      id: b.id,
      name: String(b.name),
    })),
  };
}

// ---- Sourcing reference data ---------------------------------------------
//
// THE THREE PUBLISHED RATE TABLES, read from the database over
// /anixify/api/{duty,freight,fx}_rates.
//
// ⚠️ THESE ARE THE ROWS THE PRICER READS. anixify.quote.line resolves an
// imported line's duty out of anixify.duty.rate and its conversion rate out
// of anixify.fx.rate. Until these endpoints existed, /sourcing and
// Settings > Exchange Rates rendered mocks/{dutyRates,freightRates,fxRates}.ts
// instead -- two sources for one fact, disagreeing where nobody could look.
// A rate shown on screen and a rate used in a price were different numbers.
//
// ⚠️ READ-ONLY, AND THAT IS NOT AN OVERSIGHT. There is no create, update or
// save endpoint for any of the three, so the detail panels keep their
// disabled ReadOnlySave. Who may publish a rate, and what happens to quotes
// already priced off the row being edited, is a design question that adding
// a POST would answer by accident.
//
// ⚠️ NO MOCK FALLBACK, on any of them. A failure returns {ok: false} and no
// rows; the screens clear and say why. Falling back would be strictly worse
// here than on the catalog: the screen would show a plausible rate that the
// pricer is not using, which is the exact failure these endpoints exist to
// end. `api.getFreightRates()` / `getDutyRates()` / `getFxRates()` are GONE
// for the same reason `api.getProducts()` went at slice 1c.

interface BackendDutyRate {
  id: number;
  hsn_code: string;
  description: string;
  category: string | null;
  bcd_pct: number;
  sws_pct: number;
  igst_pct: number;
  valid_from: string | null;
  valid_to?: string | null;
  created_by?: string | null;
  last_edited_by?: string | null;
  correctable?: boolean;
  notes: string;
  active: boolean;
}

interface BackendFreightRate {
  id: number;
  origin: string;
  destination: string;
  // RAW SELECTION KEYS, not labels. See narrowFreightMode below.
  freight_type: string | null;
  mode: string | null;
  basis: string | null;
  rate: number;
  currency: string | null;
  min_charge: number;
  transit_days: number;
  carrier: string;
  valid_from: string | null;
  valid_to: string | null;
  notes: string;
  active: boolean;
}

interface BackendFxRate {
  id: number;
  currency: string | null;
  rate: number;
  effective_from: string | null;
  effective_to: string | null;
  source: string | null;
  notes: string;
  active: boolean;
  // Optional: a backend predating 17.0.1.38.0 sends neither, which reads as
  // "unknown" -- the honest answer.
  created_by?: string | null;
  closed_by?: string | null;
  last_edited_by?: string | null;
  correctable?: boolean;
}

// ⚠️ NARROWED AT THE MAPPING BOUNDARY, NEVER CAST -- the same rule
// toCatalogProduct follows for brand and category, for the same reason. The
// backend stores Odoo Selection KEYS and this build compares against a closed
// union with ===; a key it has never seen really can arrive, because the
// Selection is the model's to extend. Casting one through would put a value
// into a field the filters and formatters switch on, and the filter would
// then silently match nothing while the row still rendered.
//
// The maps are key -> the union member, so the wire value and the display
// value are related in exactly one place.
const FREIGHT_TYPE_BY_KEY: Record<string, FreightType> = {
  international: "International",
  domestic: "Domestic",
};
const FREIGHT_MODE_BY_KEY: Record<string, FreightMode> = {
  sea: "Sea",
  air: "Air",
  road: "Road",
};
const FREIGHT_BASIS_BY_KEY: Record<string, FreightBasis> = {
  per_cbm: "per CBM",
  per_kg: "per kg",
  per_container: "per container",
  per_trip: "per trip",
};
const FX_SOURCE_BY_KEY: Record<string, FxRateSource> = {
  customs_notified: "Customs Notified",
  manual_override: "Manual Override",
};
// The duty schedule's `category` is FREE TEXT on the model and deliberately
// not a link to the catalog's categories -- so this is a display convenience,
// matched case-insensitively, and an unrecognised grouping is null rather
// than a guess. anixify.duty.rate's own help says it is never matched on.
const DUTY_CATEGORY_BY_NAME: Record<string, LockCategory> = {
  copper: "Copper",
  fiber: "Fiber",
  accessory: "Accessory",
};

function narrowFrom<T>(table: Record<string, T>, raw: string | null): T | null {
  if (!raw) return null;
  return table[raw] ?? null;
}

export function toDutyRate(r: BackendDutyRate): DutyRate {
  return {
    // The screens key rows and selection on this. A string because every
    // other id in this app is one; it is the backend's integer id rendered,
    // never a name -- HSN codes repeat across revisions by design.
    id: String(r.id),
    hsnCode: r.hsn_code,
    description: r.description ?? "",
    category: narrowFrom(DUTY_CATEGORY_BY_NAME, (r.category ?? "").toLowerCase()),
    bcdPct: r.bcd_pct,
    // ⚠️ A PERCENTAGE OF THE BCD, not of goods value. Carried through
    // untouched: combining it with bcd_pct is engine.sourcing's job and
    // nothing here may do it. See the model field's own help.
    swsPct: r.sws_pct,
    // Displayed so the schedule reads complete. Never enters a price.
    igstPct: r.igst_pct,
    validFrom: r.valid_from,
    validTo: r.valid_to ?? null,
    createdBy: r.created_by ?? null,
    lastEditedBy: r.last_edited_by ?? null,
    correctable: r.correctable === true,
    notes: r.notes ?? "",
    active: r.active,
  };
}

export function toFreightRate(r: BackendFreightRate): FreightRate {
  return {
    id: String(r.id),
    origin: r.origin,
    destination: r.destination,
    type: narrowFrom(FREIGHT_TYPE_BY_KEY, r.freight_type),
    mode: narrowFrom(FREIGHT_MODE_BY_KEY, r.mode),
    basis: narrowFrom(FREIGHT_BASIS_BY_KEY, r.basis),
    rate: r.rate,
    // The ISO code, straight through. "" when the row has none -- the table
    // then formats the bare number rather than asserting a currency.
    currency: r.currency ?? "",
    // ⚠️ 0 IS A STATED MINIMUM AND MEANS "no minimum", per the model's own
    // help. It is NOT collapsed to null: that would turn a card that says
    // there is no floor into one where nobody recorded whether there is.
    minCharge: r.min_charge,
    transitDays: r.transit_days,
    carrier: r.carrier ?? "",
    validFrom: r.valid_from,
    // null means OPEN-ENDED -- in force until superseded -- which is what
    // statusOf already reads as Active. Not an unknown date.
    validTo: r.valid_to,
    notes: r.notes ?? "",
    active: r.active,
  };
}

export function toFxRate(r: BackendFxRate): FxRate {
  return {
    id: String(r.id),
    currency: r.currency ?? "",
    // A MULTIPLIER: INR per one unit of `currency`. Not a percentage, and
    // nothing here divides by 100.
    rate: r.rate,
    effectiveFrom: r.effective_from,
    effectiveTo: r.effective_to,
    source: narrowFrom(FX_SOURCE_BY_KEY, r.source),
    notes: r.notes ?? "",
    active: r.active,
    createdBy: r.created_by ?? null,
    closedBy: r.closed_by ?? null,
    lastEditedBy: r.last_edited_by ?? null,
    // Absent (an older backend) reads as "not correctable": no action offered.
    correctable: r.correctable === true,
  };
}

export interface DutyRatesResult { ok: boolean; rates?: DutyRate[]; error?: string }
export interface FreightRatesResult { ok: boolean; rates?: FreightRate[]; error?: string }
export interface FxRatesResult { ok: boolean; rates?: FxRate[]; error?: string }

export async function listDutyRates(): Promise<DutyRatesResult> {
  const call = await backendGet(ENDPOINTS.dutyRates);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.duty_rates)) {
    return { ok: false, error: "Backend response had no 'duty_rates' array." };
  }
  return { ok: true, rates: (call.body.duty_rates as BackendDutyRate[]).map(toDutyRate) };
}

// ---- DUTY RATE ENTRY (duty-rate-entry, 2026-09-30) --------------------------
//
// ADMIN ONLY; the routes refuse everyone else with a 403 whatever the screen
// does. The backend is the authority on every rule (8-digit HSN, 0-100 %,
// not in the past, the window it closes, all-or-nothing on confirm); what
// comes back is shown verbatim. NOTHING is computed here.

export interface DutyRateInput {
  hsn: string;
  bcd_pct: number | string;
  sws_pct: number | string;
  igst_pct: number | string;
  effective_from: string;
}

export interface DutyPreviewRow {
  index: number;
  status: "new" | "replaces" | "error";
  detail: string;
  hsn?: string;
  bcd_pct?: number;
  sws_pct?: number;
  igst_pct?: number;
  effective_from?: string;
  combined_pct?: number;
  change_pct?: number | null;
  large_change?: boolean;
  input?: Record<string, unknown>;
}

export async function saveDutyRate(input: DutyRateInput): Promise<{
  ok: boolean; rate?: DutyRate; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveDutyRate, input);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.duty_rate as BackendDutyRate | undefined;
  if (!row || typeof row.id !== "number") {
    return { ok: false, error: "The rate was not published: no row came back." };
  }
  return { ok: true, rate: toDutyRate(row) };
}

export async function correctDutyRate(
  id: string | number, bcd: number | string, sws: number | string, igst: number | string,
): Promise<{ ok: boolean; rate?: DutyRate; error?: string; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.correctDutyRate, {
    id: Number(id), bcd_pct: bcd, sws_pct: sws, igst_pct: igst,
  });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.duty_rate as BackendDutyRate | undefined;
  if (!row || typeof row.id !== "number") {
    return { ok: false, error: "The rate was not corrected: no row came back." };
  }
  return { ok: true, rate: toDutyRate(row) };
}

/** Read an uploaded CSV/.xlsx and say what confirming WOULD do. SAVES NOTHING. */
export async function previewDutyUpload(
  filename: string, contentBase64: string,
): Promise<{ ok: boolean; rows?: DutyPreviewRow[]; errors?: number; error?: string; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.previewDutyRates, {
    filename, content_base64: contentBase64,
  });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.rows)) {
    return { ok: false, error: "The preview came back in a shape this app does not recognise." };
  }
  return { ok: true, rows: call.body.rows as DutyPreviewRow[], errors: call.body.errors as number };
}

/** Publish the previewed rows: EVERY row or none. The server re-checks them. */
export async function saveDutyRates(rows: DutyRateInput[]): Promise<{
  ok: boolean; count?: number; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveDutyRates, { rows });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const made = call.body?.duty_rates;
  return { ok: true, count: Array.isArray(made) ? made.length : rows.length };
}

// ---- WAREHOUSE MASTER (warehouse-master, 2026-10-01) ------------------------
//
// The list is readable by every role; edits, (de)activation and the CSV upload
// are ADMIN ONLY on the server. Nothing is ever deleted. Contacts are internal
// and never reach a quote document.

export interface Warehouse {
  id: number;
  code: string;
  business_tag: string | null;
  /** "City — Loc# (tag)", built by the server. */
  label: string;
  city: string;
  province_code: string | null;
  state_name: string | null;
  gstin: string | null;
  /** Why the GSTIN cannot be used for GST, or null. */
  gstin_problem: string | null;
  ship_from_address: string | null;
  bill_to_address: string | null;
  import_contact?: string | null;
  local_contact?: string | null;
  is_ftwz: boolean;
  active: boolean;
  /** Active and not FTWZ: may be chosen on a quote. */
  selectable: boolean;
  created_by?: string | null;
  last_edited_by?: string | null;
}

export interface WarehousePreviewRow {
  index: number;
  code?: string;
  label?: string;
  action: "new" | "update" | "unchanged" | "error";
  changes: string[];
  notes: string[];
  error?: string;
}

export interface WarehousePreview {
  rows: WarehousePreviewRow[];
  summary: { new: number; update: number; unchanged: number; error: number };
  truncated_contacts: string[];
  gstin_issues: string[];
}

export const FTWZ_NOTE = "FTWZ — tax treatment not configured yet";

/** The one way a warehouse is named in a picker. FTWZ and inactive sites say
 *  why they cannot be chosen. */
export function warehouseOptionLabel(w: Pick<Warehouse, "label" | "is_ftwz" | "active">): string {
  if (w.is_ftwz) return `${w.label} — ${FTWZ_NOTE}`;
  if (!w.active) return `${w.label} (inactive)`;
  return w.label;
}

export async function getWarehouses(): Promise<{ ok: boolean; warehouses?: Warehouse[]; error?: string }> {
  const call = await backendGet(ENDPOINTS.warehouses);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.warehouses)) {
    return { ok: false, error: "The warehouse list came back in a shape this app does not recognise." };
  }
  return { ok: true, warehouses: call.body.warehouses as Warehouse[] };
}

export type WarehouseEdit = Partial<Pick<Warehouse,
  "business_tag" | "city" | "province_code" | "gstin" | "ship_from_address" | "bill_to_address"
  | "import_contact" | "local_contact" | "is_ftwz">>;

export async function saveWarehouse(id: number, edit: WarehouseEdit): Promise<{
  ok: boolean; warehouse?: Warehouse; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveWarehouse, { id, ...edit });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.warehouse as Warehouse | undefined;
  if (!row || typeof row.id !== "number") return { ok: false, error: "No warehouse came back." };
  return { ok: true, warehouse: row };
}

export async function setWarehouseActive(id: number, active: boolean): Promise<{
  ok: boolean; warehouse?: Warehouse; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.setWarehouseActive, { id, active });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.warehouse as Warehouse | undefined;
  if (!row || typeof row.id !== "number") return { ok: false, error: "No warehouse came back." };
  return { ok: true, warehouse: row };
}

/** What a CSV re-upload WOULD do. SAVES NOTHING. */
export async function previewWarehouseUpload(filename: string, contentBase64: string): Promise<{
  ok: boolean; preview?: WarehousePreview; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.previewWarehouses, { filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.rows)) {
    return { ok: false, error: "The preview came back in a shape this app does not recognise." };
  }
  return { ok: true, preview: call.body as WarehousePreview };
}

/** Apply the upload: every row or none. The server re-plans it. */
export async function importWarehouseUpload(filename: string, contentBase64: string): Promise<{
  ok: boolean; preview?: WarehousePreview; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.importWarehouses, { filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  return { ok: true, preview: call.body as WarehousePreview };
}

// ---- COMPANY HOLIDAYS (holiday-list, M1, 2026-11-03) ------------------------
//
// One company-wide list. Its ONLY reader is the OEM-order "at risk" rule
// (2 working days = Mon-Fri minus active holidays). Every role reads; adding,
// renaming, (de)activating and the upload are ADMIN ONLY on the server. A
// holiday's date is never edited and nothing is ever deleted.

export interface Holiday {
  id: number;
  /** ISO yyyy-mm-dd. */
  date: string;
  name: string;
  active: boolean;
  created_by?: string | null;
  last_edited_by?: string | null;
}

export interface HolidayPreviewRow {
  index: number;
  date?: string;
  name?: string;
  action: "new" | "unchanged" | "error";
  error?: string;
}

export interface HolidayPreview {
  rows: HolidayPreviewRow[];
  summary: { new: number; unchanged: number; error: number };
}

type HolidayWrite = { ok: boolean; holiday?: Holiday; error?: string; forbidden?: boolean };

function holidayFrom(call: { ok: boolean; body?: any; error?: string; forbidden?: boolean }): HolidayWrite {
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.holiday as Holiday | undefined;
  if (!row || typeof row.id !== "number") return { ok: false, error: "No holiday came back." };
  return { ok: true, holiday: row };
}

export async function getHolidays(): Promise<{ ok: boolean; holidays?: Holiday[]; error?: string }> {
  const call = await backendGet(ENDPOINTS.holidays);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.holidays)) {
    return { ok: false, error: "The holiday list came back in a shape this app does not recognise." };
  }
  return { ok: true, holidays: call.body.holidays as Holiday[] };
}

export async function addHoliday(date: string, name: string): Promise<HolidayWrite> {
  return holidayFrom(await backendPost(ENDPOINTS.saveHoliday, { date, name }));
}

export async function renameHoliday(id: number, name: string): Promise<HolidayWrite> {
  return holidayFrom(await backendPost(ENDPOINTS.saveHoliday, { id, name }));
}

export async function setHolidayActive(id: number, active: boolean): Promise<HolidayWrite> {
  return holidayFrom(await backendPost(ENDPOINTS.setHolidayActive, { id, active }));
}

/** What an upload WOULD do. SAVES NOTHING. */
export async function previewHolidayUpload(filename: string, contentBase64: string): Promise<{
  ok: boolean; preview?: HolidayPreview; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.previewHolidays, { filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.rows)) {
    return { ok: false, error: "The preview came back in a shape this app does not recognise." };
  }
  return { ok: true, preview: call.body as HolidayPreview };
}

/** Apply the upload: every row or none. The server re-plans it. */
export async function importHolidayUpload(filename: string, contentBase64: string): Promise<{
  ok: boolean; preview?: HolidayPreview; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.importHolidays, { filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  return { ok: true, preview: call.body as HolidayPreview };
}

// ---- OEM QUOTE REGISTER (oem-quote-register, 2026-10-06) --------------------
//
// Quotes OUR SUPPLIERS gave us. Every role reads the register; Sales and Admin
// write it, and the server enforces that whatever the screen shows. There is
// no delete. An upload of lines SAVES NOTHING: its rows join the editor and
// the user saves the whole OEM quote.
//
// ⚠️ INTERNAL SUPPLIER DATA. Nothing here may reach the customer document.

export type OemCurrency = "INR" | "USD" | "EUR" | "CNY";
export const OEM_CURRENCIES: OemCurrency[] = ["INR", "USD", "EUR", "CNY"];

export interface OemQuoteLine {
  id?: number;
  part_number: string;
  description?: string | null;
  qty: number;
  uom?: string | null;
  unit_price: number;
  extended_price?: number | null;
  lead_time?: string | null;
  est_ship_date?: string | null;
  shipping_method?: string | null;
  /** On a customer quote: its part number and price can no longer change. */
  used?: boolean;
}

export interface OemQuote {
  id: number;
  oem: string;
  /** M2 (D197): the OEM this quote's name links to (stored or resolved); null = not linked. */
  oem_link?: OemLink | null;
  number: string;
  quote_date: string | null;
  valid_until: string | null;
  expired: boolean;
  project_name: string | null;
  currency: OemCurrency;
  notes: string | null;
  line_count: number;
  used_count: number;
  created_by: string | null;
  last_edited_by: string | null;
  lines?: OemQuoteLine[];
}

export interface OemQuoteEdit {
  id?: number;
  oem: string;
  /** M2 (D197): picked from the OEM master; the text snapshot is still sent. */
  oem_id?: number | null;
  number: string;
  quote_date?: string | null;
  valid_until?: string | null;
  project_name?: string | null;
  currency: OemCurrency;
  notes?: string | null;
  lines: OemQuoteLine[];
}

export interface OemQuotePreviewRow { index: number; ok: boolean; line?: OemQuoteLine; error?: string }

export interface OemQuoteMatch extends OemQuoteLine {
  id: number;
  oem_quote: Pick<OemQuote, "id" | "oem" | "number" | "quote_date" | "valid_until" | "expired"
    | "currency" | "project_name">;
  warnings: OemQuoteWarning[];
}

export async function listOemQuotes(q = ""): Promise<{ ok: boolean; oemQuotes?: OemQuote[]; error?: string }> {
  const path = q.trim() ? `${ENDPOINTS.oemQuotes}?q=${encodeURIComponent(q.trim())}` : ENDPOINTS.oemQuotes;
  const call = await backendGet(path);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.oem_quotes)) {
    return { ok: false, error: "The OEM quote list came back in a shape this app does not recognise." };
  }
  return { ok: true, oemQuotes: call.body.oem_quotes as OemQuote[] };
}

export async function getOemQuote(id: number): Promise<{ ok: boolean; oemQuote?: OemQuote; error?: string }> {
  const call = await backendPost(ENDPOINTS.getOemQuote, { id });
  if (!call.ok) return { ok: false, error: call.error };
  const row = call.body?.oem_quote as OemQuote | undefined;
  if (!row || typeof row.id !== "number") return { ok: false, error: "No OEM quote came back." };
  return { ok: true, oemQuote: row };
}

export async function saveOemQuote(edit: OemQuoteEdit): Promise<{
  ok: boolean; oemQuote?: OemQuote; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveOemQuote, edit);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.oem_quote as OemQuote | undefined;
  if (!row || typeof row.id !== "number") return { ok: false, error: "No OEM quote came back." };
  return { ok: true, oemQuote: row };
}

/** What an uploaded CSV/.xlsx of lines reads as. SAVES NOTHING. */
export async function previewOemQuoteLines(filename: string, contentBase64: string): Promise<{
  ok: boolean; rows?: OemQuotePreviewRow[]; errors?: number; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.previewOemQuoteLines, { filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.rows)) {
    return { ok: false, error: "The preview came back in a shape this app does not recognise." };
  }
  return { ok: true, rows: call.body.rows as OemQuotePreviewRow[], errors: Number(call.body.errors ?? 0) };
}

/** OEM quote lines a customer line may use: EXACT normalised part number and
 *  the same OEM. With a quote id each match carries its amber warnings. */
export async function oemQuoteMatches(args: {
  oem: string; partNumber: string; qty: number; quoteId?: number;
}): Promise<{ ok: boolean; matches?: OemQuoteMatch[]; error?: string; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.oemQuoteMatches, {
    oem: args.oem, part_number: args.partNumber, qty: args.qty,
    ...(args.quoteId ? { quote_id: args.quoteId } : {}),
  });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.matches)) {
    return { ok: false, error: "The OEM quote matches came back in a shape this app does not recognise." };
  }
  return { ok: true, matches: call.body.matches as OemQuoteMatch[] };
}

// ---- Order Management O1/O2: OEM confirmations ---------------------------------

export type OemQuoteStatus =
  | "draft" | "awaiting_oem" | "oem_confirmed"
  | "sent" | "accepted" | "lost" | "expired" | "superseded";
export type OemBadge =
  | "price_differs" | "currency_differs" | "oem_expired" | "oem_validity_before_quote" | "awaiting_oem";
export interface QuoteOemListStatus {
  status: OemQuoteStatus;
  badges: OemBadge[];
  priced_lines: number;
  confirmed_lines: number;
}
export type OemLineStatus =
  | "not_quoted" | "unconfirmed" | "lead_time_not_stated" | "validity_not_stated"
  | "expired" | "partial" | "confirmed";
/** ABSENT IS NOT ZERO: days/date are null unless the OEM stated them. */
export interface OemLeadTime {
  kind: "days" | "date" | "not_stated";
  days: number | null;
  date: string | null;
  text: string | null;
}
export interface OemConfirmation {
  /** M2 (D197): the OEM that confirmed (the line's OEM for every old record). */
  oem?: string;
  id: number;
  state: "active" | "superseded" | "withdrawn";
  confirmed_unit_price: number;
  currency: string;
  confirmed_qty: number;
  lead_time: OemLeadTime;
  valid_until: string | null;
  oem_reference: string | null;
  oem_quote_line_id: number | null;
  notes: string | null;
  recorded_by: string | null;
  recorded_at: string | null;
  supersedes_id: number | null;
  superseded_by_id: number | null;
  withdrawn_by: string | null;
  withdrawn_at: string | null;
  withdraw_reason: string | null;
  diff_accepted_by?: string | null;
  diff_accepted_at?: string | null;
  diff_accept_reason?: string | null;
  diff_accept_impact?: string | null;
  /** The difference's effect, for an Admin to read BEFORE accepting. */
  margin_impact?: string | null;
  /** Already ordered against this confirmation (not cancelled). */
  ordered_qty?: number;
}
export interface OemDifference {
  confirmation_id: number;
  comparable: boolean;
  per_unit?: number;
  on_confirmed_qty?: number;
}
export interface OemConfirmationLine {
  line_id: number;
  line_no: number;
  part_number: string;
  oem: string;
  qty: number;
  /** The unit cost the QUOTE used -- what a confirmation is compared with. */
  unit_price: number;
  price_status: string;
  status: OemLineStatus;
  confirmed_qty: number;
  earliest_valid_until: string | null;
  price_differs: boolean;
  currency_differs: boolean;
  differences: OemDifference[];
  confirmations: OemConfirmation[];
}
export interface OemConfirmationsView {
  quote_id: number;
  status: OemQuoteStatus;
  badges: OemBadge[];
  cost_currency: string;
  valid_until: string | null;
  can_record: boolean;
  can_accept_difference?: boolean;
  lines: OemConfirmationLine[];
  priced_lines: number;
  confirmed_lines: number;
  awaiting_lines: number;
  any_recorded: boolean;
  price_differs: boolean;
  currency_differs: boolean;
  expired_lines: number;
  earliest_valid_until: string | null;
}
export interface OemConfirmationInput {
  line_id: number;
  confirmed_unit_price: number;
  currency: string;
  confirmed_qty: number;
  lead_time: { kind: "days" | "date" | "not_stated"; days?: number; date?: string; text?: string };
  valid_until?: string | null;
  oem_reference?: string;
  supersedes_id?: number;
  notes?: string;
}

export async function getOemConfirmations(quoteId: number): Promise<{
  ok: boolean; view?: OemConfirmationsView; error?: string; notFound?: boolean;
}> {
  const call = await backendGet(`${ENDPOINTS.oemConfirmations}?quote_id=${encodeURIComponent(String(quoteId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.lines)) {
    return { ok: false, error: "The OEM confirmations came back in a shape this app does not recognise." };
  }
  return { ok: true, view: call.body as OemConfirmationsView };
}

export async function recordOemConfirmation(input: OemConfirmationInput): Promise<{
  ok: boolean; confirmation?: OemConfirmation; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.recordOemConfirmation, input);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const c = call.body?.confirmation as OemConfirmation | undefined;
  if (!c || typeof c.id !== "number") return { ok: false, error: "No confirmation came back." };
  return { ok: true, confirmation: c };
}

export async function withdrawOemConfirmation(id: number, reason: string): Promise<{
  ok: boolean; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.withdrawOemConfirmation, { id, reason });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  return { ok: true };
}

// ---- Order Management O3-O5 -------------------------------------------------------

export interface OemRequest {
  id: number;
  oem: string;
  state: "draft" | "sent" | "cancelled";
  display_state: "draft" | "sent" | "answered" | "cancelled";
  due_by: string | null;
  oem_contact: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string | null;
  sent_by: string | null;
  sent_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  lines: { line_id: number | null; part_number: string; qty: number; attached: boolean }[];
}
export interface GateRefusal { line_no: number | null; part_number: string | null; reason: string }
export interface OemOrderLine {
  id: number;
  line_no: number | null;
  part_number: string;
  description: string | null;
  qty_ordered: number;
  unit_price: number;
  currency: string;
  confirmation_id: number;
  oem_reference: string | null;
  lead_time: { kind: "days" | "date" | "not_stated"; days: number | null; date: string | null };
  promised_date: string | null;
  /** O6: the promise as it stands (latest acknowledgement); promised_date is
   *  the one the order was placed with and is never overwritten. */
  current_promised_date?: string | null;
  shipped_qty?: number;
  delivered_qty?: number;
  /** M4: net GOOD / damaged units received on GRNs (via-warehouse orders; null for drop-ship). */
  received_qty?: number | null;
  damaged_qty?: number | null;
  /** M3.1: the customer date of the quote line (null = not stated) and the
   *  server's derived flags. */
  customer_promised_date?: string | null;
  promise_after_customer?: boolean;
  customer_date_at_risk?: boolean;
  attached: boolean;
}
/** O6: derived by the server from the recorded events; never stored. */
export type OemOrderStatusName = "placed" | "acknowledged" | "partially_shipped" | "shipped"
  // M4 (owner Q2): a via-warehouse order part-received on GRNs.
  | "partially_delivered" | "delivered" | "cancelled";
export type OemOrderEventKind = "acknowledged" | "shipped" | "delivered" | "cancelled";
export interface OemOrderEvent {
  id: number;
  kind: OemOrderEventKind;
  event_date: string;
  oem_reference: string | null;
  note: string | null;
  reason: string | null;
  recorded_by: string | null;
  recorded_at: string | null;
  /** qty is null on an acknowledgement -- absent, not zero. */
  lines: { order_line_id: number; part_number: string; qty: number | null;
    promised_before: string | null; promised_after: string | null }[];
  /** M4: proof-of-delivery files on a drop-ship Delivered event. */
  pod_count?: number;
}
export interface OemOrder {
  id: number;
  number: string;
  quote_id: number;
  quote_name: string;
  customer?: string | null;
  oem: string;
  status: OemOrderStatusName;
  late?: boolean;
  at_risk?: boolean;
  flag_reasons?: string[];
  events?: OemOrderEvent[];
  currency: string;
  ship_to_kind: "customer" | "warehouse";
  /** M4: in the company currency (no customs actuals). */
  domestic?: boolean;
  ship_to: string | null;
  warehouse_id: number | null;
  notes: string | null;
  placed_by: string | null;
  placed_at: string | null;
  lines: OemOrderLine[];
  /** M3.1, derived on read by the server. */
  promise_after_customer?: boolean;
  customer_date_at_risk?: boolean;
  customer_reasons?: string[];
}
export interface OemOrderIssuer {
  company_name: string | null; address: string | null; gstin: string | null;
  contact_email: string | null; contact_phone: string | null;
}
export interface OemOrderItem { confirmation_id: number; qty: number }
export type OemShipTo = { kind: "customer" } | { kind: "warehouse"; warehouse_id: number };

export async function getOemRequests(quoteId: number): Promise<{
  ok: boolean; requests?: OemRequest[]; canRecord?: boolean; error?: string; notFound?: boolean;
}> {
  const call = await backendGet(`${ENDPOINTS.oemRequests}?quote_id=${encodeURIComponent(String(quoteId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.requests)) return { ok: false, error: "The price requests came back in an unknown shape." };
  return { ok: true, requests: call.body.requests as OemRequest[], canRecord: !!call.body.can_record };
}

export async function createOemRequests(quoteId: number, oem?: string, dueBy?: string): Promise<{
  ok: boolean; requests?: OemRequest[]; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.createOemRequests, {
    quote_id: quoteId, ...(oem ? { oem } : {}), ...(dueBy ? { due_by: dueBy } : {}),
  });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, requests: (call.body?.requests ?? []) as OemRequest[] };
}

export async function updateOemRequest(id: number, action: "sent" | "cancel", extra: {
  reason?: string; oemContact?: string; dueBy?: string;
} = {}): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.updateOemRequest, {
    id, action, ...(extra.reason !== undefined ? { reason: extra.reason } : {}),
    ...(extra.oemContact ? { oem_contact: extra.oemContact } : {}), ...(extra.dueBy ? { due_by: extra.dueBy } : {}),
  });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}

export async function acceptOemDifference(id: number, reason: string): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.acceptOemDifference, { id, reason });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}

/** THE GATE, asked without ordering. Writes nothing. */
export async function checkOemOrder(quoteId: number, items: OemOrderItem[]): Promise<{
  ok: boolean; canOrder?: boolean; refusals?: GateRefusal[]; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.oemOrderCheck, { quote_id: quoteId, items });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, canOrder: !!call.body?.can_order, refusals: (call.body?.refusals ?? []) as GateRefusal[] };
}

export async function createOemOrder(quoteId: number, items: OemOrderItem[], shipTo: OemShipTo, notes?: string): Promise<{
  ok: boolean; order?: OemOrder; refusals?: GateRefusal[]; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.createOemOrder, {
    quote_id: quoteId, items, ship_to: shipTo, ...(notes ? { notes } : {}),
  });
  if (!call.ok) {
    const refusals = Array.isArray(call.body?.refusals) ? (call.body.refusals as GateRefusal[]) : undefined;
    return { ok: false, error: call.error, refusals };
  }
  return { ok: true, order: call.body?.order as OemOrder };
}

export async function getOemOrders(quoteId: number): Promise<{
  ok: boolean; orders?: OemOrder[]; nextNumber?: string | null; error?: string; notFound?: boolean;
}> {
  const call = await backendGet(`${ENDPOINTS.oemOrders}?quote_id=${encodeURIComponent(String(quoteId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.orders)) return { ok: false, error: "The OEM orders came back in an unknown shape." };
  return { ok: true, orders: call.body.orders as OemOrder[], nextNumber: call.body.next_number ?? null };
}

export async function getOemOrder(id: number): Promise<{
  ok: boolean; order?: OemOrder; issuer?: OemOrderIssuer; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.getOemOrder, { id });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, order: call.body?.order as OemOrder, issuer: call.body?.issuer as OemOrderIssuer };
}

/** O6: what a person records happening to an OEM order. Quantities and dates
 *  travel as typed: an empty one is sent as null and the server refuses it. */
export interface OemOrderEventInput {
  kind: OemOrderEventKind;
  event_date: string | null;
  oem_reference?: string;
  note?: string;
  reason?: string;
  lines?: { order_line_id: number; qty?: number | null; promised_date?: string | null }[];
}

export async function recordOemOrderEvent(orderId: number, input: OemOrderEventInput): Promise<{
  ok: boolean; order?: OemOrder; event?: OemOrderEvent; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.recordOemOrderEvent, { order_id: orderId, ...input });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, order: call.body?.order as OemOrder, event: call.body?.event as OemOrderEvent };
}

/** O7: one customer order's lines, each with its quantities and OEM orders. */
export interface OrderStatusLine {
  line_id: number; line_no: number; part_number: string; oem: string; qty: number; not_quoted: boolean;
  confirmed: number; ordered: number; shipped: number; delivered: number;
  left_to_order: number; left_to_deliver: number;
  reconciles: boolean; breaks: string[]; late: boolean; at_risk: boolean;
  /** M3.1: null = "Customer date not stated". The two flags are the server's, derived on read. */
  customer_promised_date?: string | null;
  promise_after_customer?: boolean;
  customer_date_at_risk?: boolean;
  /** M4: GRN / delivery-note quantities per quote line (via-warehouse orders). */
  received?: number; damaged?: number; dispatched?: number; to_dispatch?: number;
  via_warehouse?: boolean; drop_ship?: boolean;
  orders: { order_id: number; number: string; oem: string; status: OemOrderStatusName; qty_ordered: number;
    shipped_qty: number; delivered_qty: number; promised_date: string | null;
    current_promised_date: string | null; late: boolean; at_risk: boolean;
    promise_after_customer?: boolean; customer_date_at_risk?: boolean }[];
}
export interface OrderStatusView {
  quote_id: number; lines: OrderStatusLine[]; orders: OemOrder[];
  late: boolean; at_risk: boolean; reconciles: boolean;
  promise_after_customer?: boolean; customer_date_at_risk?: boolean;
  /** M4 (O11): the derived close-out state (null = open) and its history. */
  closed?: CloseState | null;
  close_history?: { kind: "closed" | "reopened"; reason: string; by: string | null; at: string | null }[];
}

/** M3.1: set or clear customer dates on a Won (Accepted) quote. Answers the
 *  refreshed order status. The server refuses drafts, Open, Lost, Superseded. */
export async function setCustomerDates(quoteId: number, lines: CustomerDateEntry[]): Promise<{
  ok: boolean; view?: OrderStatusView; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.setCustomerDates, { quote_id: quoteId, lines });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.lines)) return { ok: false, error: "The order status came back in an unknown shape." };
  return { ok: true, view: call.body as OrderStatusView };
}

export async function getOrderStatus(quoteId: number): Promise<{
  ok: boolean; view?: OrderStatusView; error?: string; notFound?: boolean;
}> {
  const call = await backendGet(`${ENDPOINTS.orderStatus}?quote_id=${encodeURIComponent(String(quoteId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.lines)) return { ok: false, error: "The order status came back in an unknown shape." };
  return { ok: true, view: call.body as OrderStatusView };
}

export interface OpenOemOrderFilters {
  oem?: string; status?: string; late?: boolean; atRisk?: boolean;
  /** M3.1: OEM promise after the customer date, or the customer date at risk. */
  customerRisk?: boolean;
}

export async function getOpenOemOrders(f: OpenOemOrderFilters = {}): Promise<{
  ok: boolean; orders?: OemOrder[]; error?: string;
}> {
  const q = new URLSearchParams();
  if (f.oem) q.set("oem", f.oem);
  if (f.status) q.set("status", f.status);
  if (f.late) q.set("late", "1");
  if (f.atRisk) q.set("at_risk", "1");
  if (f.customerRisk) q.set("customer_risk", "1");
  const qs = q.toString();
  const call = await backendGet(`${ENDPOINTS.openOemOrders}${qs ? `?${qs}` : ""}`);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.orders)) return { ok: false, error: "The OEM orders came back in an unknown shape." };
  return { ok: true, orders: call.body.orders as OemOrder[] };
}

/** A picked match as the link a line carries. */
export function oemLinkFromMatch(m: OemQuoteMatch): OemQuoteLink {
  return {
    line_id: m.id, oem_quote_id: m.oem_quote.id, oem: m.oem_quote.oem, number: m.oem_quote.number,
    valid_until: m.oem_quote.valid_until, currency: m.oem_quote.currency, unit_price: m.unit_price,
    qty: m.qty, lead_time: m.lead_time ?? null, warnings: m.warnings,
  };
}

/** The warnings a line shows NOW. The server's, except the quantity one,
 *  which follows the line's current quantity (it may have been edited since
 *  the pick or the last save). A comparison, not a calculation. */
export function oemWarningsFor(line: Pick<Material, "qty" | "oemQuote" | "discountSource">): OemQuoteWarning[] {
  const link = line.oemQuote;
  if (!link || line.discountSource !== "oem_quote") return [];
  const rest = (link.warnings ?? []).filter((w) => w.kind !== "qty");
  if (link.qty < line.qty) {
    rest.push({ kind: "qty", text: `The OEM quote covers ${link.qty}; this line asks for ${line.qty}.` });
  }
  return rest;
}

// ---- DATA READINESS (data-readiness, 2026-09-30) ----------------------------
//
// ADMIN ONLY and READ ONLY. The backend builds every verdict from the same
// resolvers the save path uses; this only carries the answer to the screen.

export interface ReadinessQuote { quote_id: number; name: string }
export interface ReadinessItem { label: string; quotes: ReadinessQuote[] }
export interface ReadinessGroup { key: string; title: string; hint: string; items: ReadinessItem[] }
export interface ReadinessReport {
  groups: ReadinessGroup[];
  blocked_quotes: number;
  badge: number;
  truncated: boolean;
}

export async function getReadiness(): Promise<{
  ok: boolean; report?: ReadinessReport; error?: string; forbidden?: boolean;
}> {
  const call = await backendGet(ENDPOINTS.readiness);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.groups)) {
    return { ok: false, error: "The readiness report came back in a shape this app does not recognise." };
  }
  return { ok: true, report: call.body as unknown as ReadinessReport };
}

export async function listFreightRates(): Promise<FreightRatesResult> {
  const call = await backendGet(ENDPOINTS.freightRates);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.freight_rates)) {
    return { ok: false, error: "Backend response had no 'freight_rates' array." };
  }
  return { ok: true, rates: (call.body.freight_rates as BackendFreightRate[]).map(toFreightRate) };
}

/** Publish a new exchange rate. ADMIN ONLY -- the route refuses anyone else
 *  with a 403, and the Settings screen does not offer the form to them.
 *
 *  ⚠️ NEVER REPORTS A RATE THAT WAS NOT PUBLISHED. The returned row is the
 *  only proof one exists; an ok:true without it is a failure here, on the
 *  same terms createProduct and saveQuoteToBackend follow.
 *
 *  ⚠️ IT SENDS THREE FIELDS AND NOTHING ELSE. No source, no effective_to, no
 *  actor: the backend decides the window and stamps the author from the
 *  SESSION, and a body carrying `created_by` must change nothing. */
export async function saveFxRate(
  currency: string,
  rate: number,
  effectiveFrom: string,
): Promise<{
  ok: boolean; fxRate?: FxRate; error?: string;
  unauthenticated?: boolean; forbidden?: boolean;
}> {
  const call = await backendPost(ENDPOINTS.saveFxRate, {
    currency, rate, effective_from: effectiveFrom,
  });
  if (!call.ok) {
    return { ok: false, error: call.error,
             unauthenticated: call.unauthenticated, forbidden: call.forbidden };
  }
  const row = call.body?.fx_rate as BackendFxRate | undefined;
  if (!row || typeof row.id !== "number") {
    return { ok: false, error: "The rate was not published: no row came back." };
  }
  return { ok: true, fxRate: toFxRate(row) };
}

/** Correct a MISTYPED rate (fx-rate-correction). The backend is the
 *  authority on every rule -- today-or-later only, never a rate a finalised
 *  quote used, the value only -- and its refusal comes back verbatim. */
export async function correctFxRate(
  id: string | number,
  rate: number,
): Promise<{ ok: boolean; fxRate?: FxRate; error?: string; forbidden?: boolean }> {
  const call = await backendPost(ENDPOINTS.correctFxRate, { id: Number(id), rate });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  const row = call.body?.fx_rate as BackendFxRate | undefined;
  if (!row || typeof row.id !== "number") {
    return { ok: false, error: "The rate was not corrected: no row came back." };
  }
  return { ok: true, fxRate: toFxRate(row) };
}

export async function listFxRates(): Promise<FxRatesResult> {
  const call = await backendGet(ENDPOINTS.fxRates);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.fx_rates)) {
    return { ok: false, error: "Backend response had no 'fx_rates' array." };
  }
  return { ok: true, rates: (call.body.fx_rates as BackendFxRate[]).map(toFxRate) };
}

// ---- Stock levels --------------------------------------------------------
//
// ⚠️ THE ONE RULE THIS WHOLE PATH EXISTS TO PRESERVE: ABSENT IS NOT ZERO.
//
// Three states, not two. A product with NO ENTRY has not been reported on --
// nobody looked, or the read failed. A product whose entry holds 0 is a
// STATEMENT: somebody looked and there is none. A product whose entry holds a
// positive number is the ordinary case.
//
// Collapsing the first two is what the previous mock join did at every step
// (a Map miss became 0; a real 0 was then written back out as undefined), and
// it is why the picker could show "—" against a part nobody had ever counted
// while showing the identical "—" against a part known to be empty. Neither
// the caller nor the screen could tell which. Every function below is written
// so that distinction survives: nothing fills a missing id in, and nothing
// converts a stated 0 to an absence.

/** One location's line in the ?locations=1 breakdown, exactly as the wire
 *  sends it. Every field but the quantity is nullable, and a null stays null:
 *  a figure the source did not attribute to a location is exactly that, and
 *  calling it "ALL" would invent a warehouse that sorts and filters like a
 *  real one. */
export interface StockLocationLevel {
  location: string | null;
  qty_available: number;
  as_of: string | null;
  source: string | null;
}

export interface StockReadResult {
  ok: boolean;
  /** id -> quantity, holding ONLY the ids the server actually reported on.
   *  A missing id is UNKNOWN and must never be defaulted to 0 by a reader. */
  stock?: Map<number, number>;
  /** Present only when `locations` was asked for. Same absent-is-unknown rule. */
  locations?: Map<number, StockLocationLevel[]>;
  /** FALSE when the backend has no real stock source connected
   *  (no-mock-stock-on-live): the screens say "No stock data yet". */
  configured?: boolean;
  error?: string;
}

/** The one wording for "no stock source is connected" (no-mock-stock-on-live). */
export const NO_STOCK_DATA_TEXT = "No stock data yet";

// The backend refuses more than 500 ids per call -- an ERROR, not a silent
// truncation, because a trimmed list would answer about a subset while
// looking complete and the dropped ids would then read as "unreported"
// instead of "not asked". So the chunking is the CLIENT's job, and this
// constant must not drift above the server's cap.
const STOCK_MAX_PRODUCT_IDS = 500;

/** Read stock levels for a specific set of product ids.
 *
 *  ⚠️ AN EMPTY LIST IS NOT A REQUEST FOR EVERYTHING. The endpoint reads an
 *  omitted (or blank) `product_ids` as "everything the provider knows about",
 *  so passing the query string through unguarded would turn a screen with no
 *  linked products into a full-catalog scan. Asking about nothing is answered
 *  here, with nothing, and no request is made at all.
 *
 *  ⚠️ IT NEVER FALLS BACK TO MOCK INVENTORY. There is no `?? mockInventory`
 *  on this path and there must never be one: two stock sources behind one pill
 *  is the same class of bug the two-catalogs cutover killed, and a quantity
 *  invented by a generator is indistinguishable on screen from one somebody
 *  counted. A failure returns {ok: false} and NO map -- the caller renders
 *  unknown, which is honest, rather than zero, which is a claim.
 *
 *  Chunked at the server's cap and merged. A chunk failing fails the whole
 *  call: a partial map would report the un-fetched ids as unknown, which is
 *  true of them individually but would let a half-answered screen look
 *  fully answered. */
export async function getStock(
  productIds: number[],
  opts: { locations?: boolean } = {},
): Promise<StockReadResult> {
  // Deduped, and non-ids dropped rather than sent. The endpoint rejects a
  // non-integer token outright (it refuses to guess at a part number), so one
  // stray undefined in the caller's list would fail the whole read.
  const ids = Array.from(
    new Set(productIds.filter((n) => Number.isInteger(n) && n > 0)),
  );
  if (ids.length === 0) return { ok: true, stock: new Map(), locations: opts.locations ? new Map() : undefined };

  const stock = new Map<number, number>();
  const locations = opts.locations ? new Map<number, StockLocationLevel[]>() : undefined;
  let configured = true;

  for (let i = 0; i < ids.length; i += STOCK_MAX_PRODUCT_IDS) {
    const chunk = ids.slice(i, i + STOCK_MAX_PRODUCT_IDS);
    const params = new URLSearchParams({ product_ids: chunk.join(",") });
    if (opts.locations) params.set("locations", "1");

    const call = await backendGet(`${ENDPOINTS.stock}?${params.toString()}`);
    if (!call.ok) return { ok: false, error: call.error };

    if (call.body?.configured === false) configured = false;
    const raw = call.body?.stock;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return {
        ok: false,
        error: "The stock levels came back in a shape this app does not recognise.",
      };
    }

    // ⚠️ BUILT FROM WHAT CAME BACK, NEVER FROM WHAT WAS ASKED FOR. The loop is
    // over the RESPONSE's keys, not over `chunk`, which is what keeps an
    // unreported product out of the map entirely instead of in it holding 0.
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      const id = Number(key);
      if (!Number.isInteger(id) || id <= 0) continue;
      // A non-numeric quantity is a shape failure, not a zero. Reporting it as
      // 0 would be this app inventing the one value it is least allowed to.
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return {
          ok: false,
          error: `The stock level for product ${id} came back as a ${typeof value}, not a number.`,
        };
      }
      stock.set(id, value);
    }

    if (locations) {
      const rawLoc = call.body?.locations;
      if (rawLoc && typeof rawLoc === "object" && !Array.isArray(rawLoc)) {
        for (const [key, value] of Object.entries(rawLoc as Record<string, unknown>)) {
          const id = Number(key);
          if (!Number.isInteger(id) || id <= 0 || !Array.isArray(value)) continue;
          locations.set(id, value as StockLocationLevel[]);
        }
      }
    }
  }

  return { ok: true, stock, locations, configured };
}

/** The single warehouse name to show against a product, or undefined.
 *
 *  ⚠️ ONLY WHEN THERE IS EXACTLY ONE, AND IT IS NAMED. The quantity this app
 *  shows is a SUM across every location the provider reported, so naming one
 *  of several beside it would attribute the whole total to a warehouse that
 *  holds part of it. Two locations, or one the source did not name, leave the
 *  label off -- the pill then reads "N available" with no place attached,
 *  which is exactly what is known. */
export function singleWarehouseName(rows: StockLocationLevel[] | undefined): string | undefined {
  if (!rows || rows.length !== 1) return undefined;
  return rows[0].location ?? undefined;
}

/** Copy `materials` with stock levels attached from a getStock result.
 *
 *  Pure, so the absent-is-not-zero rule can be tested directly rather than
 *  inferred from a screen. Three outcomes per line, and the middle one is the
 *  one that used to be impossible to express:
 *
 *    * no productId (a free-text line)      -> stock stays undefined (UNKNOWN)
 *    * productId with no key in the map     -> stock stays undefined (UNKNOWN)
 *    * productId with a key, INCLUDING 0    -> stock is that number
 *
 *  ⚠️ A STATED 0 IS COPIED THROUGH AS 0. It is not converted to undefined on
 *  the way past -- that conversion is precisely what made "we have none"
 *  unsayable, and Material.stock has always had room for it. */
export function withStockLevels(
  materials: Material[],
  stock: Map<number, number> | undefined,
  locations?: Map<number, StockLocationLevel[]>,
): Material[] {
  if (!stock) return materials;
  return materials.map((m) => {
    if (m.productId === undefined || !stock.has(m.productId)) return m;
    return {
      ...m,
      stock: stock.get(m.productId),
      warehouse: singleWarehouseName(locations?.get(m.productId)) ?? m.warehouse,
    };
  });
}

/** Create ONE product in the master. Sales role, same-origin enforced.
 *
 *  `{ product: spec }` mirrors createCustomer's `{ customer: spec }` -- the
 *  same flat envelope this API uses for every create.
 *
 *  ⚠️ THE SPEC IS AN INPUT PAYLOAD, NOT A CatalogProduct. It carries only what
 *  the backend's allowlist accepts, and only the keys that have a real value:
 *  an absent description or weight is OMITTED, never sent as "" or 0. The
 *  caller builds it (see NewProductModal) because the caller is the only thing
 *  that knows the difference between "the user left this blank" and "the user
 *  typed zero".
 *
 *  ⚠️ PROVENANCE IS NOT OURS TO CLAIM. `source` and `created_by_uid` are
 *  stamped server-side from the code path and the session; neither is in the
 *  backend's input allowlist, so sending one is dropped. Do not add them here.
 *
 *  ⚠️ IT NEVER FALLS BACK AND NEVER INVENTS AN ID. A failure returns
 *  {ok: false} and no product -- the returned record is the only proof a row
 *  exists, exactly as quote_id is on the save path. */
export async function createProduct(
  spec: Record<string, unknown>,
): Promise<{ ok: boolean; product?: CatalogProduct; error?: string }> {
  const call = await backendPost(ENDPOINTS.createProduct, { product: spec });
  if (!call.ok) return { ok: false, error: call.error };
  if (!call.body?.product) {
    return {
      ok: false,
      error: "The product was not returned, so this app cannot confirm it was created.",
    };
  }
  return { ok: true, product: toCatalogProduct(call.body.product) };
}

/** Create MANY products in ONE transaction, or prove that it would.
 *
 *  ⚠️ TWO RESPONSE SHAPES, AND THEY ARE NOT INTERCHANGEABLE. A dry run answers
 *  `{ok, validated, would_create}` and DELIBERATELY carries no products array:
 *  the records it made are about to stop existing, so their ids would be
 *  phantoms. A real import answers `{ok, created, products}`. Each branch below
 *  therefore proves a DIFFERENT thing, and neither looks for the other's field.
 *
 *  ⚠️ A COUNT IS NOT PROOF OF A ROW. On the real path the returned records are
 *  the only evidence anything persisted -- the same rule createProduct follows
 *  for `product` and the save path for `quote_id` -- so a `created: 3` with no
 *  products array is reported as a FAILURE, not as a success with a gap.
 *
 *  ⚠️ `validate_only` GOES OVER THE WIRE AS A REAL BOOLEAN. The backend refuses
 *  anything else outright rather than coercing, because bool("false") is True
 *  and the dangerous direction here is a dry run that quietly reads as False
 *  and COMMITS. The parameter is typed boolean so a caller cannot pass "true".
 *
 *  It never falls back and never invents a row, exactly like every other call
 *  in this file. */
export async function importProducts(
  specs: Record<string, unknown>[],
  validateOnly: boolean,
): Promise<{ ok: boolean; created?: CatalogProduct[]; wouldCreate?: number; error?: string }> {
  const call = await backendPost(ENDPOINTS.importProducts, {
    products: specs,
    validate_only: validateOnly,
  });
  if (!call.ok) {
    // ⚠️ A TIMEOUT MEANS DIFFERENT THINGS ON THE TWO PATHS, so it says so.
    // The real import may have reached Odoo and committed before this client
    // stopped waiting -- genuinely ambiguous, exactly as a timed-out quote
    // save is. A DRY RUN cannot have left anything behind whatever happened,
    // because that path rolls back rather than merely declining to commit, so
    // telling someone to go and check would send them looking for nothing.
    const error = call.timedOut && !validateOnly
      ? `${call.error} The products may or may not have been imported — check the catalog before retrying.`
      : call.error;
    return { ok: false, error };
  }

  if (validateOnly) {
    // The count is the WHOLE answer on this path, so it is the thing proved.
    // Nothing is read off `products` here -- the endpoint does not send one.
    if (typeof call.body?.would_create !== "number") {
      return {
        ok: false,
        error: "The dry run came back without a row count, so this app cannot confirm the file was checked.",
      };
    }
    return { ok: true, wouldCreate: call.body.would_create };
  }

  if (!Array.isArray(call.body?.products)) {
    return {
      ok: false,
      error: "The imported products were not returned, so this app cannot confirm they were created.",
    };
  }
  return { ok: true, created: call.body.products.map(toCatalogProduct) };
}

/** The ACTIVE brand and category names a product may be classified with.
 *
 *  Flat string arrays, as sent -- names, not ids, and not {id, name}.
 *
 *  ⚠️ RETURNED UN-INTERSECTED, DELIBERATELY. The backend sends its whole
 *  active list, which can legitimately contain a name this build's closed
 *  ProductBrand / ProductCategory unions have never seen. Narrowing it HERE
 *  would silently drop it, and the caller would never learn the name existed.
 *  The intersect belongs to the form that offers the choice, which is the only
 *  place that can also SAY why a name is missing -- see NewProductModal. */
export async function getTaxonomy(): Promise<{
  ok: boolean;
  brands?: string[];
  categories?: string[];
  error?: string;
}> {
  const call = await backendGet(ENDPOINTS.taxonomy);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.brands) || !Array.isArray(call.body?.categories)) {
    return {
      ok: false,
      error: "The taxonomy came back in a shape this app does not recognise.",
    };
  }
  return {
    ok: true,
    brands: call.body.brands.map(String),
    categories: call.body.categories.map(String),
  };
}

// WHETHER STORED ENGINE NUMBERS MAY BE SHOWN AS LIVE.
//
// Reopening a quote hands back numbers the engine computed at save time. They
// are only still true if they belong to the inputs now on screen -- so this
// proves that before anything reaches a `live` column, and refuses when it
// cannot. A refusal leaves the columns blank with the recompute prompt, which
// is the behaviour reopen already had; the cost of being wrong in the other
// direction is a validated badge over a number that is not.
//
// Five conditions, all required:
//
//  1. There are rows, and every one has an entry. A partially-priced table
//     would blend engine and placeholder figures into one total.
//  2. unit_sp / extended_sp are real numbers.
//  3. gp_pct_snapshot > 0 -- the marker that the engine RAN. A line saved
//     against no pricing config stores all-zero outputs, and 0 is not
//     undefined, so nothing downstream would notice: the document's own
//     unpriced check passes and it prints Rs 0.00 as an engine price. The
//     snapshot is the only field that distinguishes "priced at zero" from
//     "never priced", because a real config always writes a GP into it.
//  4. and 5. The signature of the rows equals the signature rebuilt from the
//     backend's own echoed inputs, mode included. Both derive from one
//     response but by different routes -- one through savedQuoteToMaterials
//     and recomputeAll, the other straight off the wire -- so a disagreement
//     means the rows on screen are not the rows these prices belong to.
export function livePricingCorrespondsTo(
  rows: Material[],
  live: Map<number, LivePricedLine>,
  detail: SavedQuoteDetail,
  mode: "domestic" | "import",
): boolean {
  if (rows.length === 0) return false;
  if (rows.length !== detail.lines.length) return false;

  for (const row of rows) {
    const priced = live.get(row.lineNo);
    if (!priced) return false;
    if (!Number.isFinite(priced.unit_sp)) return false;
    if (!Number.isFinite(priced.extended_sp)) return false;
    // See (3) above. Strictly greater than zero: a stored 0 means the
    // compute zeroed this line, not that someone chose a GP of nothing.
    if (!(priced.gp_pct_snapshot > 0)) return false;
  }

  // The saved side is fingerprinted with the mode the quote was SAVED in,
  // not the mode the caller is asking about. Using the caller's mode on both
  // sides would put the same string in both halves and make the mode part of
  // the comparison decorative -- a screen in import mode would happily accept
  // prices computed by the domestic chain.
  // BOTH SIDES GET THE SAME id->key MAP, and that is the whole reason it is
  // built here rather than inside the signature. The rows side carries keys
  // that resolveLineShipmentKey already produced; the saved side carries raw
  // shipment ids. Without the map one side would fingerprint "ship-11" and
  // the other "", and every reopened assigned quote would look stale and
  // blank its live columns -- the same trap the saved-mode fix hit.
  const shipmentKeyById = new Map(
    detailToQuoteShipments(detail)
      .filter((s) => s.id !== undefined)
      .map((s) => [s.id as number, s.key] as const),
  );

  return (
    pricingSignatureOf(rows, mode) ===
    pricingSignatureOfSavedLines(
      detail.lines, detail.pricing_mode ?? "domestic", shipmentKeyById,
    )
  );
}

// WHAT A SAVE JUST TOLD US ABOUT THE LINES IT WROTE.
//
// The save response is a PRICED result, not a receipt: `_serialize_saved_quote`
// reuses the same `_serialize_line` the reopen read uses, so every line comes
// back with the allocator's `freight_pct` and the engine's outputs. This app
// dropped all of it -- the two save handlers used `response.lines` positionally
// for adoptSavedShipments and nothing else -- so an allocated share only ever
// reached the grid via a full reopen.
//
// ⚠️ THE SHARE AND THE PRICES MOVE TOGETHER, AND SPLITTING THEM IS THE BUG.
// `freightPct` is a term of the pricing fingerprint, so writing the share onto
// a row moves `pricingSignatureOf(rows)`. Advance the priced-signature ref
// without the engine numbers that share produced and the ref becomes a lie:
// the live columns would keep showing figures priced at the row's OLD freight
// (the stateless pricing call sends `freightPct ?? 0`, which is 0 on an
// assigned line) underneath a fingerprint claiming the new one. Adopt the
// share alone and skip the ref, and the guard blanks a freshly-saved quote.
// Neither half is correct on its own -- so this returns rows, live prices and
// the signature as ONE answer, exactly as restoreFromSavedQuote does for the
// reopen path.
//
// IMPORT ONLY. A domestic save serializes no `freight_pct` at all, so there is
// nothing to adopt and the domestic path stays byte-for-byte what it was.
export function adoptSavedPricing(
  response: { lines?: BackendSavedLine[] },
  // The rows AS SENT. Positionally matched to response.lines -- the same and
  // only wire correlation adoptSavedShipments uses. A second rule for the same
  // join is how the two start disagreeing about which line is which.
  sentRows: Material[],
  // The rows as they stand NOW, which is not always what was sent: an edit
  // during an in-flight save leaves them different. See the mid-flight guard.
  currentRows: Material[],
  // The shipments AFTER adoptSavedShipments has run, because those carry the
  // PANEL's keys. Deriving the map from the raw response instead would produce
  // "ship-33" for a shipment the rows still call "new-1", and the
  // correspondence check below would reject every newly-created shipment.
  shipments: QuoteShipment[],
  mode: "domestic" | "import",
): {
  rows: Material[];
  live: Map<number, LivePricedLine>;
  signature: string;
  // False means "change nothing" -- the caller must not touch rows, live
  // prices or the ref. Every refusal below lands here rather than throwing:
  // a save that really happened must not be reported as a failure because
  // the screen could not take its numbers.
  adopted: boolean;
} {
  const nothing = { rows: currentRows, live: new Map<number, LivePricedLine>(), signature: "", adopted: false };

  if (mode !== "import") return nothing;
  // FAIL CLOSED, the same reading adoptSavedShipments takes: an older backend
  // or an omitted field means we cannot prove which line is which, and
  // guessing is what loses data quietly.
  if (!Array.isArray(response.lines)) return nothing;
  if (response.lines.length !== sentRows.length) return nothing;

  // ⚠️ THE MID-FLIGHT EDIT GUARD, and it is not optional. The handler closes
  // over the rows it SENT; the user can edit a cell while the request is out,
  // and the autosave's own ticket does not count that as superseding anything
  // (no newer save has begun -- the debounce is still counting). Writing the
  // sent rows back would revert that edit, silently, on a save that reported
  // success. When the two disagree we adopt NOTHING: the share is stale for
  // the edited quote anyway, and the next save brings a fresh one.
  if (pricingSignatureOf(currentRows, mode) !== pricingSignatureOf(sentRows, mode)) {
    return nothing;
  }

  // The wire join stays positional; lineNo is only how this app identifies its
  // OWN rows, which it already does everywhere (livePricing is keyed by it).
  const freightByLine = new Map<number, number>();
  // THE RATE THE SCHEDULE SUPPLIED (fx-rate-source-marker). A line the backend
  // resolved comes back rate_source "schedule"; the screen takes its rate and
  // marker so what is shown is what was stored. A line the person STATED is
  // never overwritten here -- it was sent, and it came back unchanged.
  const scheduleRateByLine = new Map<number, number | undefined>();
  // THE DUTY THE SCHEDULE SUPPLIED (line-sources, 2026-09-30) -- the same
  // adoption, for the same reason. A line with an HSN and no typed duty is
  // saved with the schedule's duty; the row holds none until this hands it
  // over, and without it the rows' fingerprint (duty 0) never matches the
  // wire's (duty 11) and NOTHING from the save is adopted -- not the freight
  // share, not the prices.
  const scheduleDutyByLine = new Map<number, number>();
  const pricedByLine = new Map<number, LivePricedLine>();
  response.lines.forEach((line, index) => {
    const sent = sentRows[index];
    if (!sent) return;
    pricedByLine.set(sent.lineNo, line as LivePricedLine);
    if (line.rate_source === "schedule" && sent.rateSource !== "stated") {
      scheduleRateByLine.set(sent.lineNo, zeroRateMeansNoRate(line.conversion_rate));
    }
    const savedDuty = line as { duty_source?: string | null; duty_pct?: number };
    if (savedDuty.duty_source === "schedule" && sent.dutySource !== "stated"
        && typeof savedDuty.duty_pct === "number") {
      scheduleDutyByLine.set(sent.lineNo, savedDuty.duty_pct);
    }
    // ONLY AN ASSIGNED LINE. An unassigned one keeps the percentage the user
    // typed -- it is an input, and the backend echoes it straight back.
    if (!sent.shipmentKey) return;
    // ⚠️ `!== undefined`, NEVER a truthy test. The serializer returns a raw
    // Float, so a line allocated a zero share comes back as an explicit 0.0.
    // Dropping it as falsy would leave `freightPct` undefined, which the grid
    // reads as "not allocated yet" and renders as a dash -- turning the
    // engine's answer of zero into no answer at all.
    if (typeof line.freight_pct !== "number") return;
    freightByLine.set(sent.lineNo, line.freight_pct);
  });

  let changed = false;
  const rows = currentRows.map((r) => {
    let next = r;
    const share = freightByLine.get(r.lineNo);
    if (share !== undefined && r.freightPct !== share) {
      next = { ...next, freightPct: share };
    }
    if (scheduleDutyByLine.has(r.lineNo)) {
      const duty = scheduleDutyByLine.get(r.lineNo) as number;
      if (r.dutyPct !== duty || r.dutySource !== "schedule") {
        next = { ...next, dutyPct: duty, dutySource: "schedule" };
      }
    }
    if (scheduleRateByLine.has(r.lineNo)) {
      const rate = scheduleRateByLine.get(r.lineNo);
      if (r.conversionRate !== rate || r.rateSource !== "schedule") {
        next = { ...next, conversionRate: rate, rateSource: "schedule" };
      }
    }
    if (next !== r) changed = true;
    return next;
  });

  // NOTHING MOVED -> ADOPT NOTHING, and this is what stops the autosave loop
  // rather than merely tidying the result. The second tick sends rows that
  // already carry the share, gets the same answer back, and must produce no
  // state change at all: `rows` is an autosave dependency, so a fresh array
  // every 1.5s would re-arm the timer forever.
  if (!changed) return nothing;

  const signature = pricingSignatureOf(rows, mode);

  // THE BOTH-SIDES PROOF, on the same terms as livePricingCorrespondsTo. One
  // side is the rows we are about to show, the other is read straight off the
  // wire -- if they disagree, the numbers do not belong to these inputs and
  // nothing here is safe to display.
  const shipmentKeyById = new Map(
    shipments
      .filter((s) => s.id !== undefined)
      .map((s) => [s.id as number, s.key] as const),
  );
  if (signature !== pricingSignatureOfSavedLines(response.lines, mode, shipmentKeyById)) {
    return nothing;
  }

  return { rows, live: pricedByLine, signature, adopted: true };
}

// THE CLIENT KEY FOR A SHIPMENT READ BACK OFF THE DATABASE.
//
// The key this app sent on the way in is NOT stored -- it names nothing
// outside the request that used it -- so a reopened shipment arrives with only
// its id. Something has to mint a key again, because lines reference a
// shipment by key and by nothing else.
//
// DERIVED FROM THE ID, so it is stable: reopen the same quote twice and every
// line lands on the same key both times. A counter or a random id would
// produce a different answer per load, which is fine until two code paths
// disagree about which shipment a line is on.
export function shipmentKeyForId(id: number): string {
  return `ship-${id}`;
}

const ALLOCATION_BASES: readonly AllocationBasis[] = [
  "By Value", "By Weight", "By Volume", "Equal Split",
];

// The key for a shipment the PANEL just made, which has no id yet.
//
// A SEPARATE PREFIX FROM shipmentKeyForId, deliberately. Both mint keys into
// the same namespace and a collision would silently make two shipments one:
// "ship-" is reserved for a database id, so a counter here can never land on
// a key that names a real record.
//
// The counter is module-level and never resets. It does not need to be
// durable -- a key means nothing outside the one request that used it -- only
// unique among the shipments held on screen at once.
let newShipmentSeq = 0;
export function newShipmentKey(): string {
  newShipmentSeq += 1;
  return `new-${newShipmentSeq}`;
}

/** A blank shipment for the panel's "Add shipment", in the quote's currency.
 *
 *  THE CURRENCY IS INHERITED, NOT CHOSEN. Freight becomes a percentage of the
 *  goods value in the supplier's currency, so a bill denominated in anything
 *  else is wrong by the whole exchange rate — the backend refuses a mismatch,
 *  and offering a picker here would invite exactly the refusal.
 *
 *  A ZERO BILL is the honest starting state: the record exists, and nobody has
 *  said what it cost yet. It allocates nothing until someone types a figure. */
export function newQuoteShipment(currency: string): QuoteShipment {
  return {
    key: newShipmentKey(),
    name: "New shipment",
    mode: "sea",
    freightTotal: 0,
    currency,
    allocationBasis: "By Value",
  };
}

// The panel's shipments, re-seeded from what a save actually wrote.
//
// THE IDS ARE THE POINT. A shipment the panel made has no id, and the backend
// matches an update on the id alone — so re-sending an id-less entry CREATES a
// second record and deletes the first. Adopting the issued ids is what makes
// the next save an update instead of that churn.
//
// ⚠️ THE KEYS ARE PRESERVED WHERE THERE IS EVIDENCE FOR IT, and that half is
// newer than the ids. This used to replace wholesale, which RENAMED every key:
// a shipment the panel called "new-1" came back as "ship-5". Once a line
// carries a shipmentKey (BF5c-2b) that rename orphans it — the row goes on
// naming "new-1" while nothing defines it any more. The backend then refuses
// the next save outright, and since BF5c-2c the table self-heals a dangling
// key by CLEARING it, which turns that loud refusal into a silent unassign:
// the save succeeded, the screen shows the line unassigned, and Odoo has it
// assigned. Not renaming a key that a row still holds is what removes both.
//
// CORRELATION IS ID-DRIVEN, from two independent sources, neither of which is
// a guess:
//
//   1. A panel entry that ALREADY carries an id names that record itself.
//      Sound on its own and needs no lines at all — this is what keeps a
//      reopened "ship-11" stable across every save.
//
//   2. The response's own lines. `lines[i].shipment_id` is the id the saved
//      row i landed on, and `sentLines[i].shipmentKey` is the key this app
//      sent for that same row — so the pair proves which panel key the
//      backend turned into which id. Line order is reliable: the save
//      replaces lines wholesale from the payload in one batched create, and
//      anixify.quote.line._order is "quote_id, id", which is the same
//      positional assumption savedQuoteToMaterials already makes on reopen.
//
// NOT BY NAME (two shipments can share one) and NOT BY ZIPPING THE SHIPMENT
// ARRAYS. That last one looks like it would work and does not: the response is
// ordered by anixify.shipment._order ("quote_id, id"), while the payload is in
// panel order, and a newly created record always gets a HIGHER id than the
// ones already stored. Panel [new-1, ship-5] comes back [id 5, id 9] — the
// orders disagree, and that is the ordinary case of adding a shipment to a
// quote that already had one, not a corner.
//
// A returned record that nothing correlates to gets shipmentKeyForId as
// before. By definition no row can be orphaned by that: if no line named it,
// no line can be left naming it.
// IS THIS THE SAME SHIPMENT LIST, BY CONTENT?
//
// Used only to decide whether adoptSavedShipments may hand back the array it
// was given instead of a fresh one. That identity matters far out of
// proportion to its size: `shipments` is a dependency of the quote screen's
// debounced autosave, so a new array with identical content reads as a change,
// re-arms the timer, and saves again -- which produces another new array. The
// quote then rewrote all of its lines against the backend roughly every two
// seconds with nobody touching it. Measured at 11 saves in 25s, on a quote
// holding no shipments at all.
//
// ⚠️ COMPARED OVER THE UNION OF OWN KEYS, NOT AN ENUMERATED FIELD LIST. Every
// field of QuoteShipment is a primitive -- string, number, or a string union
// -- with no nested object or array anywhere, so `!==` per key is exact rather
// than an approximation. Taking the union of BOTH sides' keys is what makes it
// complete: a field present on one and absent on the other compares undefined
// against a value and reads as changed, and a field added to the type later is
// compared without anyone remembering to update this.
//
// An enumerated list was the alternative and is worse in the one way that
// matters here: a field it forgot would be silently swallowed. The panel would
// keep showing a stale value AND the next save would send it back, so the
// backend's answer would be discarded rather than merely undisplayed. Length
// alone is far worse still -- a rename and an id-attach both keep the length.
function sameShipment(a: QuoteShipment, b: QuoteShipment): boolean {
  const fields = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const field of fields) {
    if (a[field as keyof QuoteShipment] !== b[field as keyof QuoteShipment]) return false;
  }
  return true;
}

/** Content equality for the whole list, position by position.
 *
 *  ORDER IS PART OF THE CONTENT: the panel renders in array order, so a
 *  reordered list is a changed list even when the same records are in it.
 *
 *  ⚠️ THE AUTOSAVE LOOP THIS EXISTS TO STOP IS NOT COVERED BY ANY PAGE TEST.
 *  The debounced autosave never fires under jsdom -- zero /save_quote calls in
 *  a 20s window on a fully mounted quote -- so a "does not re-save" assertion
 *  is vacuously green there, with or without this function. The unit pins
 *  below it cover the MECHANISM (identity stability and convergence); the
 *  browser pass is the only thing that guards the loop itself. Do not assume
 *  the suite protects it. */
function sameShipmentList(a: QuoteShipment[], b: QuoteShipment[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((s, i) => sameShipment(s, b[i]));
}

export function adoptSavedShipments(
  response: { shipments?: BackendShipment[]; lines?: BackendSavedLine[] },
  current: QuoteShipment[],
  // The rows as they were SENT, positionally matching response.lines. Optional
  // for the two-argument callers that predate the key-preserving half and have
  // no rows to protect; passing it is what turns source 2 on.
  sentLines?: Material[],
): QuoteShipment[] {
  // A response that says nothing about shipments leaves the panel alone --
  // the same sticky reading the save payload itself uses. An older backend,
  // or a save that sent no block, must not blank what is on screen.
  if (!Array.isArray(response.shipments)) return current;

  // FAIL CLOSED. The caller has handed us rows -- so a key may well be in use
  // -- but the response carries no lines to correlate them against, which an
  // older backend or an omitted field would do. Renaming blind is the one
  // outcome that loses data silently, so nothing is adopted at all: the panel
  // keeps its keys and its id-less entries stay id-less.
  //
  // The cost is a duplicate record on the next save, which is loud, visible in
  // Odoo and recoverable. The cost of the alternative is a line that quietly
  // unassigns itself after a save that reported success. Not a close call.
  if (sentLines !== undefined && !Array.isArray(response.lines)) return current;

  const saved = detailToQuoteShipments(response);

  // Which panel key each returned id belongs to. Absent means "nothing named
  // this record", which is the mint-a-fresh-key case.
  const keyById = new Map<number, string>();

  // SOURCE 1, first, so an entry that already knows its own id wins over
  // anything a line implies. A row can be mid-edit; an adopted id cannot.
  for (const s of current) {
    if (s.id !== undefined && !keyById.has(s.id)) keyById.set(s.id, s.key);
  }

  // SOURCE 2. Only fills ids source 1 did not, which is exactly the set of
  // records the backend has just created.
  if (sentLines !== undefined && Array.isArray(response.lines)) {
    response.lines.forEach((line, index) => {
      const sentKey = sentLines[index]?.shipmentKey;
      const id = line.shipment_id;
      if (!sentKey || id === undefined || id === null) return;
      if (!keyById.has(id)) keyById.set(id, sentKey);
    });
  }

  const next = saved.map((s) => {
    const preserved = keyById.get(s.id);
    // shipmentKeyForId's answer is already on `s`; overriding it is the whole
    // of the preservation, and every other field stays as the backend stored
    // it.
    return preserved === undefined ? s : { ...s, key: preserved };
  });

  // NOTHING ACTUALLY MOVED -> HAND BACK WHAT WE WERE GIVEN.
  //
  // The caller does `setShipments(prev => adoptSavedShipments(...))`, so
  // returning the same reference makes React bail out instead of re-rendering
  // with a new-but-identical array. That is what stops the autosave loop: the
  // screen's `shipments` dependency only changes when the shipments do.
  //
  // ⚠️ THIS IS AN IDENTITY OPTIMISATION, NEVER A CONTENT ONE. Every real
  // transition still produces a new array and is still applied -- a rename, a
  // changed bill, a deletion, and in particular the id-attach that BF5c-2c-pre
  // depends on ("new-1" with no id becoming "new-1" with id 9 differs on `id`,
  // so it is a change and the panel sees it). Swallowing that would leave the
  // panel id-less and make the NEXT save create a duplicate.
  //
  // It also leaves the BF5c-2c reconcile intact: a genuine deletion makes the
  // list shorter, which is a length difference and therefore a change, so
  // MaterialTable still receives the shortened list and still clears the lines
  // that named the deleted shipment.
  //
  // And it does not weaken BF5c-2e-i: a superseded response is dropped before
  // reaching here, by the caller's own generation check.
  return sameShipmentList(next, current) ? current : next;
}

// The quote's REAL shipments, as the panel's own objects.
//
// This is the id<->key structure the SPA has never had. It goes one way here
// (id -> key, minted) and the other in resolveLineShipmentKey below; between
// them a reopened quote can be re-saved with `id` on each shipment and `key`
// on each line, which is exactly what the backend's update path matches on.
//
// Defaults rather than rejection for the two required fields a serialized
// shipment can legitimately be missing: `allocation_basis` is required on the
// record so it is always present in practice, and a bill of zero is a real
// state (carrier-included freight). Nothing here can invent a currency,
// though -- an absent one stays empty and the caller decides, because guessing
// the quote's own currency is the ~83x trap the backend guards against.
// Structurally typed rather than taking SavedQuoteDetail, because the SAVE
// response carries the same `shipments` array from the same backend
// serializer — and adopting the ids off a save is the same job as rebuilding
// on reopen. Narrowing this to the read type would have meant a second copy
// of the mapping for the write path, which is how the two drift.
export function detailToQuoteShipments(
  detail: { shipments?: BackendShipment[] },
): QuoteShipment[] {
  if (!Array.isArray(detail.shipments)) return [];

  return detail.shipments.map((s) => ({
    key: shipmentKeyForId(s.id),
    id: s.id,
    name: s.name ?? "",
    ...(s.origin ? { origin: s.origin } : {}),
    mode: s.mode ?? "sea",
    // The stored total, however it was arrived at -- entered, or resolved by
    // the model from a rate card. Re-sending THIS is what stops the card
    // recomputing over a hand-corrected figure on the next save.
    freightTotal: s.freight_total ?? 0,
    currency: s.currency ?? "",
    allocationBasis: ALLOCATION_BASES.includes(s.allocation_basis as AllocationBasis)
      ? (s.allocation_basis as AllocationBasis)
      : "By Value",
    ...(s.gross_weight ? { grossWeight: s.gross_weight } : {}),
    ...(s.volume ? { volume: s.volume } : {}),
    ...(s.container_count ? { containerCount: s.container_count } : {}),
    ...(s.trip_count ? { tripCount: s.trip_count } : {}),
    ...(s.rate ? { rate: s.rate } : {}),
    ...(s.rate_basis ? { rateBasis: s.rate_basis } : {}),
    ...(s.min_charge ? { minCharge: s.min_charge } : {}),
    ...(s.notes ? { notes: s.notes } : {}),
  }));
}

// Which panel shipment a reopened line belongs to, as a key.
//
// UNDEFINED FOR AN UNASSIGNED LINE, which is the ordinary case and not a
// failure. Also undefined when the id names a shipment this quote's response
// did not include: that should not happen -- the backend serializes both from
// the same record -- and inventing a key for it would attach the line to a
// shipment nobody can see, so it reads as unassigned instead.
export function resolveLineShipmentKey(
  line: { shipment_id?: number | null },
  shipments: QuoteShipment[],
): string | undefined {
  if (line.shipment_id === undefined || line.shipment_id === null) return undefined;
  return shipments.find((s) => s.id === line.shipment_id)?.key;
}

// The SidePanel's settings as they were saved, or undefined.
//
// Validated field by field rather than cast. What comes back is a JSON blob
// the backend stores opaquely and never inspects, so this is the only place
// its shape is checked at all -- and a malformed one must fall back to the
// default context. Anything unexpected returns undefined and the caller keeps
// what it had.
//
// ⚠️ IT VALIDATES WHAT IT READS AND NOTHING ELSE. This used to require
// `Array.isArray(ctx.shipments)` and check every row in it, back when that
// array drove the placeholder freight allocation. BF5d-3 deleted the array;
// leaving the requirement would have been a silent wrong number of the worst
// kind, because it fails on the QUOTES SAVED AFTER THE DEPLOY and not before:
// an older context still carries the key and passes, while a context written
// from now on has no `shipments` at all, fails here, and returns undefined --
// at which point the caller falls back to the DEFAULT context and the quote
// reopens showing the mock FX rate, the mock risk buffer and a zero
// negotiation buffer in place of the ones that were saved with it.
//
// The general rule this is an instance of: a stored blob may carry keys this
// version no longer knows about, so validate the fields you are about to
// return and ignore the rest. Pinned by savedQuoteToPricingContext's own
// reopen test -- restoring the guard fails it.
export function savedQuoteToPricingContext(
  detail: SavedQuoteDetail,
): PricingContext | undefined {
  const raw = detail.pricing_context;
  if (!raw || typeof raw !== "object") return undefined;

  const ctx = raw as Partial<PricingContext>;
  if (!ctx.fx || typeof ctx.fx !== "object") return undefined;
  if (!Number.isFinite(ctx.fx.customsRate)) return undefined;
  if (!Number.isFinite(ctx.fx.riskBufferPct)) return undefined;
  if (!Number.isFinite(ctx.negotiationBuffer)) return undefined;

  return {
    fx: {
      customsRate: ctx.fx.customsRate,
      // Carried for provenance -- it names which published rate was chosen.
      // It enters no calculation; computeEffectiveRate reads only the rate
      // and the buffer.
      effectiveFrom: ctx.fx.effectiveFrom ?? "",
      riskBufferPct: ctx.fx.riskBufferPct,
    },
    negotiationBuffer: ctx.negotiationBuffer,
  };
}

// WHAT A SAVED QUOTE'S STATUS PILL SAYS.
//
// Two different facts, and the pill has to respect both. is_draft is whether
// the quote has been FINALISED (issued a number). `state` is where it stands
// commercially -- draft/sent/won/lost -- and the addon never writes it, so a
// finalised quote arrives state="draft". Showing state alone said "Draft"
// under a numbered quote. Display only: no state value is written or invented.
//
// is_draft absent (a backend predating the flag) keeps the old "Draft" rather
// than claiming a finalisation nobody reported.
export function quoteStatusFor(
  isDraft: boolean | undefined,
  state: string | undefined,
  // quote-won-lost: the backend's outcome, when it sent one. It decides.
  outcome?: string | null,
): Quote["status"] {
  if (isDraft !== false) return "Draft";
  if (outcome === "open") return "Open";
  if (outcome === "won") return "Won";
  if (outcome === "lost") return "Lost";
  if (outcome === "expired") return "Expired";
  if (outcome === "superseded") return "Superseded";
  if (state === "sent") return "Sent";
  if (state === "won") return "Won";
  if (state === "lost") return "Lost";
  return "Finalised";
}

export function savedQuoteToQuote(detail: SavedQuoteDetail | null): Quote | undefined {
  if (!detail) return undefined;
  return {
    quoteNo: detail.name,
    ownerUid: detail.owner_uid ?? null,
    endClient: detail.partner_name ?? "",
    customer: savedQuoteToCustomer(detail),
    customerSnapshot: toCustomerSnapshot(detail.customer_snapshot),
    value: detail.grand_total,
    margin: 0,
    // Was a hard-coded "Draft", so every reopened quote -- finalised ones
    // included -- read Draft in the header. See quoteStatusFor.
    status: quoteStatusFor(
      detail.is_draft, detail.state,
      (detail as { outcome?: BackendOutcome | null }).outcome?.status,
    ),
    // ⚠️ THE STORED valid_until, set at finalise (quote-validity). This used
    // to read `detail.date` -- the QUOTE date -- so every reopened quote
    // printed "valid until <the day it was written> (0 days)". "" = none.
    validUntil: (detail as { valid_until?: string | null }).valid_until ?? "",
    // project-id (17.0.1.55.0): the stored project, "" when none.
    projectId: (detail as { project_ref?: string | null }).project_ref ?? "",
    projectName: (detail as { project_name?: string | null }).project_name ?? "",
    // warehouse-master: the stored dispatch warehouse, null when none.
    warehouseId: (detail as { warehouse?: { id: number } | null }).warehouse?.id ?? null,
    warehouseLabel: (detail as { warehouse?: { label: string } | null }).warehouse?.label ?? null,
    copiedFrom: (detail as { copied_from?: { id: number; name: string } | null }).copied_from ?? undefined,
    approval: toQuoteApproval((detail as { approval?: BackendApproval | null }).approval),
    outcome: toQuoteOutcome((detail as { outcome?: BackendOutcome | null }).outcome),
    revision: toQuoteRevision((detail as { revision?: BackendRevision | null }).revision),
    tax: toQuoteTax((detail as { tax?: BackendTax | null }).tax),
    // gp-per-quote: the stored GP % (the quote's pricing config), as sent.
    gpPct: typeof detail.gp_pct === "number" ? detail.gp_pct : undefined,
    createdAt: detail.date ?? "",
    odooQuoteId: detail.quote_id,
    // Read off the response, not guessed. This is what makes a reopened
    // import quote render its own columns; without it the table falls back to
    // domestic and shows an import quote's lines under domestic headings.
    // Undefined is left as undefined — every reader already treats an absent
    // mode as domestic, same as the backend does.
    pricingMode: detail.pricing_mode,
    // Restored so a reopened quote shows the currency it was saved with.
    // Left undefined when the backend recorded none, rather than defaulted --
    // the whole point of the field is that unknown stays unknown.
    supplierCurrency: detail.supplier_currency ?? undefined,
  };
}

// The engine values for those same rows, keyed by the lineNo assigned above
// so MaterialTable's live columns light up exactly as they do after a live
// recalculation. Same index order as savedQuoteToMaterials, deliberately —
// part numbers repeat within a quote, so keying on them would collide.
//
// Both chains are copied through unconditionally, which is not laziness: the
// backend serializes ONE of them per line and omits the other entirely (see
// _serialize_line — an import line has no cost_with_sv, a domestic line has
// no landing_cost), so the absent side arrives as undefined and stays
// undefined. Branching on mode here would re-derive something the response
// already states, and get it wrong the moment the two disagree.
export function savedQuoteToLivePricing(
  detail: SavedQuoteDetail,
): Map<number, LivePricedLine> {
  const byLine = new Map<number, LivePricedLine>();
  detail.lines.forEach((line, index) => {
    byLine.set(index + 1, {
      // Domestic. inbound_freight / cost_with_inbound_freight / total are NOT
      // copied because _serialize_line does not return them on a domestic
      // line — only ibf_pct and cost_with_sv. They stay undefined rather than
      // being reconstructed from ibf_pct here, which would be this layer
      // recomputing a step the engine owns.
      cost_with_sv: line.cost_with_sv,
      // Import chain, in full — every intermediate the serializer returns, so
      // a reopened import quote has the same detail a fresh price does.
      freight_value: line.freight_value,
      freight_plus_unit: line.freight_plus_unit,
      value_in_inr: line.value_in_inr,
      landing_cost: line.landing_cost,
      landing_plus_variance: line.landing_plus_variance,
      total_landing_cost: line.total_landing_cost,
      cf: line.cf,
      // Both.
      variance_pct_snapshot: line.variance_pct_snapshot,
      gp_pct_snapshot: line.gp_pct_snapshot,
      unit_sp: line.unit_sp,
      extended_sp: line.extended_sp,
      extd_price: line.extd_price,
    });
  });
  return byLine;
}

// ---- Company settings ---------------------------------------------------
//
// Branding and document identity, stored once in Odoo. Not pricing —
// nothing here touches the engine.
//
// THE LOGO IS STICKY, and the payload encodes that in three states:
//   omit logo_base64   -> keep whatever is stored
//   logo_base64: null  -> clear it
//   logo_base64: "..." -> replace it
// Omission is the default on purpose: a user editing only the phone number
// must not re-upload the image, and must not risk losing it. JSON.stringify
// drops undefined keys, so passing `undefined` produces exactly the "omit"
// wire form without any special casing.

// Mirrors the backend's own limit. Checked client-side too, so a too-large
// file is refused instantly with a friendly message rather than after a
// pointless upload. The backend still enforces it — this is UX, not
// security.
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export const ACCEPTED_LOGO_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

export interface BackendCompanySettings {
  company_name: string;
  gstin: string;
  address: string;
  contact_email: string;
  contact_phone: string;
  // One clause per line. Editable by an Admin, so the document's terms are
  // no longer compiled into the bundle.
  terms?: string;
  has_logo: boolean;
  // A data URL ready for an <img src>, or null when no logo is stored.
  logo_data_url: string | null;
  logo_bytes: number;
  // quote-validity: days, or null when unset (nothing printed).
  quote_validity_days?: number | null;
  // margin-approval: the GP % limit, or null when unset.
  approval_gp_threshold?: number | null;
  // warehouse-master: the default dispatch warehouse, or null when unset.
  default_warehouse_id?: number | null;
  default_warehouse_label?: string | null;
  // Configurable identity (Phase 1, 2026-10-29). Absent on an older backend,
  // so every reader falls back to today's behaviour.
  our_entity_aliases?: string;
  supplier_code_label?: string;
  location_code_label?: string;
  po_profile_oracle?: boolean;
  quote_prefix?: string | null;
  quote_padding?: number | null;
  next_quote_number?: string | null;
  oem_order_prefix?: string | null;
  oem_order_padding?: number | null;
  next_oem_order_number?: string | null;
  // M4 (D195): GRN and delivery-note numbering, the same shape.
  grn_prefix?: string | null;
  grn_padding?: number | null;
  next_grn_number?: string | null;
  dn_prefix?: string | null;
  dn_padding?: number | null;
  next_dn_number?: string | null;
  default_gst_rate?: number;
  tax_regime?: string;
  money_locale?: string;
  currency_symbol?: string;
  amount_words?: string;
  product_name?: string;
}

export const DEFAULT_SUPPLIER_CODE_LABEL = "Supplier part no.";
export const DEFAULT_LOCATION_CODE_LABEL = "Loc#";

/** `our_entity_aliases` (one per line) as a list, blanks dropped. */
export function entityAliasList(text: string | null | undefined): string[] {
  return (text ?? "").split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
}

export interface CompanySettingsResult {
  ok: boolean;
  settings?: BackendCompanySettings;
  error?: string;
}

export interface SaveCompanySettingsInput {
  companyName: string;
  gstin: string;
  address: string;
  contactEmail: string;
  contactPhone: string;
  // quote-validity: whole days as typed, "" = none. undefined = not sent.
  quoteValidityDays?: string;
  // margin-approval: the GP % limit as typed, "" = none. undefined = not sent.
  approvalGpThreshold?: string;
  // warehouse-master: the default dispatch warehouse id, null = none,
  // undefined = not sent.
  defaultWarehouseId?: number | null;
  // undefined = keep the stored logo, null = clear it, string = replace it.
  logoBase64?: string | null;
  // Configurable identity. undefined = not sent (left as stored).
  quotePrefix?: string;
  quotePadding?: string;
  oemOrderPrefix?: string;
  oemOrderPadding?: string;
  grnPrefix?: string;
  grnPadding?: string;
  dnPrefix?: string;
  dnPadding?: string;
  ourEntityAliases?: string;
  supplierCodeLabel?: string;
  locationCodeLabel?: string;
  poProfileOracle?: boolean;
  defaultGstRate?: string;
  moneyLocale?: string;
  currencySymbol?: string;
  amountWords?: string;
  productName?: string;
}

// THE CUSTOMER HALF OF A SAVE PAYLOAD.
//
// IDS, NOT A NAME. partner_name is deliberately NOT sent any more: the
// backend still accepts it, but only as a fallback for clients that have
// nothing better, and sending both would put two statements of who the
// customer is in one request. This app has ids, so it sends ids.
//
// Each role is omitted when unset rather than sent as null, because the
// backend leaves a field alone when its key is absent -- so a quote that has
// only an entity so far cannot wipe a ship-to chosen elsewhere.
export function customerToBackendPayload(
  customer: QuoteCustomer | undefined,
): Record<string, number> {
  if (!customer?.entity?.id) return {};

  const payload: Record<string, number> = { partner_id: customer.entity.id };
  if (customer.shipping?.id) payload.partner_shipping_id = customer.shipping.id;
  if (customer.invoice?.id) payload.partner_invoice_id = customer.invoice.id;
  if (customer.contact?.id) payload.partner_contact_id = customer.contact.id;
  return payload;
}

/** The customer graph as the backend returned it, or undefined. */
export function savedQuoteToCustomer(
  detail: SavedQuoteDetail,
): QuoteCustomer | undefined {
  const raw = (detail as { customer?: QuoteCustomer }).customer;
  if (!raw || !raw.entity) return undefined;
  return {
    entity: raw.entity,
    shipping: raw.shipping ?? null,
    invoice: raw.invoice ?? null,
    contact: raw.contact ?? null,
  };
}

// The fields the save endpoint accepts on a line, and no others.
// Reuses the pricing mapper for everything it shares, so the two payloads
// can't drift on how a Material becomes engine input — including the mode
// split, which is threaded straight through rather than re-decided here.
//
// Overloaded for the same reason materialToBackendLine is, and with the same
// ordering requirement: see the note there. The no-mode signature comes LAST
// so `lines.map(fn)` keeps compiling untouched.
export function materialToBackendSaveLine(
  m: Material,
  mode: "import",
): BackendImportSaveLineInput;
export function materialToBackendSaveLine(
  m: Material,
  mode: "domestic",
): BackendSaveLineInput;
// For a mode held in a variable rather than written as a literal — see the
// matching note on materialToBackendLine.
export function materialToBackendSaveLine(
  m: Material,
  mode: "domestic" | "import",
): BackendSaveLineInput | BackendImportSaveLineInput;
export function materialToBackendSaveLine(m: Material): BackendSaveLineInput;
export function materialToBackendSaveLine(
  m: Material,
  mode: "domestic" | "import" = "domestic",
): BackendSaveLineInput | BackendImportSaveLineInput {
  // manufacturer rides along as a DESCRIPTIVE field. The backend keeps it in
  // _LINE_DESCRIPTIVE_FIELDS, deliberately not the pricing allowlist, so it
  // reaches the record and never the engine. Omitted when unset rather than
  // sent empty.
  const descriptive = {
    description: m.description,
    ...(m.manufacturer ? { manufacturer: m.manufacturer } : {}),
    ...(m.uom ? { uom: m.uom } : {}),
    // THE CATALOG LINK. Conditional like every neighbour: a free-text line
    // has no product and omits the key rather than sending a 0 that names no
    // record. The backend accepts it in _LINE_DESCRIPTIVE_FIELDS -- pointing
    // a line at a catalog row says what the part IS and moves no number the
    // engine produces.
    //
    // ⚠️ supplier_ref DOES NOT COME WITH IT, and must never be added here. It
    // is the DISTRIBUTOR's own SKU -- a label that varies by seller, that two
    // distributors can reuse for different parts, and that nothing on either
    // side matches on. The backend pins its absence from both line allowlists
    // and from the bridge signature (test_product_master.py); the picker
    // shows it and stops. If a PO ever needs it, it is read through
    // product_id, not copied onto the line.
    ...(m.productId ? { product_id: m.productId } : {}),
    // RESTORED HERE, not new. The backend has always accepted origin and
    // always serialized it back; this app dropped it on save, which left the
    // estimate columns with nothing to join a shipment on. The fix shipped
    // once on its own branch, was never merged, and a later frontend deploy
    // built from a lineage without it put the bug back in production.
    ...(m.origin ? { origin: m.origin } : {}),
    // Per-unit kg and CBM, for a freight allocation that does not exist yet.
    // Conditional like the rest: a line with no figure omits the key rather
    // than asserting a zero the catalog never stated.
    ...(m.weight ? { weight: m.weight } : {}),
    ...(m.volume ? { volume: m.volume } : {}),
    ...(m.remark ? { remark: m.remark } : {}),
    ...(m.leadTime ? { lead_time: m.leadTime } : {}),
    // THE LIST/DISCOUNT SPLIT -- BOTH KEYS OR NEITHER, IN ONE SPREAD.
    //
    // ⚠️ THE SHAPE IS WHAT MAKES IT SAFE. The backend REFUSES a half-split
    // and fails the WHOLE batch, so a conditional that could emit one key
    // without the other is a save-killer for every other line on the quote.
    // One spread carrying both makes that unrepresentable -- do not split it
    // into two `...(x ? {} : {})` clauses.
    //
    // ⚠️ AND IT RIDES BOTH SAVE PATHS BECAUSE IT IS HERE. saveDraft (the 1.5s
    // autosave tick) and saveQuoteToBackend (the Save press) both build their
    // lines through THIS function. The update path replaces lines wholesale
    // -- quote.line_ids.unlink() then recreate -- and a line has no stable id
    // across a save, so the split cannot be preserved server-side and must be
    // resent every time. A copy of this block on one path only would wipe a
    // recorded split seconds after it was entered, silently. Never add the
    // split to a caller; it belongs in this shared builder and nowhere else.
    //
    // Gated on the FLAG, never on the numbers: a recorded 0% discount is a
    // real claim and must be sent, and `m.discountPct ? ...` would drop it.
    ...(m.discountRecorded && m.listPrice !== undefined && m.discountPct !== undefined
      ? { list_price: m.listPrice, discount_pct: m.discountPct }
      : {}),
    // WHERE THE PERCENTAGE CAME FROM, RESENT ON EVERY SAVE.
    //
    // ⚠️ IT BELONGS IN THIS SHARED BUILDER FOR THE REASON THE SPLIT ABOVE
    // DOES, and the reason is the update path: a save UNLINKS the lines and
    // recreates them, so a line has no stable id across one and nothing can
    // be preserved server-side. A copy of this on saveQuoteToBackend but not
    // on saveDraft would wipe a recorded source on the next 1.5s autosave
    // tick, silently. Never add provenance to a caller.
    //
    // ⚠️ OMITTED, NEVER DEFAULTED, WHEN THERE IS NO SOURCE. A reopened line
    // that predates provenance carries undefined, and sending "none" for it
    // would turn "nobody recorded this" into "the resolver found nothing" --
    // a claim, about the 5 live drafts, that was never decided. Absent keys
    // leave the backend columns NULL, which is what they already are.
    //
    // ⚠️ BOTH KEYS OR THE SOURCE ALONE. The model refuses a rule id under a
    // non-"rule" source and a "rule" source with no id, and fails the whole
    // batch, so the id is spread only inside the "rule" branch rather than
    // conditionally beside it.
    ...(m.discountSource === "rule" && m.discountRuleId
      ? { discount_source: "rule" as const, discount_rule_id: Number(m.discountRuleId) }
      // THE OEM LINK, only while the net cost IS still the OEM price. A cost
      // edited since the pick is the user's own (manual) -- sending the link
      // with it would be refused by the server and fail the whole save.
      : m.discountSource === "oem_quote"
        ? (m.oemQuote && m.netPrice === m.oemQuote.unit_price
          ? { discount_source: "oem_quote" as const, oem_quote_line_id: m.oemQuote.line_id }
          : { discount_source: "manual" as const })
      : m.discountSource === "manual" || m.discountSource === "none"
        ? { discount_source: m.discountSource }
        : {}),
    // Only sent when it is not the default, so an ordinary line's payload is
    // unchanged from before this existed.
    // `as const` so the literal survives -- without it TypeScript widens this
    // to `string` and it no longer satisfies the union on the line type.
    ...(m.priceStatus === "restricted" ? { price_status: "restricted" as const } : {}),
    ...(m.priceStatusReason ? { price_status_reason: m.priceStatusReason } : {}),
  };
  if (mode !== "import") {
    return { ...materialToBackendLine(m, "domestic"), ...descriptive };
  }

  const pricedRaw = materialToBackendLine(m, "import");

  // AN UNKNOWN CONVERSION RATE IS OMITTED, NOT SENT AS 0.
  //
  // materialToBackendLine coerces an absent rate to 0 because it also serves
  // the stateless pricing call, where the backend resolves nothing and a
  // missing key reaches the engine bridge as None — which `_to_decimal`
  // rejects outright. That path must keep stating a number. Stripped HERE for
  // exactly the reason freight_pct is stripped here: this is where the save
  // payload diverges from the pricing payload.
  //
  // ⚠️ ABSENCE IS A REQUEST, not merely a smaller payload. The backend's
  // _should_resolve_fx gates on the KEY being present, so an explicit 0 reads
  // as "the caller has stated the rate" and suppresses the FX schedule lookup.
  // Every line that never had a rate was being saved with 0 and priced through
  // `value_in_inr = freight_plus_unit x 0` — the whole import chain collapsed,
  // silently, on a quote that looked saved.
  //
  // `!== undefined`, NEVER a truthy test. A rate of 0 that someone actually
  // typed is a statement, and the backend honours an explicitly supplied rate
  // over its own lookup. Omitting it because it happens to be falsy would
  // reinterpret that statement as a question.
  //
  // A "schedule" LINE OMITS ITS RATE WHATEVER IT HOLDS (fx-rate-source-
  // marker). The rate it holds is what the schedule said LAST time; omitting
  // it is how the draft follows today's schedule. The backend drops a sent
  // rate on a schedule line anyway -- omitting here is the same rule stated
  // on both sides, not a second one.
  const priced =
    m.conversionRate !== undefined && m.rateSource !== "schedule"
      ? { ...pricedRaw, ...(m.rateSource === "stated" ? { rate_source: "stated" as const } : {}) }
      : {
          ...(({ conversion_rate: _resolveThisServerSide, ...rest }) => rest)(pricedRaw),
          ...(m.rateSource === "schedule" ? { rate_source: "schedule" as const } : {}),
        };

  // UNASSIGNED: the shape this function has always emitted, minus a
  // conversion_rate nobody stated.
  if (!m.shipmentKey) {
    return { ...priced, ...descriptive };
  }

  // ASSIGNED: name the shipment and DROP the freight percentage.
  //
  // Destructured out rather than deleted, and stripped HERE rather than inside
  // materialToBackendLine, because that function also serves the stateless
  // pricing call — which has no shipments at all and must keep stating a
  // percentage or every line would price at zero freight.
  //
  // Sending it would not be merely redundant: the backend refuses the whole
  // save, by design, because a supplied percentage on an assigned line is
  // about to be overwritten by the allocator and nothing would say so.
  const { freight_pct: _allocatorOwnsThis, ...withoutFreightPct } = priced;
  return {
    ...withoutFreightPct,
    ...descriptive,
    shipment_key: m.shipmentKey,
  };
}

// The `shipments` block for a save payload — or nothing at all.
//
// ⚠️ ABSENT AND EMPTY ARE DIFFERENT INSTRUCTIONS, and this is the one place
// the SPA decides between them. The backend reads a MISSING key as "leave the
// quote's shipments alone" and an explicit `[]` as "delete every one of them".
//
// So an empty source returns `undefined`, which the caller spreads into
// nothing — sticky. It must NEVER emit `[]` just because it happens to hold no
// shipments: a screen that never rendered the sourcing panel would then delete
// what another screen created. Clearing is a deliberate act and belongs to a
// caller that knows it holds the full set.
//
// ONE BUILDER FOR BOTH SAVE PATHS. saveQuoteToBackend and saveDraft both call
// it in BF5c-2. A draft that decided this differently from a final save would
// fight it — the autosave would clear what Save had just written.
export function buildShipmentsPayload(
  shipments: QuoteShipment[] | undefined,
): BackendShipmentInput[] | undefined {
  if (!shipments || shipments.length === 0) return undefined;

  return shipments.map((s) => ({
    key: s.key,
    // Only when the backend has issued one. An absent id is a create, and an
    // invented one would be refused — rightly, since it could name another
    // quote's shipment.
    ...(s.id !== undefined ? { id: s.id } : {}),
    name: s.name,
    ...(s.origin ? { origin: s.origin } : {}),
    mode: s.mode,
    // THE BILL, always stated. Re-sending the served total is what stops a
    // rate card silently recomputing over a figure someone corrected by hand:
    // the backend lets an explicit total win, but only if it is sent.
    freight_total: s.freightTotal,
    currency: s.currency,
    allocation_basis: s.allocationBasis,
    // Measured quantities and the rate card, each omitted when unset rather
    // than asserted as a zero nobody stated.
    ...(s.grossWeight !== undefined ? { gross_weight: s.grossWeight } : {}),
    ...(s.volume !== undefined ? { volume: s.volume } : {}),
    ...(s.containerCount !== undefined ? { container_count: s.containerCount } : {}),
    ...(s.tripCount !== undefined ? { trip_count: s.tripCount } : {}),
    ...(s.rate !== undefined ? { rate: s.rate } : {}),
    ...(s.rateBasis ? { rate_basis: s.rateBasis } : {}),
    ...(s.minCharge !== undefined ? { min_charge: s.minCharge } : {}),
    ...(s.notes ? { notes: s.notes } : {}),
  }));
}

// The shipments block, but only when the payload it is going into can carry
// one — decided from the values that payload actually DECLARES.
//
// ⚠️ THE PAYLOAD MUST BE INTERNALLY CONSISTENT, and this is the only place
// that can guarantee it, because `mode` and `supplier_currency` are derived
// here too. The backend refuses a shipment on a quote that is not import with
// a supplier currency, and a refusal fails the WHOLE save — taking any line
// edits in the same request with it.
//
// A CALLER-SIDE GATE IS NOT ENOUGH, and this is not hypothetical: the quote
// screen holds its mode as `pricingModeOverride ?? quote.pricingMode`, and the
// draft-restore path sets that override WITHOUT writing the stored quote. So
// after a reload the screen read "import" while this function's `mode` read
// "domestic" — the screen sent shipments and the backend refused every save
// until the page was touched again. Caught in a browser pass, not by a test.
//
// Deriving both from the same variable makes the disagreement unrepresentable
// rather than merely unlikely.
function shipmentsFor(
  mode: "domestic" | "import",
  supplierCurrency: string | undefined,
  shipments: QuoteShipment[] | undefined,
): BackendShipmentInput[] | undefined {
  if (mode !== "import" || !supplierCurrency) return undefined;
  return buildShipmentsPayload(shipments);
}

// Maps one of this app's Material rows onto the engine's inputs.
//
// netPrice is the right source for the engine's `unit_price` on BOTH paths:
// the engine means the incoming unit COST by that name (the client's own
// sheet column name, not a sell price — the engine calls the sell price
// unit_sp), and netPrice is list price less discount, i.e. the buy price
// before any freight or duty. On the import path that cost is still the
// supplier-currency one; conversion_rate is what converts it, so converting
// here would both double-count and move a pricing step into the frontend.
//
// The two modes are separate branches, not one body with conditional keys.
// The domestic branch must stay byte-for-byte what it has always emitted —
// the validated row 4 -> unit_sp 10247 result depends on this exact wire
// shape — and a merged body is how that quietly acquires an extra key.
//
// OVERLOADED, deliberately, and the ORDER IS LOAD-BEARING. The
// implementation signature takes a second parameter, but the single-argument
// overload is what keeps `rows.map(materialToBackendLine)` compiling with
// zero call-site change: map would otherwise pass its index as the second
// argument and fail to match a "domestic" | "import" parameter.
//
// That overload must be LAST. TypeScript resolves an overloaded function
// passed as a callback by inferring from the final signature, so listing the
// domestic one first makes map() infer BackendImportPriceLineInput[] and the
// existing domestic call sites stop compiling. Explicit two-argument calls
// still resolve top-down and are unaffected by the ordering.
export function materialToBackendLine(
  m: Material,
  mode: "import",
  supplierCurrency?: string,
): BackendImportPriceLineInput;
export function materialToBackendLine(
  m: Material,
  mode: "domestic",
): BackendPriceLineInput;
// The variable-mode signature. A caller that derives its mode at runtime
// (`quote.pricingMode ?? "domestic"`) holds a "domestic" | "import" union,
// which matches NEITHER literal overload above — TypeScript requires one
// signature to accept the whole argument type, it will not split a union
// across two. Without this, every runtime-mode call site would have to
// re-branch on the literal purely to satisfy the compiler. Returns the union,
// so a caller wanting one concrete shape passes the literal and hits an
// overload above.
export function materialToBackendLine(
  m: Material,
  mode: "domestic" | "import",
  supplierCurrency?: string,
): BackendPriceLineInput | BackendImportPriceLineInput;
export function materialToBackendLine(m: Material): BackendPriceLineInput;
export function materialToBackendLine(
  m: Material,
  mode: "domestic" | "import" = "domestic",
  // The quote's supplier currency, when it has one. Not a pricing input and
  // never sent on a line -- it decides only whether an UNSET conversion rate
  // can be left out for the backend to resolve. Defaulted, so every existing
  // two-argument call site keeps emitting exactly what it always did.
  supplierCurrency?: string,
): BackendPriceLineInput | BackendImportPriceLineInput {
  if (mode === "import") {
    return {
      oem: m.oem,
      part_number: m.partNo,
      qty: m.qty,
      unit_price: m.netPrice,
      // A missing freight/conversion/duty coerces to 0 rather than to some
      // invented default. That mirrors the backend's own missing-input
      // behaviour — it outputs 0 for that component and does not crash — so
      // an unfilled row reads the same on both sides. Picking a "sensible"
      // default here would be this frontend deciding a pricing input, which
      // is exactly what the engine is the single source of truth for.
      // NO FREIGHT IS NOT ZERO FREIGHT (line-sources, 2026-09-30). A typed
      // freight (0 included) or an allocated share is sent; a line on a
      // shipment that has not been allocated yet names its shipment
      // instead; anything else OMITS the key and the server refuses.
      // `!== undefined`, never a truthy test.
      ...(m.freightPct !== undefined
        ? { freight_pct: m.freightPct }
        : (m.shipmentKey ? { shipment_key: m.shipmentKey } : {})),
      // AN UNSET RATE IS OMITTED ONLY WHEN SOMETHING CAN RESOLVE IT.
      //
      // Slice 2 taught price_lines to look the scheduled rate up, but only
      // for lines that state none -- a present value, 0 included, is read as
      // "the caller has said what the rate is" and honoured. So while this
      // kept sending `?? 0`, the resolution never fired and a fresh import
      // line still priced through `value_in_inr = freight_plus_unit x 0`.
      // Sending 0 was the thing suppressing the fix for sending 0.
      //
      // The condition is the supplier currency, because that is exactly what
      // the backend needs to resolve FROM. Without one -- an import quote
      // that has not named its currency -- nothing server-side could answer,
      // and omitting would reach the engine bridge as None, which
      // _to_decimal rejects outright. Confirmed live during Slice 1:
      // "conversion_rate is None; the engine needs a number."
      //
      // `!== undefined`, never a truthy test: a 0 someone actually typed is a
      // statement the backend honours over its own schedule, and dropping it
      // for being falsy would turn that answer back into a question.
      //
      // A "schedule" line omits its rate here too, so the live price the
      // screen shows is the one the save will store: today's schedule.
      ...((m.conversionRate !== undefined && m.rateSource !== "schedule") || !supplierCurrency
        ? { conversion_rate: m.conversionRate ?? 0 }
        : {}),
      // A MISSING DUTY IS NOT A ZERO DUTY (missing-duty-not-zero,
      // 2026-09-30) -- the same rule the conversion rate follows above.
      // `!== undefined`, never a truthy test: a duty a person TYPED, 0
      // included ("this HSN is genuinely exempt"), is a statement the server
      // honours over its schedule. Only a line that never had one, and has an
      // HSN the schedule can be asked about, omits it. A line with neither a
      // duty nor an HSN still sends 0 (there is nothing to look up; the
      // server's bridge rejects an absent number).
      //
      // line-sources (2026-09-30): the `?? 0` for "no duty and no HSN" is
      // gone too -- that line OMITS the key and the server refuses "HSN code
      // required for import lines". A SCHEDULE line (resolved from its HSN
      // last time) omits its duty whatever it holds, like a schedule rate, so
      // the draft follows today's schedule.
      ...(m.dutyPct === undefined || m.dutySource === "schedule"
        ? (m.hsnCode ? { hsn_code: m.hsnCode } : {})
        : { duty_pct: m.dutyPct }),
    };
  }

  // ibf_pct is 0 because this is the domestic path; inbound freight is
  // exactly what the validated deal had at zero throughout.
  return {
    oem: m.oem,
    part_number: m.partNo,
    qty: m.qty,
    unit_price: m.netPrice,
    ibf_pct: 0,
  };
}

/** ONE quote line built from a CATALOGUE PRODUCT.
 *
 *  ⚠️ EXTRACTED SO THERE IS EXACTLY ONE DEFINITION OF WHAT A NEW QUOTE
 *  LINE LOOKS LIKE. It was inline in addMaterialsToQuote until the
 *  po-lines-to-quote slice needed the identical row for a PO line that
 *  matched the master. A second copy of a twenty-field literal is a second
 *  thing to keep in step, and the fields that would drift are the ones
 *  nobody re-reads: priceStatus, the discount provenance block, and the
 *  three-state stock.
 *
 *  ⚠️ IT SETS NO PRICE, AND THAT IS NOT AN OMISSION. anixify.product
 *  carries none, permanently, so listPrice and netPrice are UNDEFINED --
 *  never 0 -- and priceStatus is 'unpriced'. See the unpriced-line hard
 *  rule in CLAUDE.md. A refused discount rule records WHY in
 *  priceStatusReason and still leaves the line unpriced. */
function catalogQuoteLine(
  product: CatalogProduct,
  qty: number,
  lineNo: number,
  resolution: DiscountResolution,
  rules: readonly OemDiscountRule[],
  level: number | undefined,
  lockWarning: Material["lockWarning"],
): Material {
  // The narrowing is the caller's job: this is only ever reached for a
  // product whose brand and category this build recognises.
  return {
    lineNo,
    partNo: product.partNo,
    description: product.description,
    // ⚠️ EMPTY, NOT INVENTED. anixify.product has no spec column, so
    // there is nothing to copy. Same rule savedQuoteToMaterials follows
    // for spec/uom/origin: a guessed value is indistinguishable from a
    // recorded one once it is on screen.
    spec: "",
    oem: product.brand,
    qty,
    uom: product.uom ?? "",
    // The duty schedule's lookup key (never a price); dutyPct stays undefined
    // until a person states one.
    hsnCode: product.hsnCode,
    // ⚠️ NO PRICE. The catalog has none and never will -- see
    // CatalogProduct. listPrice and netPrice are left undefined rather
    // than defaulted to 0, and priceStatus below records why. Read
    // services/unpricedGuard.ts before changing any of these three lines.
    listPrice: undefined,
    // ⚠️ RESOLVED AGAINST THE REAL RULE TABLE, ON IDS, OR NOT AT ALL.
    //
    // `resolution` is computed just above this literal. Three outcomes
    // reach these two fields and they are deliberately different:
    //
    //   rule      the percentage applies the moment a List Price is typed
    //   refused   discountPct stays UNDEFINED -- not 0 -- and the line
    //             carries the reason. Absent is not zero: 0% is a claim
    //             that the OEM gave nothing off.
    //   pending   also undefined. The quote has no customer yet, so there
    //             is nothing to resolve against; this is a quote being
    //             built, not a quote that cannot be priced.
    //
    // ⚠️ priceStatus IS ALREADY "unpriced" ON EVERY PICKED LINE, because
    // the catalog carries no price -- so the refusal needs no new state
    // and, more importantly, no new way for a line to reach the engine.
    // unpricedGuard already keeps a line with no netPrice out of the
    // pricing request entirely. What the refusal adds is the REASON, so
    // the banner can say which of the two remedies applies.
    discountPct:
      resolution.kind === "rule" ? resolution.discountPct : undefined,
    // WHERE THAT PERCENTAGE CAME FROM, RECORDED AT THE ONE MOMENT IT IS
    // KNOWN. `resolution` is discarded after this literal, so the rule's
    // identity is unrecoverable a line later: re-deriving it would mean
    // matching against TODAY's rules, and a rule archived between the
    // save and the reopen would make the line claim it had no source
    // when it was priced off one.
    //
    // provenanceFromResolution is the only place the four outcomes map
    // to the three sources -- see discountProvenance.ts for why refused
    // and pending both record "none".
    ...(() => {
      const p = provenanceFromResolution(resolution);
      return {
        discountSource: p.source,
        ...(p.ruleId ? { discountRuleId: p.ruleId } : {}),
        // The pairing for display, taken from the rule that actually
        // matched rather than looked up again later. Names only; the id
        // above is the handle that survives an archive.
        ...(resolution.kind === "rule"
          ? {
              discountRulePairing: rulePairingLabel(
                rules, resolution.ruleId,
              ),
            }
          : {}),
      };
    })(),
    netPrice: undefined,
    priceStatus: "unpriced",
    priceStatusReason:
      resolution.kind === "refused" ? resolution.reason : undefined,
    productId: product.id,
    // The brand RECORD, carried so a re-resolution matches on the id
    // rather than looking the name up again. See Material.brandId.
    brandId: product.brandId ?? undefined,
    origin: product.defaultOrigin ?? "",
    // Copied from the catalog, per unit, exactly as origin is. A
    // free-text line picked without a product has neither, which is why
    // both are optional rather than defaulted to a made-up figure.
    weight: product.weight,
    volume: product.volume,
    landedCost: 0,
    marginPct: DEFAULT_NEW_LINE_MARGIN_PCT,
    sellPrice: 0,
    lineTotal: 0,
    category: toMaterialCategory(product.category),
    // THREE STATES, and the middle one is new here. undefined means the
    // stock read never covered this product (or never happened); 0 means
    // it did and there is none; a positive number is the ordinary case.
    stock: level,
    // ⚠️ NO WAREHOUSE FROM AN AGGREGATE. The quantity above is a SUM
    // across every location the provider reported, so there is no single
    // place to name unless the caller asked for the breakdown and it held
    // exactly one named row -- see singleWarehouseName. The picker reads
    // the aggregate, so this stays undefined and the pill reads
    // "N available" with no place attached, which is what is known.
    warehouse: undefined,
    lockWarning,
  };
}

/** QUOTE LINES FROM A CONFIRMED CUSTOMER PO.
 *
 *  ⚠️ FOUR FIELDS COME FROM THE PO AND A PRICE IS NOT ONE OF THEM: part
 *  number, quantity, description and UOM. Nothing else on the PO line reaches
 *  the quote -- not `unit_price`, not `extended_price`, not
 *  `unit_price_text`, and not `anixter_part_number`. A quote price is
 *  produced by the pricing path and the discount rules, exactly as it is for
 *  a line added from the picker, and the PO is then something to COMPARE the
 *  result against. A price copied across would make that comparison agree
 *  with itself.
 *
 *  ⚠️ IT WRITES NOTHING AND STORES NOTHING. It returns rows. The caller puts
 *  them on screen and saves them through the existing draft save, so there is
 *  one write path for a quote's lines and this is not a second one. That is
 *  also why it takes `startLineNo` instead of reading a store: the rows it
 *  appends to are the ones the caller is holding.
 *
 *  ⚠️ AND IT ADDS A LINE FOR EVERY PO LINE, MATCHED OR NOT. An unmatched line
 *  becomes a FREE-TEXT, UNPRICED row carrying what the customer printed --
 *  never a dropped line. A PO line that quietly failed to arrive is the worst
 *  outcome here: the quote would look complete and be short, and the
 *  comparison would report a PO line with no quote line beside it only if
 *  somebody went looking. */
export function poLinesToQuoteRows(
  matches: PoLineMatch[],
  opts: {
    /** The line number to start at -- normally existing rows' max + 1. */
    startLineNo: number;
    rules: readonly OemDiscountRule[];
    partnerId?: number;
    partnerName?: string;
  },
): Material[] {
  const rows: Material[] = [];
  let lineNo = opts.startLineNo;

  for (const match of matches) {
    const line = match.line as PoLine;
    // ⚠️ THE ONE PLACE A QUANTITY IS DECIDED, and 0 stays 0. See
    // quantityForQuoteLine: a quantity nobody could read must not become a 1
    // that prices.
    const qty = quantityForQuoteLine(line.quantity);
    // The CUSTOMER's own words and unit, per the brief. Falls back to the
    // master's only when the PO printed none -- never the other way round, or
    // a matched line would silently restate what the customer asked for.
    const description = (line.description ?? "").trim();
    const uom = (line.uom ?? "").trim();

    if (match.product) {
      const product = match.product;
      const resolution = resolveDiscount(
        { netPriceEntered: undefined, discountRecorded: undefined,
          listPrice: undefined, discountPct: undefined },
        opts.rules,
        opts.partnerId,
        product.brandId ?? undefined,
        {
          partnerName: opts.partnerName ?? "",
          brandName: product.brand ?? "",
        },
      );
      const row = catalogQuoteLine(
        product, qty, lineNo++, resolution, opts.rules,
        // No stock read on this path: undefined is "never looked", which is
        // the truth, and is a different cell state from a reported 0.
        undefined,
        undefined,
      );
      rows.push({
        ...row,
        ...(description ? { description } : {}),
        ...(uom ? { uom } : {}),
      });
      continue;
    }

    // ---- the free-text line ------------------------------------------
    //
    // ⚠️ NO productId, NO brandId, NO DISCOUNT RULE. There is no product to
    // resolve a rule against, so there is nothing for the discount machinery
    // to be asked -- and asking it with a blank brand would record a
    // provenance for a decision nobody made.
    rows.push({
      lineNo: lineNo++,
      partNo: (line.part_number ?? "").trim(),
      description,
      spec: "",
      // Free text on the PO and free text here. It is what the customer
      // printed, and Material.oem has always carried whichever of
      // manufacturer-or-brand the typist had to hand.
      oem: (line.manufacturer ?? "").trim(),
      qty,
      uom,
      listPrice: undefined,
      netPrice: undefined,
      priceStatus: "unpriced",
      // ⚠️ `category` IS REQUIRED AND THERE IS NOTHING TO PUT IN IT.
      // "accessory" is the display-only neutral savedQuoteToMaterials already
      // uses for the same reason: it is the one value that raises no spurious
      // brand-lock warning about a line whose brand is unknown.
      category: "accessory",
      origin: "",
      landedCost: 0,
      marginPct: DEFAULT_NEW_LINE_MARGIN_PCT,
      sellPrice: 0,
      lineTotal: 0,
    });
  }

  return rows;
}

export const api = {
  listCurrencies,
  getQuotes(): Quote[] {
    return quotesStore;
  },
  getQuote(id: string): Quote | undefined {
    return quotesStore.find((q) => q.quoteNo === id);
  },
  getMaterials(quoteId: string): Material[] {
    return materialsByQuote[quoteId] ?? mockMaterials;
  },
  // ⚠️ DOES THIS QUOTE HAVE A MATERIAL LIST OF ITS OWN?
  //
  // The FACT behind services/mockQuoteGuard.ts, exposed as a fact rather
  // than inferred from the rows: `getMaterials` above answers the shared
  // 180-row example list for any key nobody has ever seeded, and from the
  // caller's side that answer is indistinguishable from a real one. This is
  // the one place the difference is visible.
  //
  // TRUE means something deliberately wrote this quote's list -- createQuote
  // seeding an empty one, seedReopenedQuote restoring a saved quote or a
  // draft, or addMaterialsToQuote forking on the first pick. FALSE means the
  // key has never been seeded and every row the screen is showing came from
  // the fallthrough.
  //
  // ⚠️ A READ, AND IT MUST STAY ONE. It does not create the bucket it is
  // asking about -- `in` rather than an index-and-compare for exactly that
  // reason, so a genuinely-empty seeded list (createQuote's `[]`) reads TRUE
  // and is not confused with an absent one.
  hasOwnMaterialStore(quoteId: string): boolean {
    return quoteId in materialsByQuote;
  },
  // ⚠️ IS THIS ONE OF THE 25 EXAMPLE QUOTES SHIPPED IN mocks/quotes.ts?
  //
  // The SECOND fact behind services/mockQuoteGuard.ts, added by C1a after
  // hasOwnMaterialStore alone was measured to be insufficient on live: a
  // pre-C1 draft RESTORES from the database, seedReopenedQuote gives that
  // quote a list of its own, and the first fact then reads true. Two example
  // quotes were finalised that way on 2026-09-07 and drew real sequence
  // numbers (Q-2026-0154, Q-2026-0155), putting 360 generated lines on
  // /saved-quotes.
  //
  // ⚠️ A FACT ABOUT OUR OWN BUNDLED ARRAY, NOT ABOUT A DATABASE ROW. It reads
  // the shipped mockQuotes names and nothing else, so it does not reintroduce
  // the content-of-the-rows heuristic mockQuoteGuard exists to avoid. The
  // answer is knowable without a database at all.
  //
  // ⚠️ BUILT FROM THE IMPORTED ARRAY, NEVER FROM quotesStore. quotesStore is
  // SEEDED from mockQuotes but grows: createQuote pushes real quotes into it.
  // Deriving the set from the store would therefore start matching quotes a
  // user genuinely created, and refuse to save them. The imported array never
  // changes, so this set is exactly the 25 shipped names for the life of the
  // tab -- including across __resetStoresForTests.
  //
  // ⚠️ THE NAME ALONE IS NOT ENOUGH, AND THIS FUNCTION DELIBERATELY DOES NOT
  // TRY TO BE. Live holds TEN database rows named Q-2026-0001. A saved quote
  // is a real record whose name proves nothing about it, so the route is what
  // separates the two -- see mockQuoteGuard.exampleDataProblem, which returns
  // early in saved mode before this fact is ever consulted. Do not add a
  // "unless it is saved" clause here; one place decides that.
  isExampleQuoteNo(quoteNo: string): boolean {
    return SHIPPED_EXAMPLE_QUOTE_NOS.has(quoteNo);
  },
  getPricingContext(_quoteId: string): PricingContext {
    // Deep clone so callers can mutate freely.
    return JSON.parse(JSON.stringify(defaultPricingContext));
  },
  // getProducts() WAS HERE AND IS GONE (slice 1c). /catalog was its only
  // caller and now reads searchProducts() like the picker does, so keeping a
  // mock-returning function alive next to the real one would be an invitation
  // to wire a screen back to 412 invented parts.
  getBrandLockRules(): BrandLockRule[] {
    return mockBrandLockRules;
  },
  // getDiscountRules() WAS HERE AND IS GONE (D3a-ii). The Pricing screen was
  // its only caller and now reads listDiscountRules(), which returns
  // anixify.discount.rule. Removed rather than deprecated, for the same reason
  // api.getProducts() went at slice 1c and the three rate getters went at U2:
  // a mock-returning function sitting next to the real one is an invitation to
  // wire a screen back to invented data.
  //
  // ⚠️ AND AS OF D3a-iii THE MOCK IS OUT OF THE PRICING PATH TOO.
  // findDiscountPct is deleted; services/discountResolver.ts resolves against
  // the real table on (partner_id, brand_id) and refuses when there is no
  // rule. mocks/discountRules.ts survives only as a negative fixture in one
  // test -- see its own header.
  // getFreightRates() / getDutyRates() / getFxRates() WERE HERE AND ARE GONE.
  //
  // They returned mocks/{freightRates,dutyRates,fxRates}.ts while
  // anixify.quote.line resolved a line's duty and conversion rate out of the
  // REAL anixify.duty.rate / anixify.fx.rate tables. /sourcing and
  // Settings > Exchange Rates were therefore showing figures the pricer was
  // not using. Removed rather than deprecated, for the same reason
  // api.getProducts() was at slice 1c: a mock-returning function sitting next
  // to the real one is an invitation to wire a screen back to it.
  //
  // The replacements are api.listDutyRates() / listFreightRates() /
  // listFxRates(), which read the database and never fall back.
  getRiskBufferSettings(): RiskBufferSettings {
    return { ...defaultRiskBufferSettings };
  },
  getBufferChangeHistory(): BufferChangeEntry[] {
    return mockBufferChangeHistory;
  },
  getCompanySettings(): CompanySettings {
    return { ...mockCompanySettings };
  },
  createQuote(input: NewQuoteInput): Quote {
    const today = new Date().toISOString().slice(0, 10);
    const quote: Quote = {
      quoteNo: nextQuoteNo(),
      endClient: input.endClient,
      customer: input.customer,
      value: 0,
      margin: 0,
      status: "Draft",
      // No validity on a new draft: it is stored at finalise (quote-validity).
      validUntil: "",
      createdAt: today,
      projectId: input.projectId,
      projectName: input.projectName,
      warehouse: input.warehouse,
    };
    quotesStore = [quote, ...quotesStore];
    materialsByQuote[quote.quoteNo] = [];
    return quote;
  },
  // Seeds the per-quote material store for a quote that came from OUTSIDE this
  // module -- one reopened from /saved-quotes/:id, or a restored draft.
  //
  // ⚠️ WITHOUT THIS, addMaterialsToQuote's `materialsByQuote[key] ?? mockMaterials`
  // falls through to the 180 mock lines, and four things go wrong at once, all
  // silently: the dedupe scans mock rows so a repeat pick never matches the
  // line it belongs to; bumpedLines carries mock lineNos, which QuoteDetail
  // merges against its live rows BY lineNo and so lands the quantity on an
  // unrelated line or on none; nextLineNo counts from the mock array so a new
  // pick is numbered 181 on a twelve-line quote; and the store is then written
  // back under this quote's key holding mock lines.
  //
  // ⚠️ TAKES THE ALREADY-MAPPED ROWS, not the SavedQuoteDetail. The caller has
  // just built them with savedQuoteToMaterials(detail, shipments) and is about
  // to put that same array on screen; re-deriving here would make a second
  // array that can drift from the one the user is looking at. lineNos are
  // therefore the real 1..n the mapper assigned.
  //
  // Copied on the way in for the same reason createQuote stores copies: the
  // caller's array is React state that it goes on to replace.
  seedReopenedQuote(key: string, materials: Material[], endClient: string): void {
    materialsByQuote[key] = [...materials];
    reopenedEndClient[key] = endClient;
  },
  // Sets which pricing path a quote is on.
  //
  // A write, so it lives here next to the reads it affects rather than in a
  // component — same rule and same in-memory, tab-lifetime store as
  // createQuote()/addMaterialsToQuote(). It does not survive a refresh, and
  // it is not a persistence layer.
  //
  // Returns the updated quote, or undefined when the id matches nothing —
  // which is the normal case for a quote read back from Odoo, since those
  // are never in quotesStore. The caller keeps its own view of the mode, so
  // a miss is not an error; the mode simply lives only in the component for
  // that quote.
  setQuotePricingMode(quoteId: string, mode: "domestic" | "import"): Quote | undefined {
    const quote = quotesStore.find((q) => q.quoteNo === quoteId);
    if (!quote) return undefined;
    quote.pricingMode = mode;
    return quote;
  },
  // Same shape as setQuotePricingMode above. Prototype in-memory state, so
  // it survives navigation within the tab and nothing more -- the durable
  // copy is the one the save payload carries to Odoo.
  setQuoteSupplierCurrency(quoteId: string, code: string): Quote | undefined {
    const quote = quotesStore.find((q) => q.quoteNo === quoteId);
    if (!quote) return undefined;
    quote.supplierCurrency = code;
    return quote;
  },
  // ⚠️ `stockLevels` IS THE PICKER'S ALREADY-FETCHED MAP, id-keyed, and its
  // ABSENCES ARE MEANINGFUL. A product with no entry is UNKNOWN and the line
  // is created with no stock at all; a product whose entry holds 0 is a
  // stated "there is none" and lands on the line AS 0. Omitting the argument
  // entirely (a caller with no stock read behind it) leaves every new line
  // unknown, which is the honest answer rather than a zeroed one.
  //
  // Passed in rather than fetched here so this stays synchronous and so the
  // levels a line is created with are exactly the ones the user was looking
  // at when they picked it -- re-reading would open a window where the pill
  // said one thing and the line recorded another.
  addMaterialsToQuote(
    quoteId: string,
    picks: MaterialPick[],
    stockLevels?: Map<number, number>,
    // ⚠️ THE DISCOUNT CONTEXT, PASSED IN RATHER THAN FETCHED HERE.
    //
    // Two reasons, both load-bearing. It keeps this function SYNCHRONOUS, like
    // the stockLevels argument above and for the same reason -- the rules a
    // line is created under are exactly the ones the user was looking at, and
    // re-reading here would open a window where the screen said one thing and
    // the line recorded another. And it means there is NO MODULE-LEVEL RULE
    // CACHE: the caller holds a freshly-loaded list, so a rate an Admin
    // changed a moment ago is resolvable immediately, with nothing stale to
    // invalidate.
    //
    // ⚠️ OPTIONAL, AND OMITTING IT RESOLVES NOTHING RATHER THAN RESOLVING 0%.
    // With no partnerId the resolver returns `pending` and the line carries no
    // percentage at all -- which is the honest answer to "nobody told me who
    // this quote is for". A default of 0 here would be the exact bug D3a-iii
    // removed, reintroduced through the back door.
    discountContext?: {
      rules: readonly OemDiscountRule[];
      partnerId?: number;
      partnerName?: string;
    },
  ): AddMaterialsResult {
    // ⚠️ A REOPENED QUOTE IS NOT IN quotesStore, so the find below misses under
    // a `saved:<id>` key. `quotesStore.find(...)` used to stand alone here:
    // endClient fell to "", findDiscountPct matched nothing, and every line
    // added to a reopened quote lost its negotiated discount as a STATED 0% --
    // indistinguishable on screen from a real one. seedReopenedQuote records
    // the client under the same key the lines are stored under, so a key that
    // has lines always has its client, and a mock key has neither and falls
    // through to exactly the behaviour it had before.
    //
    // The seeded value is detail.partner_name, which is also what
    // savedQuoteToQuote puts in quote.endClient -- the value MaterialPicker
    // already filters its brand-lock rules by. So the discount lookup and the
    // rule banner read one value and cannot disagree about who the quote is for.
    const endClient =
      reopenedEndClient[quoteId]
      ?? quotesStore.find((q) => q.quoteNo === quoteId)?.endClient
      ?? "";
    const current = materialsByQuote[quoteId] ?? mockMaterials;
    const list = [...current];
    let nextLineNo = list.reduce((max, m) => Math.max(max, m.lineNo), 0) + 1;

    const newLines: Material[] = [];
    const bumpedLines: { lineNo: number; addedQty: number }[] = [];

    for (const pick of picks) {
      // THE PICKED ROW ITSELF. No second lookup -- see MaterialPick.product.
      const product = pick.product;

      // ⚠️ AN UNCLASSIFIED PRODUCT CANNOT BE ADDED, and this is the second of
      // two gates rather than the only one: the picker refuses to select such
      // a row at all. Repeated here because brand and category are the join
      // keys for brand-lock evaluation -- a null brand matches no rule, so a
      // Strict lock would simply stop firing. Skipping is the conservative
      // answer; the picker is what explains it to the user.
      //
      // ⚠️ IT IS ALSO WHY resolveDiscount's brandId BRANCH IS UNREACHABLE
      // today: a line never gets built from a product with no brand, so it
      // never carries a null brandId into resolution. That branch refuses
      // rather than falling through anyway -- see its comment for why.
      if (!product.brand || !product.category) continue;

      // ⚠️ MATCHED BY CATALOG ID WHEN THERE IS ONE, and only falling back to
      // the part number for a line that has no id (a mock line, or one
      // reopened from a save before product_id was sent). Part numbers are
      // NOT unique -- two manufacturers ship different goods under the same
      // string and the backend deliberately has no constraint saying
      // otherwise -- so a partNo match would merge a Panduit part into a
      // CommScope line and add the quantities together.
      const existingIdx = list.findIndex((m) =>
        m.productId !== undefined
          ? m.productId === product.id
          : m.partNo === product.partNo,
      );
      if (existingIdx !== -1) {
        const existing = list[existingIdx];
        list[existingIdx] = { ...existing, qty: existing.qty + pick.qty };
        bumpedLines.push({ lineNo: existing.lineNo, addedQty: pick.qty });
        continue;
      }

      // ⚠️ THE DISCOUNT DECISION, MADE ONCE PER LINE AND ON IDS.
      //
      // Everything about which percentage this line gets -- or whether it may
      // be priced at all -- is decided here and read off `resolution` below.
      // One call site, so there is no second place a percentage can be
      // invented.
      //
      // `product.brandId ?? undefined` because the wire says `number | null`
      // and the resolver's contract is `number | undefined`; null and
      // undefined mean the same thing here (nobody classified this product)
      // and the resolver has one branch for it.
      const resolution = resolveDiscount(
        // A brand-new line: no netPrice, no listPrice, no recorded split. Spelt
        // out rather than passed as `{}` so the resolver's manual-split check
        // is reading the line's real state, not an assumption about it.
        { netPriceEntered: undefined, discountRecorded: undefined,
          listPrice: undefined, discountPct: undefined },
        discountContext?.rules ?? [],
        discountContext?.partnerId,
        product.brandId ?? undefined,
        {
          partnerName: discountContext?.partnerName ?? endClient,
          brandName: product.brand ?? "",
        },
      );

      // ⚠️ BY PRODUCT ID, NEVER BY PART NUMBER -- the same rule the dedupe
      // above already follows, applied to the other lookup on this line.
      // `mockInventory.find((l) => l.partNo === product.partNo)` used to sit
      // here: part numbers are not unique and the backend deliberately has no
      // constraint saying they are, so that match could return a DIFFERENT
      // manufacturer's stock and stamp it onto this line.
      //
      // ⚠️ `.has` BEFORE `.get`. A stated 0 is a real level and must survive;
      // a missing id must stay undefined. `stockLevels.get(id) || undefined`
      // and the old `available > 0 ? available : undefined` both collapse
      // those two into one, which is the exact confusion this slice removes.
      const hasLevel = product.id !== undefined && !!stockLevels?.has(product.id);
      const level = hasLevel ? stockLevels!.get(product.id) : undefined;
      const lineNo = nextLineNo++;

      const material = catalogQuoteLine(
        product, pick.qty, lineNo, resolution,
        discountContext?.rules ?? [], level, pick.lockWarning,
      );
      list.push(material);
      newLines.push(material);
    }

    materialsByQuote[quoteId] = list;
    const touchedLineNos = [...bumpedLines.map((b) => b.lineNo), ...newLines.map((m) => m.lineNo)];
    return { newLines, bumpedLines, touchedLineNos };
  },

  // ---- Removing a line ---------------------------------------------------
  //
  // THE INVERSE OF addMaterialsToQuote, and it follows the same two rules.
  //
  // FORK, THEN MUTATE. A quote that has never been touched reads its lines
  // straight off the shared `mockMaterials` array, so removing in place
  // would delete the line from all 25 mock quotes at once. renumberLines
  // below rebuilds every row as a new object, which forks by construction --
  // but the result is still stored under this quote's own key, never written
  // back through the shared reference.
  //
  // KEYED ON lineNo, NEVER AN INDEX. The table renders `visibleRows`, which
  // is a FILTERED view once "issues only" or "in stock only" is on, so a row
  // index from the UI names a different row in the full list. lineNo is the
  // only handle that means the same thing on both sides.
  //
  // WHAT IT DOES NOT DO: reach the backend. There is no delete endpoint and
  // there should not be one -- a quote's lines are replaced wholesale by the
  // authoritative save (anixify.quote._update_from_payload unlinks and
  // recreates them), so a removal reaches Odoo by being absent from the next
  // save's payload. That save must carry quote_id, or it creates a second
  // quote and the removal lands on nothing.
  removeMaterialFromQuote(quoteId: string, lineNo: number): RemoveMaterialResult {
    const current = materialsByQuote[quoteId] ?? mockMaterials;
    const result = removeLineFromRows(current, lineNo);
    // Only when something was actually removed. A no-op must not fork the
    // shared array into this quote's key and quietly detach it from the
    // other 24.
    if (result.removed) materialsByQuote[quoteId] = result.rows;
    return result;
  },

  // Put a removed line back exactly where it was, edits included.
  //
  // The row handed back here is the one that was ON SCREEN when Remove was
  // pressed -- qty, discount and margin as the user had them -- because the
  // caller passes its own live rows, not the store's copy. Restoring the
  // store's copy instead would silently discard those edits, which is the
  // failure that makes an Undo worse than no Undo.
  restoreMaterialToQuote(quoteId: string, removed: Material, index: number): Material[] {
    const current = materialsByQuote[quoteId] ?? mockMaterials;
    const next = restoreLineToRows(current, removed, index);
    materialsByQuote[quoteId] = next;
    return next;
  },

  // Loads the stored company settings, logo included as a data URL.
  //
  // Named ...FromBackend rather than getCompanySettings because that name
  // is already taken by the mock read below, which still serves the two
  // fields the backend does not store. Matches the ...FromBackend /
  // ...ToBackend convention the other real calls in this file use.
  async getCompanySettingsFromBackend(): Promise<CompanySettingsResult> {
    const call = await backendGet(ENDPOINTS.companySettings);
    if (!call.ok) return { ok: false, error: call.error };

    if (!call.body.settings) {
      return { ok: false, error: "Backend response had no 'settings' object." };
    }
    const settings = call.body.settings as BackendCompanySettings;
    setQuoteNumbering(settings.quote_prefix, settings.quote_padding);
    setMoneyFormat({ locale: settings.money_locale, symbol: settings.currency_symbol, words: settings.amount_words });
    setProductName(settings.product_name);
    return { ok: true, settings };
  },

  // Saves company settings. See "Company settings" above for the three
  // logo states — in particular, omitting logoBase64 keeps the stored one.
  async saveCompanySettingsToBackend(
    input: SaveCompanySettingsInput,
  ): Promise<CompanySettingsResult> {
    const payload: Record<string, unknown> = {
      company_name: input.companyName,
      gstin: input.gstin,
      address: input.address,
      contact_email: input.contactEmail,
      contact_phone: input.contactPhone,
    };
    if (input.quoteValidityDays !== undefined) {
      const typed = input.quoteValidityDays.trim();
      payload.quote_validity_days = typed === "" ? null : typed;
    }
    if (input.approvalGpThreshold !== undefined) {
      const typed = input.approvalGpThreshold.trim();
      payload.approval_gp_threshold = typed === "" ? null : typed;
    }
    if (input.defaultWarehouseId !== undefined) {
      payload.default_warehouse_id = input.defaultWarehouseId;
    }
    // Configurable identity (P1-5/P1-6): sent only when the form holds them,
    // so a save from an older screen never touches them.
    if (input.quotePrefix !== undefined) payload.quote_prefix = input.quotePrefix;
    if (input.quotePadding !== undefined) payload.quote_padding = input.quotePadding;
    if (input.oemOrderPrefix !== undefined) payload.oem_order_prefix = input.oemOrderPrefix;
    if (input.oemOrderPadding !== undefined) payload.oem_order_padding = input.oemOrderPadding;
    if (input.grnPrefix !== undefined) payload.grn_prefix = input.grnPrefix;
    if (input.grnPadding !== undefined) payload.grn_padding = input.grnPadding;
    if (input.dnPrefix !== undefined) payload.dn_prefix = input.dnPrefix;
    if (input.dnPadding !== undefined) payload.dn_padding = input.dnPadding;
    if (input.ourEntityAliases !== undefined) payload.our_entity_aliases = input.ourEntityAliases;
    if (input.supplierCodeLabel !== undefined) payload.supplier_code_label = input.supplierCodeLabel;
    if (input.locationCodeLabel !== undefined) payload.location_code_label = input.locationCodeLabel;
    if (input.poProfileOracle !== undefined) payload.po_profile_oracle = input.poProfileOracle;
    if (input.productName !== undefined) payload.product_name = input.productName;
    if (input.defaultGstRate !== undefined) payload.default_gst_rate = input.defaultGstRate;
    if (input.moneyLocale !== undefined) payload.money_locale = input.moneyLocale;
    if (input.currencySymbol !== undefined) payload.currency_symbol = input.currencySymbol;
    if (input.amountWords !== undefined) payload.amount_words = input.amountWords;

    // Only mentioned when it is actually changing. `undefined` would be
    // dropped by JSON.stringify anyway, but not assigning it at all makes
    // the intent explicit at the one place it matters.
    if (input.logoBase64 !== undefined) {
      payload.logo_base64 = input.logoBase64;
    }

    const call = await backendPost(ENDPOINTS.saveCompanySettings, payload);
    if (!call.ok) return { ok: false, error: call.error };

    // Read the saved values back off the response rather than assuming the
    // request went in as sent — same reason the backend serializes from the
    // stored record.
    if (!call.body.settings) {
      return { ok: false, error: "Backend reported success but returned no settings." };
    }
    const saved = call.body.settings as BackendCompanySettings;
    setQuoteNumbering(saved.quote_prefix, saved.quote_padding);
    setMoneyFormat({ locale: saved.money_locale, symbol: saved.currency_symbol, words: saved.amount_words });
    setProductName(saved.product_name);
    return { ok: true, settings: saved };
  },

  // Lists quotes persisted in Odoo, newest first. Read-only.
  async listSavedQuotes(limit?: number, q?: string): Promise<ListSavedQuotesResult> {
    // `q` (global-search, 2026-09-30) narrows the SAME list on the server by
    // quote number, customer or project; nothing else about the list changes.
    const params = new URLSearchParams();
    if (limit) params.set("limit", String(limit));
    if (q && q.trim()) params.set("q", q.trim());
    const query = params.toString() ? `?${params.toString()}` : "";
    const call = await backendGet(`${ENDPOINTS.quotes}${query}`);
    if (!call.ok) return { ok: false, error: call.error };

    if (!Array.isArray(call.body.quotes)) {
      return { ok: false, error: "Backend response had no 'quotes' array." };
    }
    return { ok: true, quotes: call.body.quotes as SavedQuoteSummary[] };
  },

  // Loads one saved quote with all its lines. Read-only: this reads records,
  // it does not re-save or re-price them.
  async getSavedQuote(quoteId: number): Promise<GetSavedQuoteResult> {
    const call = await backendPost(ENDPOINTS.getQuote, { quote_id: quoteId });
    if (!call.ok) {
      // The backend answers a missing id with ok:false / "not found" at HTTP
      // 200. Flag it so the UI can say "this quote no longer exists" rather
      // than implying the backend is broken.
      const notFound = (call.error ?? "").toLowerCase() === "not found";
      return { ok: false, error: call.error, notFound };
    }

    if (!call.body.quote || !Array.isArray(call.body.quote.lines)) {
      return { ok: false, error: "Backend response had no 'quote' with lines." };
    }
    return { ok: true, quote: call.body.quote as SavedQuoteDetail };
  },

  // Prices lines via the real backend engine. See "Real backend pricing"
  // above — never falls back to local math on failure.
  async priceLinesFromBackend(
    // Accepts either path's line shape. The two are distinct shapes, not one
    // extending the other, so an import line is not assignable to
    // BackendPriceLineInput and the parameter has to name both. `mode` below
    // is what tells the backend which it is looking at.
    lines: (BackendPriceLineInput | BackendImportPriceLineInput)[],
    variancePct: number,
    gpPct: number,
    // Defaulted, so every existing three-argument call site keeps sending
    // exactly what it sent before.
    mode: "domestic" | "import" = "domestic",
    // THE QUOTE'S SUPPLIER CURRENCY, so the backend can resolve the FX rate
    // for lines that state none. Same key save_quote already accepts.
    //
    // Without it this route had no way to know what the costs are denominated
    // in, so an unset rate reached the engine as the 0 the frontend sent and
    // multiplied the whole import chain by zero. The currency SELECTS a rate
    // server-side; it never reaches the engine itself.
    //
    // Optional, and omitted entirely on the domestic path below -- that chain
    // has no conversion_rate concept, so sending one would imply it did.
    supplierCurrency?: string,
  ): Promise<PriceLinesResult> {
    if (lines.length === 0) {
      return { ok: false, error: "No lines to price." };
    }

    // pricing_mode is now always sent, including "domestic". That is
    // contract-equivalent to omitting it — the backend defaults a missing
    // pricing_mode to "domestic" — so the validated domestic result cannot
    // shift, and the request states its path rather than relying on a
    // default that would silently pick the wrong one if it ever changed.
    const call = await backendPost(ENDPOINTS.priceLines, {
      variance_pct: variancePct,
      gp_pct: gpPct,
      pricing_mode: mode,
      // Import only, and only when the quote names one. An absent key is the
      // backend's "the caller states its own rates", which is exactly what
      // every request sent before this existed.
      ...(mode === "import" && supplierCurrency
        ? { supplier_currency: supplierCurrency }
        : {}),
      lines,
    });
    if (!call.ok) return { ok: false, error: call.error };

    if (!Array.isArray(call.body.lines)) {
      return { ok: false, error: "Backend response had no 'lines' array." };
    }

    return { ok: true, lines: call.body.lines as BackendPricedLine[] };
  },

  // Persists a quote and its lines to Odoo. See "Saving a quote to the
  // backend" above — never reports a save that did not happen.
  async saveQuoteToBackend(
    quote: Quote,
    lines: Material[],
    variancePct: number,
    gpPct: number,
    // The draft row to finalise, when this quote has one. Sending it makes
    // Save turn that draft INTO the quote -- same row, and the sequence
    // issues its number on that transition -- rather than leaving a stray
    // draft behind next to a brand-new final record.
    existingId?: number,
    // The SidePanel settings to store alongside the quote, so reopening it
    // shows the estimate columns it was left showing. Not a pricing input --
    // the backend stores it opaquely and no compute reads it.
    pricingContext?: PricingContext,
    // The quote's REAL shipments -- database records, not the display blob
    // inside pricingContext above. Same builder and same terms as saveDraft.
    shipments?: QuoteShipment[],
    // THE TOKEN THIS COPY LAST READ, echoed so the backend can tell a fresh
    // edit from a stale one. THE MANUAL PRESS IS THE PATH THAT NEEDS IT: a
    // stale tab's draft tick is already refused (it carries is_draft: true
    // and the backend's B2 rule catches it), but a press carries
    // is_draft: false, which reads as an ordinary edit of a saved quote and
    // was ALLOWED -- so a stale press could replace a finalised quote's
    // lines. Undefined is a real state, not a bug: a quote never saved from
    // this tab has no token, and the backend allows an absent one this pass.
    saveToken?: number,
  ): Promise<SaveQuoteResult> {
    if (lines.length === 0) {
      return { ok: false, error: "This quote has no lines to save." };
    }

    // Derived once and used for BOTH the envelope and the line mapping. They
    // must change together — a payload whose pricing_mode and line shape
    // disagree is worse than one that is uniformly domestic. A quote with no
    // mode set is domestic, which is what every existing quote is.
    const mode = quote.pricingMode ?? "domestic";
    const shipmentsBlock = shipmentsFor(mode, quote.supplierCurrency, shipments);

    // WHETHER TO SEND A NAME AT ALL.
    //
    // A quote that has never been persisted sends NO name, so Odoo's
    // ir.sequence assigns one. This app's quoteNo is a mock reference
    // invented client-side (Q-2026-0001 and friends, several of which
    // already collide in the database) — sending it is what kept the
    // sequence from ever being used.
    //
    // A quote that HAS been persisted sends the reference Odoo gave it, so
    // a re-save keeps the number it was already issued rather than drawing
    // a fresh one. odooQuoteId is the proof a record exists; in saved mode
    // quoteNo IS that Odoo name, which is why it is the fallback.
    const persistedName =
      quote.odooQuoteName ??
      (quote.odooQuoteId !== undefined ? quote.quoteNo : undefined);

    const call = await backendPost(ENDPOINTS.saveQuote, {
      // THE ID IS WHAT MAKES THIS AN UPDATE. Present -> the backend
      // overwrites that record, keeping its reference. Absent -> it creates
      // one and the sequence names it.
      //
      // Without this, every Save inserted a row: pressing it twice produced
      // two quotes sharing a reference (433 and 434 on live). The id is
      // only ever what the backend itself returned, never guessed.
      ...(quote.odooQuoteId !== undefined
        ? { quote_id: quote.odooQuoteId }
        : existingId !== undefined
          ? { quote_id: existingId }
          : {}),
      // Absent would also mean final, but saying so is what makes the
      // draft -> final transition explicit at the call site.
      is_draft: false,
      // Omitted entirely when new — the backend assigns the sequence value
      // when name is absent or "New".
      ...(persistedName ? { name: persistedName } : {}),
      // IDS when a customer has been picked; the name only as a fallback for
      // a quote that has not been. See customerToBackendPayload.
      ...(quote.customer?.entity?.id
        ? customerToBackendPayload(quote.customer)
        : { partner_name: quote.endClient ?? "" }),
      // Omitted when absent: the backend leaves a stored context alone on a
      // save that does not mention it, so this can never wipe settings.
      ...(pricingContext ? { pricing_context: pricingContext } : {}),
      // THE REAL SHIPMENT RECORDS. Omitted when the caller holds none, which
      // the backend reads as "leave the quote's shipments alone" -- so a
      // screen that never opened the sourcing panel cannot delete what
      // another one created. See buildShipmentsPayload.
      ...(shipmentsBlock ? { shipments: shipmentsBlock } : {}),
      variance_pct: variancePct,
      gp_pct: gpPct,
      pricing_mode: mode,
      // Omitted when unchosen, so a save never blanks a currency someone set:
      // the backend treats an absent key as "leave the stored one alone".
      ...(quote.supplierCurrency ? { supplier_currency: quote.supplierCurrency } : {}),
    // project-id: sent when known; an absent key leaves the stored one alone.
    ...(quote.projectId ? { project_ref: quote.projectId } : {}),
    ...(quote.projectName ? { project_name: quote.projectName } : {}),
    // warehouse-master: only a choice made on this screen; absent = untouched.
    ...(quote.dispatchWarehouseChoice !== undefined ? { warehouse_id: quote.dispatchWarehouseChoice } : {}),
      // OMITTED WHEN UNKNOWN, never sent as null or 0. The backend reads an
      // absent key as "this caller cannot say", which it allows this pass;
      // 0 is a REAL token (every quote written before the column existed
      // holds it), so sending it as a stand-in for "none" would claim a
      // freshness this copy has not got.
      ...(saveToken !== undefined ? { save_token: saveToken } : {}),
      // Passed by value, not left to `map` — bare `map(fn)` would hand the
      // index in as the second argument.
      lines: lines.map((m) => materialToBackendSaveLine(m, mode)),
    });
    if (!call.ok) {
      // A timeout on a WRITE is genuinely ambiguous in a way a failed read
      // never is — the request may have reached Odoo and committed before
      // we stopped waiting. Say so rather than implying nothing happened.
      const error = call.timedOut
        ? `${call.error} The quote may or may not have saved — check Odoo before retrying.`
        : call.error;
      // Carried through rather than flattened into `error`, on the same
      // terms as saveDraft's quoteFinalised: this is a refusal the caller
      // reacts to differently from an ordinary failure.
      // margin-approval refusals, read off the machine-readable reason.
      const reason = (call.body as { reason?: string } | undefined)?.reason;
      const appr = (call.body as { approval?: { quote_gp_pct?: number; threshold_pct?: number } } | undefined)?.approval;
      return {
        ok: false, quoteStale: call.quoteStale, error,
        approvalRequired: reason === "approval_required" && appr
          && typeof appr.quote_gp_pct === "number" && typeof appr.threshold_pct === "number"
          ? { quoteGpPct: appr.quote_gp_pct, thresholdPct: appr.threshold_pct }
          : undefined,
        awaitingApproval: reason === "awaiting_approval",
      };
    }

    const body = call.body;

    // No id means no proof a record exists, so this counts as a failure
    // even though the backend said ok. Reporting "Saved" without an id
    // would be exactly the false confirmation this guard exists to stop.
    if (typeof body.quote_id !== "number") {
      return { ok: false, error: "Backend reported success but returned no quote id." };
    }

    // Record the real id on the stored quote. Same in-memory, tab-lifetime
    // store as createQuote()/addMaterialsToQuote() — see the note at the
    // top of this file. It is written only from the response.
    const stored = quotesStore.find((q) => q.quoteNo === quote.quoteNo);
    if (stored) {
      stored.odooQuoteId = body.quote_id;
      // The assigned reference, so a re-save preserves it. Deliberately NOT
      // written over quoteNo: that is this store's lookup key and the
      // /quotes/:id route parameter, so renaming it would detach the open
      // quote from its own URL.
      stored.odooQuoteName = body.name;
    }

    return {
      ok: true,
      quoteId: body.quote_id,
      name: body.name,
      grandTotal: body.grand_total,
      lines: body.lines as BackendSavedLine[],
      // Ids the panel adopts, so a second press of Save updates these
      // shipments rather than replacing them with copies of themselves.
      shipments: body.shipments as BackendShipment[] | undefined,
      // The token as this save left it. Adopted so the NEXT press echoes the
      // current value instead of the one this press just superseded --
      // without it a tab would refuse its own second save.
      saveToken: typeof body.save_token === "number" ? body.save_token : undefined,
      // Read off the response, never inferred from "this tab pressed Save":
      // an Admin finalising, or a re-save of a quote finalised earlier by
      // someone else, must show the RECORDED finaliser.
      finalisedBy: body.finalised_by ?? null,
      finalisedByUid: body.finalised_by_uid ?? null,
      finalisedAt: body.finalised_at ?? null,
      finalEditedBy: body.final_edited_by ?? null,
      finalEditedAt: body.final_edited_at ?? null,
      customerSnapshot: toCustomerSnapshot(body.customer_snapshot),
      // won-at-finalise: finalising a quote that holds a PO marks it Won.
      outcome: toQuoteOutcome(body.outcome),
    };
  },
};

// ---- M4 FULFILMENT (O8-O11, owner D195) -----------------------------------------------
//
// Goods receipts (via-warehouse orders), delivery notes (challans), proof of
// delivery (drop-ship), customs actuals (import orders), the read-only
// "Actual" true-margin report (Admin) and close / reopen (Admin). The server
// scopes every read and write (Sales own, Admin any, Viewer reads). ⚠️ The
// challan payload is the server's ALLOWLIST; the preview narrows it again.
// ABSENT IS NOT ZERO: every optional quantity or amount travels as null.

export interface GrnLine {
  order_line_id: number; line_no: number | null; part_number: string; qty_received: number;
  qty_damaged: number | null; qty_per_document: number | null;
  discrepancy: "short" | "damaged" | "short_and_damaged" | null; discrepancy_reason: string | null;
}
export interface Grn {
  id: number; number: string; oem_order_id: number; oem_order_number: string; warehouse: string | null;
  received_date: string; oem_document_ref: string | null; note: string | null;
  reverses: string | null; reverse_reason: string | null; reversed_by: string | null;
  recorded_by: string | null; recorded_at: string | null; lines: GrnLine[];
}
export interface GrnInput {
  received_date: string | null; oem_document_ref?: string; note?: string;
  lines: { order_line_id: number; qty_received: number | null; qty_damaged?: number | null;
    qty_per_document?: number | null; discrepancy_reason?: string }[];
}
export interface DeliveryNote {
  id: number; number: string; quote_id: number; warehouse_id: number; warehouse: string | null; dn_date: string;
  eway_bill_no: string | null; lr_no: string | null; transporter: string | null; vehicle_no: string | null;
  note: string | null; reverses: string | null; reverse_reason: string | null; reversed_by: string | null;
  recorded_by: string | null; recorded_at: string | null;
  lines: { quote_line_id: number; line_no: number | null; part_number: string | null; qty: number }[];
}
export interface DeliveryNoteInput {
  warehouse_id: number | null; dn_date: string | null; eway_bill_no?: string; lr_no?: string;
  transporter?: string; vehicle_no?: string; note?: string;
  lines: { quote_line_id: number; qty: number | null }[];
}
/** THE CHALLAN, exactly as the server's allowlist sends it. */
export interface DeliveryNotePrint {
  number: string; dn_date: string; quote_name: string;
  customer: { name: string | null; vat: string | null };
  ship_to: { name: string | null; street: string | null; street2: string | null; city: string | null;
    zip: string | null; state: string | null; vat: string | null };
  eway_bill_no: string | null; lr_no: string | null; transporter: string | null; vehicle_no: string | null;
  note: string | null; reverses: string | null;
  issuer: { company_name: string | null; address: string | null; gstin: string | null;
    contact_email: string | null; contact_phone: string | null };
  lines: { line_no: number | null; part_number: string | null; description: string | null; uom: string | null;
    hsn_code: string | null; qty: number }[];
}
export interface PodDocument { id: number; name: string; mimetype: string; size: number;
  uploaded_at: string | null; uploaded_by: string | null }
export interface CustomsLine {
  order_line_id: number; line_no: number | null; part_number: string; qty: number;
  assessable_value_inr: number | null; bcd_inr: number | null; sws_inr: number | null; igst_inr: number | null;
}
export interface CustomsEntry {
  id: number; oem_order_id: number; oem_order_number: string; currency: string; boe_number: string;
  boe_date: string; port: string | null; other_label: string | null; reverses: boolean; reverse_reason: string | null;
  reversed: boolean; recorded_by: string | null; recorded_at: string | null;
  customs_rate: number | null; remittance_rate: number | null; oem_paid_inr: number | null;
  freight_inr: number | null; insurance_inr: number | null; cha_inr: number | null; other_inr: number | null;
  lines: CustomsLine[];
}
export interface CustomsEntryInput {
  boe_number: string; boe_date: string | null; port?: string; customs_rate?: number | null;
  remittance_rate?: number | null; oem_paid_inr?: number | null; freight_inr?: number | null;
  insurance_inr?: number | null; cha_inr?: number | null; other_inr?: number | null; other_label?: string;
  lines: { order_line_id: number; qty: number | null; assessable_value_inr?: number | null; bcd_inr?: number | null;
    sws_inr?: number | null; igst_inr?: number | null }[];
}
export interface TrueMarginLine {
  line_id: number; line_no: number; part_number: string; qty: number; delivered: number; short_closed: number;
  quoted_revenue: number; igst_credit: number; computed: boolean; missing: string[];
  revenue: number | null; cost: number | null; margin: number | null; margin_pct: number | null;
  /** D196: which recorded input the goods cost used -- "Bank advice", "OEM price x remittance rate",
   *  "OEM price (INR order)" or "Not recorded"; per order / Bill of Entry in the detail. Read-only. */
  cost_basis?: string[];
  cost_basis_detail?: { order: string; boe: string | null; basis: string }[];
}
export interface TrueMarginView {
  quote_id: number; quote_name: string; label: "Actual"; closed: boolean; lines: TrueMarginLine[];
  complete: boolean; totals: { revenue: number; cost: number; margin: number; margin_pct: number | null } | null;
  quoted_total: number; igst_credit_total: number;
}
export interface CloseState { closed_at: string | null; closed_by: string | null; reason: string }

type M4Read<T> = { ok: boolean; error?: string; notFound?: boolean } & T;

const qid = (id: number) => `?quote_id=${encodeURIComponent(String(id))}`;

export async function getGrns(quoteId: number): Promise<M4Read<{ grns?: Grn[]; nextNumber?: string | null }>> {
  const call = await backendGet(`${ENDPOINTS.grns}${qid(quoteId)}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.grns)) return { ok: false, error: "The goods receipts came back in an unknown shape." };
  return { ok: true, grns: call.body.grns as Grn[], nextNumber: call.body.next_number ?? null };
}
export async function recordGrn(orderId: number, input: GrnInput): Promise<{ ok: boolean; grn?: Grn; error?: string }> {
  const call = await backendPost(ENDPOINTS.recordGrn, { order_id: orderId, ...input });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, grn: call.body?.grn as Grn };
}
export async function reverseGrn(grnId: number, reason: string): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.reverseGrn, { grn_id: grnId, reason });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
export async function getDeliveryNotes(quoteId: number): Promise<M4Read<{ notes?: DeliveryNote[]; nextNumber?: string | null }>> {
  const call = await backendGet(`${ENDPOINTS.deliveryNotes}${qid(quoteId)}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.delivery_notes)) return { ok: false, error: "The delivery notes came back in an unknown shape." };
  return { ok: true, notes: call.body.delivery_notes as DeliveryNote[], nextNumber: call.body.next_number ?? null };
}
export async function createDeliveryNote(quoteId: number, input: DeliveryNoteInput): Promise<{
  ok: boolean; note?: DeliveryNote; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.createDeliveryNote, { quote_id: quoteId, ...input });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, note: call.body?.delivery_note as DeliveryNote };
}
export async function reverseDeliveryNote(dnId: number, reason: string): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.reverseDeliveryNote, { dn_id: dnId, reason });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
export async function getDeliveryNotePrint(id: number): Promise<{ ok: boolean; note?: DeliveryNotePrint; error?: string }> {
  const call = await backendPost(ENDPOINTS.getDeliveryNote, { id });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, note: call.body?.delivery_note as DeliveryNotePrint };
}
export async function attachPod(eventId: number, filename: string, contentBase64: string): Promise<{
  ok: boolean; documents?: PodDocument[]; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.attachPod, { event_id: eventId, filename, content_base64: contentBase64 });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, documents: (call.body?.documents ?? []) as PodDocument[] };
}
export async function getPodDocuments(eventId: number): Promise<M4Read<{ documents?: PodDocument[] }>> {
  const call = await backendGet(`${ENDPOINTS.podDocuments}?event_id=${encodeURIComponent(String(eventId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  return { ok: true, documents: (call.body?.documents ?? []) as PodDocument[] };
}
export async function getCustomsEntries(quoteId: number): Promise<M4Read<{ entries?: CustomsEntry[] }>> {
  const call = await backendGet(`${ENDPOINTS.customsEntries}${qid(quoteId)}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.entries)) return { ok: false, error: "The customs entries came back in an unknown shape." };
  return { ok: true, entries: call.body.entries as CustomsEntry[] };
}
export async function recordCustomsEntry(orderId: number, input: CustomsEntryInput): Promise<{
  ok: boolean; entry?: CustomsEntry; error?: string;
}> {
  const call = await backendPost(ENDPOINTS.recordCustomsEntry, { order_id: orderId, ...input });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, entry: call.body?.entry as CustomsEntry };
}
export async function reverseCustomsEntry(entryId: number, reason: string): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.reverseCustomsEntry, { entry_id: entryId, reason });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
/** Admin only (403 for anyone else, which the screen never asks for). */
export async function getTrueMargin(quoteId: number): Promise<{ ok: boolean; view?: TrueMarginView; error?: string; forbidden?: boolean }> {
  const call = await backendGet(`${ENDPOINTS.trueMargin}${qid(quoteId)}`);
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  if (!Array.isArray(call.body?.lines)) return { ok: false, error: "The true margin came back in an unknown shape." };
  return { ok: true, view: call.body as TrueMarginView };
}
export async function setQuoteOrderClosed(quoteId: number, close: boolean, reason: string): Promise<{
  ok: boolean; closed?: CloseState | null; error?: string; forbidden?: boolean;
}> {
  const call = await backendPost(close ? ENDPOINTS.closeQuoteOrder : ENDPOINTS.reopenQuoteOrder,
    { quote_id: quoteId, reason });
  if (!call.ok) return { ok: false, error: call.error, forbidden: call.forbidden };
  return { ok: true, closed: (call.body?.closed ?? null) as CloseState | null };
}

// ---- M2: THE OEM MASTER, OFFERS AND THE SUPPLIER CHOICE (owner B4/B5, D197) -------------
//
// Forward-only: old records keep their OEM text; `oem_link` is the OEM it
// resolves to now (null = "Not linked to an OEM"). Offers are READ-ONLY; the
// resulting sell price of an offer comes from the existing price_lines route.

export interface OemLink { id: number; name: string; active: boolean; stored: boolean }
export interface OemMaster {
  id: number; name: string; active: boolean; currency: string | null; contact_name: string | null;
  contact_email: string | null; contact_phone: string | null; notes: string | null; aliases: string[];
  created_by: string | null; last_edited_by: string | null;
}
export interface OemUnlinked { text: string; uses: number }
export interface OemOffer {
  kind: "oem_quote" | "confirmation"; id: number; oem: string; oem_link: OemLink | null; same_oem: boolean;
  reference: string | null; price: number; currency: string; qty: number | null; covers: boolean;
  lead_time: string | null; valid_until: string | null; expired: boolean; comparable: boolean;
  warnings: string[]; in_use: boolean;
}
export interface OemOffersLine {
  line_id: number; line_no: number; part_number: string; oem: string; qty: number; unit_price: number;
  cost_currency: string; offers: OemOffer[]; oem_count: number;
}
export interface OemOffersView { quote_id: number; cost_currency: string; lines: OemOffersLine[]; offers: number; oems: number }

export async function getOems(includeInactive = false): Promise<{
  ok: boolean; oems?: OemMaster[]; unlinked?: OemUnlinked[]; error?: string;
}> {
  const call = await backendGet(`${ENDPOINTS.oems}${includeInactive ? "?inactive=1" : ""}`);
  if (!call.ok) return { ok: false, error: call.error };
  if (!Array.isArray(call.body?.oems)) return { ok: false, error: "The OEM list came back in an unknown shape." };
  return { ok: true, oems: call.body.oems as OemMaster[], unlinked: (call.body.unlinked ?? []) as OemUnlinked[] };
}
export async function saveOem(input: Partial<OemMaster> & { name: string }): Promise<{ ok: boolean; oem?: OemMaster; error?: string }> {
  // An allowlist: only the editable fields travel.
  const body = { id: input.id, name: input.name, currency: input.currency ?? null,
    contact_name: input.contact_name ?? null, contact_email: input.contact_email ?? null,
    contact_phone: input.contact_phone ?? null, notes: input.notes ?? null };
  const call = await backendPost(ENDPOINTS.saveOem, body);
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, oem: call.body?.oem as OemMaster };
}
export async function addOemAlias(id: number, alias: string): Promise<{ ok: boolean; oem?: OemMaster; error?: string }> {
  const call = await backendPost(ENDPOINTS.addOemAlias, { id, alias });
  if (!call.ok) return { ok: false, error: call.error };
  return { ok: true, oem: call.body?.oem as OemMaster };
}
export async function mergeOem(id: number, intoId: number): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.mergeOem, { id, into_id: intoId });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
export async function setOemActive(id: number, active: boolean): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.setOemActive, { id, active });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
export async function getOemOffers(quoteId: number): Promise<{ ok: boolean; view?: OemOffersView; error?: string; notFound?: boolean }> {
  const call = await backendGet(`${ENDPOINTS.oemOffers}?quote_id=${encodeURIComponent(String(quoteId))}`);
  if (!call.ok) return { ok: false, error: call.error, notFound: call.error === "not found" };
  if (!Array.isArray(call.body?.lines)) return { ok: false, error: "The offers came back in an unknown shape." };
  return { ok: true, view: call.body as OemOffersView };
}
export async function selectOemOffer(input: {
  quoteId: number; lineId: number; kind: OemOffer["kind"]; id: number; best: boolean; reason?: string;
}): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.selectOemOffer, {
    quote_id: input.quoteId, line_id: input.lineId, kind: input.kind, id: input.id, best: input.best,
    ...(input.reason ? { reason: input.reason } : {}) });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
/** M2: ask ANOTHER OEM (from the master) to price these lines -- one request. Nothing is emailed. */
export async function requestOtherOem(quoteId: number, oemId: number, lineIds: number[]): Promise<{ ok: boolean; error?: string }> {
  const call = await backendPost(ENDPOINTS.createOemRequests, { quote_id: quoteId, oem_id: oemId, line_ids: lineIds });
  return call.ok ? { ok: true } : { ok: false, error: call.error };
}
